// ============================================================
// TERROR — THE PLAZA
// The main hub. Login here, walk around, do everything here.
//
// ── SPRITE SWAP GUIDE ────────────────────────────────────────
// Drop .webp files into your cards folder, deploy via Wrangler,
// then update the SPRITES object below.
//
// companion_default:  jynxi.webp (already live)
// player sprites: player_{m|f}_{down|up|left|right}_{a|b}.webp
//
// THE ROOM ITSELF IS NOT SPRITES. Every fixture — arches, bulletin,
// Willy's bench, the fountain, both shops, V'yen's stall — is PAINTED
// INTO plaza_1/2/3.webp. Nothing in this file draws a piece of the
// room, and nothing in this file should start.
// ============================================================

// ── THE DEAD-DECOR SWEEP (caP'n, July 30 2026) ───────────────
// 242 lines cut, all of it built before the painting and rendering
// nothing since:
//
//   PortalVisual + PORTAL_SPRITES  the arches drawn in code — the
//                                  "pillars." Glowing frames and
//                                  standing posts positioned by hand
//                                  where the painting has none. The
//                                  sprite map was five nulls waiting
//                                  on art that arrived as part of the
//                                  background instead. It also still
//                                  read portal.x / portal.y, fields
//                                  that stopped existing the day the
//                                  zone table landed — it could not
//                                  have rendered if something had
//                                  called it.
//   ProximityBubble, PortalBubble  retired July 8 when RoomConsole
//                                  took the prompts. Kept "for
//                                  reference" for three weeks.
//   InteractModal                  never mounted.
//   getPortalAt / getShopAt /      four wrappers around plazaZones
//   getNPCAt / getLandmarkAt       helpers that nothing called.
//   PORTALS / SHOPS / NPCS         three filters nothing read.
//   T = { FLOOR, WALL }            the last of the tile-type enum.
//                                  Its own comment claimed
//                                  PlayerEntity still read it. It
//                                  did not.
//
// WHY IT MATTERS BEYOND TIDINESS: dead room-drawing code is where a
// phantom collision comes from. Anything that positions a fixture is
// a second opinion about where that fixture IS, and the painting has
// already given the only one that counts. One room, one source.
// ============================================================

import { equippedGraceOf, renderableGrace } from "./kingsGrace"; // KING'S GRACE
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ref, set, onValue, remove, get } from "firebase/database";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import { db, auth } from "./firebase";
import { useSocialBridge, releasePresence } from "./ieSocial";   // ◆ CALL A FRIEND TO THE TABLE
import { SD, GM, shimmerBorder, GOLD_METAL, GOLD_METAL_H, OBSIDIAN_SLAB, OBSIDIAN_RECESS, facetTB, textGlow, TEXT_SET } from "./theme";
import {
  CDN,
  getPlayerSprite,
  getRestSprite,
  getIdleSprite,
  getCompanionSprite,
  MobileDpad,
  useChatSystem,
  ChatSystem,
  useOrientationLock,
  RotateGate,
  DockPortal,
} from "./engineIndex";
import CompanionSprite from "./CompanionSprite";
import CompanionCloseup from "./CompanionInteraction";
import StartMenu from "./StartMenu";
import AchievementGallery from "./AchievementGallery";
import AchievementToast from "./AchievementToast";
import { useAchievements } from "./useAchievements";
import { CosmeticOverlay, getEquippedTitle } from "./CosmeticOverlay";
import { STARTER_DECKS, getStarterDeck } from "./starterDecks";
import StarterArt from "./StarterArt";   // the deck boxes — see StarterArt.js
import { DUNGEONS, LADDER_VILLAINS } from "./storyData";
import EndlessDepths from "./EndlessDepths";
import Onboarding from "./Onboarding";
import { listenToProfile, ensureStarterCompanion, backfillStoryTrophies, awardSweetTreat } from "./progression";
import { COMPANIONS, getStarterCompanionId } from "./companions";
import RoomConsole, { getSessionLog, setSessionLog } from "./RoomConsole";
import { ChocolateCoin, CoinBadge, CrestBadge } from "./CoinGlyph";
import RoomBackdrop from "./RoomBackdrop";
import { useRail, useRailHost, RailFoldButton, RailMenuPill } from "./railDock"; // the band: one panel at a time + fold to MENU
// Dial Still Rules — the Plaza's background breath answers to the same
// motion dial as the board. Plain function call, not the useMotion hook:
// this component is long enough that adding a hook mid-body is a risk for
// no gain, and Plaza re-renders on every step anyway, so a mid-session
// flip of the dial is picked up on the next frame the player moves.
import { getMotion, setMotion, MOTION, MOTION_ORDER, MOTION_LABELS } from "./strikeLanguage";
import {
  COLS, ROWS, P_COLS, P_ROWS, SPAWN, P_SPAWN,
  isWalkable as zIsWalkable, zonesAt, zonesFor,
} from "./plazaZones";
import NpcStage from "./NpcStage"; // the keeper on screen with you (V'yen's window)
import { soundMenuSection, useMusicOverride } from "./Soundtrack";
import FountainOverlay from "./FountainOverlay";
import FactionWarBanner from "./FactionWarBanner";
import { factionOf } from "./factionWars"; // H5 — the weekly tally, its banner, and the settlement walk
import { ReplayShelf } from "./ReplayTheatre"; // H6
import TournamentBoard from "./TournamentBoard"; // X4
import { SHOW_TOURNAMENTS } from "./tournament"; // X4 — off by decree until the duel-start seam lands
import { IS_DEV_BUILD, GRIMOIRE_PAGE_TOTAL } from "./worldData";
import { useChallenge } from "./ChallengeSystem";  // one idea of "am I in development" — see the Settings section
// ── PLAZA 3D (caP'n, 2026-09-16: "flip a 3D switch") ──────────────
// Plaza3D is a RENDERER of the same zone table and the same refs — it
// owns no state and Plaza.js never reads from it. plazaCamera is the
// dependency-free module both sides share: the switch, the persisted
// camera view, and the ground-forward the movement loop rotates by.
// With the switch OFF nothing below this line changes a single pixel.
import Plaza3D from "./Plaza3D";
import { getPlaza3D, togglePlaza3D, subscribe as subscribePlazaCam, rotateGridInput } from "./plazaCamera";

// ── Grimoire palette — CONSOLIDATED (July 5, 2026) ───────────
// The local GM copy died in the GILDED OBSIDIAN wave. GM now
// imports from theme.js (v4) — one source of truth, every future
// theme shift is a one-file deploy. Do NOT re-declare GM here.


// ── Firebase Auth helpers ─────────────────────────────────────
const getProfileByUid = async (uid) => {
  const snap = await get(ref(db, `profiles/${uid}`));
  return snap.exists() ? snap.val() : null;
};

const createProfile = async (uid, displayName, starterDeckId = null) => {
  const profile = {
    uid, name: displayName,
    // ═══════════════════════════════════════════════════════════
    // 🔒 THE PRE-ALPHA PURSE — 300 ◕ + 300 ◆, and it is a GATE ITEM.
    // ═══════════════════════════════════════════════════════════
    // caP'n, 2026-08-19: "it's just me playing right now, friends
    // sometimes, and they've always known I will wipe data without
    // consent in this pre-alpha phase xD — so if possible make it so
    // all new players have 300 CC and 300 DC to start with, so I may
    // personally delete my own account and make another."
    //
    // So this is a TEST HARNESS wearing a starting bonus. The purpose
    // is that caP'n can nuke his own profile, re-register, and be back
    // to a fully-stocked account in one screen instead of ten sessions
    // of grinding. It travels with the every-price-is-1 decree and it
    // comes off the same day, on THE LAST GATE.
    //
    // ⚠ 300 ◆ AT LAUNCH IS A CATASTROPHE, NOT A GENEROUS WELCOME.
    // Divine Crests are the EMPIRE-WIDE reserve — under THE ENDGAME
    // RULING (2026-08-18) they are spendable in EVERY IE title and
    // they buy the top shelf: the Foil Vault, mythic cosmetics, and
    // whatever the Divine rung ends up holding. A new Terror account
    // handing out 300 ◆ is a new account handing out endgame currency
    // for Snake Eyes, Pierre and everything after them. The launch
    // numbers are the ones directly below; restoring them is one edit.
    //
    //   LAUNCH VALUES:  chocolateCoins: 600   divineCrests: 5
    //
    chocolateCoins: 300,
    divineCrests: 300,
    totalWins: 0,
    storyProgress: { currentDungeon: 1, defeatedOpponents: [], completedDungeons: [] },
    collection: {},
    lastLogin: Date.now(),
    createdAt: Date.now(),
    starterDeckId: starterDeckId || null,
  };
  await set(ref(db, `profiles/${uid}`), profile);
  return profile;
};

const listenToProfileByUid = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}`), snap => {
    if (snap.exists()) callback({ ...snap.val(), uid });
    else callback(null);
  });
};

// ─────────────────────────────────────────────────────────────
// SPRITE MAP
// ─────────────────────────────────────────────────────────────
// CDN imported from engine

const UI = {
  interact: `${CDN}/a_main.webp`,
  npc:      `${CDN}/a_npc_box.webp`,
  bulletin: `${CDN}/a_bulletin.webp`,
  solrend:  `${CDN}/a_solrend.webp`,
  boss:     `${CDN}/a_boss_box.webp`,
};

// ─────────────────────────────────────────────────────────────
// PLAZA BACKGROUND — the four-beat breath (caP'n, July 29)
// ─────────────────────────────────────────────────────────────
// Three files, FOUR beats, one second each:
//
//     plaza_1 → plaza_2 → plaza_3 → plaza_2 → (loop)
//
// plaza_2 is the middle of the swing, so it gets played twice — once
// on the way up and once on the way down. That ping-pong is what makes
// the portals and the fountain read as *breathing* instead of as a
// three-frame flipbook that snaps back to the start every third beat.
//
// HOW IT RUNS — all three <img> layers mount at once, stacked, and only
// one is ever opaque. Nothing swaps `src`, so:
//   · no network hitch on the first pass through the loop
//   · no decode pop when a frame comes up for the first time
//   · zero React re-renders — the cycle is pure CSS on the compositor
// (Perf Law: animation never goes through setState.)
const PLAZA_FRAMES = [
  `${CDN}/plaza_1.webp`,
  `${CDN}/plaza_2.webp`,
  `${CDN}/plaza_3.webp`,
];
// Still-frame fallback AND the truthiness gate the render block already
// tests against — kept so the no-art branch below stays reachable.
const PLAZA_BG = PLAZA_FRAMES[0];

// FRAME 1 IS THE FLOOR — it never animates, it just sits there at full
// opacity forever. Frames 2 and 3 are opaque art of the same scene, so
// they simply PAINT OVER it on their beats. That buys the fallback for
// free: if plaza_2 or plaza_3 ever 404s, the room holds on plaza_1
// instead of strobing to black for a second, and the whole effect
// degrades into "a still Plaza" rather than "a broken Plaza."
//
// step-end holds each keyframe's value until the next one lands, which
// is what gives a hard CUT rather than a crossfade. 4s cycle = 4 × 1s.
//   beat 1 (0–25%)  → nothing painted, frame 1 floor shows
//   beat 2 (25–50%) → frame 2
//   beat 3 (50–75%) → frame 3
//   beat 4 (75–100%)→ frame 2 again
const PLAZA_CYCLE_CSS = `
@keyframes plazaCycle2 { 0%{opacity:0} 25%{opacity:1} 50%{opacity:0} 75%{opacity:1} 100%{opacity:0} }
@keyframes plazaCycle3 { 0%{opacity:0} 50%{opacity:1} 75%{opacity:0} 100%{opacity:0} }
`;

// Keyframes go into document.head imperatively with an id-guard, never a
// JSX <style> block — a JSX block is what froze the J'ynxi foil. The
// textContent compare is the second half of the guard: a stale <style>
// left over from a previous build otherwise wins and the keyframes never
// resolve, so the animation silently does nothing.
function ensurePlazaCycleKeyframes() {
  if (typeof document === "undefined") return;
  const ID = "terror-plaza-bg-cycle";
  let el = document.getElementById(ID);
  if (el && el.textContent === PLAZA_CYCLE_CSS) return;
  if (!el) {
    el = document.createElement("style");
    el.id = ID;
    document.head.appendChild(el);
  }
  el.textContent = PLAZA_CYCLE_CSS;
}
ensurePlazaCycleKeyframes();

// ─────────────────────────────────────────────────────────────
// MAP CONFIG
// ─────────────────────────────────────────────────────────────
// COLS/ROWS/P_COLS/P_ROWS are imported from plazaZones.js — the
// camera math and the collision table have to agree on the size of
// the room, and two copies of "34" is how they stop agreeing.

// Mobile detection
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};

// Portrait = tall screen on a touch device
const isPortrait = () => isMobile() && window.innerHeight > window.innerWidth;

// ── PORTRAIT SPLIT-SCREEN ─────────────────────────────────────
// In portrait the world only occupies the TOP slice of the screen;
// the bottom is the reserved dock (d-pad + popups). This MUST match
// WORLD_FRACTION in engine/PortraitFrame.js or the camera and the
// visible world clip will disagree.
const PORTRAIT_WORLD_FRACTION = 0.62;

// Effective viewport HEIGHT for camera/tile math. In portrait this is
// just the world-zone height (top slice), so the player stays centered
// in the visible world instead of behind the dock.
function getViewportH() {
  if (isPortrait()) return Math.round(window.innerHeight * PORTRAIT_WORLD_FRACTION);
  return window.innerHeight;
}

// Portrait mobile → fixed 40px tiles, camera follows player. Dropped 48→40
// (caP'n, July 18) to match desktop's zoom feel — 48 read a touch too big on
// phones. STILL a fixed value, NOT computed. calcTile's portrait branch must
// always `return PORTRAIT_TILE` — that's the load-bearing rule, the number is
// free to tune. Nudge to taste.
const PORTRAIT_TILE = 40;

// ── PORTRAIT MOBILE compact layout (20×12) — see plazaZones.js ──

// ── DESKTOP camera viewport target ────────────────────────────
// We aim for ~16 tiles wide / ~10 tiles tall worth of viewport.
// Tile size derived so the character reads big and the world
// extends past the screen edges (camera follows, clamped to map).
// WORKSHOP-STYLE FIT (July 8, final form) — landscape shows the WHOLE
// plaza at once, centered like Willie's Workshop, with SYMMETRIC vertical
// bars on BOTH sides. THE RECORD lives in the right bar; the left bar is
// reserved real estate for what's coming — animations, menus, data, the
// works ("holy smokes im cooking" — caP'n, canon). No scrolling camera;
// calcCamOffset's "map smaller than viewport → center it" clamp places it.

// Width the Record panel claims on the right (RoomConsole is min(280,24vw)
// at right:12). calcTile subtracts this so the map fits left of it, and
// calcCamOffset centers the map in that same (viewport − reserve) space so
// it sits flush-left of the panel with a matching gap on the left. ONE
// source of truth — the two must always agree or the map drifts.
function RECORD_RESERVE() {
  if (typeof window === "undefined") return 0;
  if (isPortrait()) return 0;  // panel is bottom-docked in portrait
  return Math.min(280, window.innerWidth * 0.24) + 32;  // panel + right:12 + gap
}

// ── PLAZA CAMERA ZOOM (caP'n, July 8 — "zoom in like it used to") ──────
// Multiplier on the fit-to-screen tile size. 1.0 = the old whole-plaza
// fit (map centered, no scrolling). >1.0 = tiles get bigger, the map
// grows past the viewport, and calcCamOffset's deadzone-follow takes over
// so the camera tracks the character — bigger sprite, same screen frame,
// same backdrop, same map (objectFit:"fill" still draws all of it, just
// larger). Nothing else moves. Nudge this one number to taste.
const PLAZA_ZOOM = 1.85;

function calcTile() {
  // Portrait: fixed tile, camera follows the player (zoomed in, PC-like).
  // Do NOT fit the whole compact map here — that pins the world to the top
  // and zooms all the way out. This regressed once; keep the fixed tile.
  if (isPortrait()) return PORTRAIT_TILE;
  // Desktop/landscape. Base fit: the tile size at which the whole 34×20 map
  // would fit left of the Record panel (Record is RIGHT-only, so reserve one
  // side). We then SCALE that up by PLAZA_ZOOM to zoom in on the character;
  // the surplus size spills off-screen and the follow-cam handles it.
  const byWidth  = Math.floor((window.innerWidth - RECORD_RESERVE()) / COLS);
  const byHeight = Math.floor(window.innerHeight / ROWS);
  const fit = Math.min(byWidth, byHeight);
  return Math.max(12, Math.round(fit * PLAZA_ZOOM));
}

// ── Deadzone camera — camera only moves when player leaves
// a centered box in the viewport. Feels solid for small moves,
// smooth for long traversals. Clamps to map bounds so the
// camera never shows past the room edge.
// Returns {x, y} offset to apply as negative translate on the world.
function calcCamOffset(playerX, playerY, tile, prevOffset = null, opts = {}) {
  const cols = opts.cols ?? (isPortrait() ? P_COLS : COLS);
  const rows = opts.rows ?? (isPortrait() ? P_ROWS : ROWS);
  const vw = window.innerWidth;
  const vh = getViewportH();
  const mapW = cols * tile;
  const mapH = rows * tile;

  // Player center in world pixels
  const px = playerX * tile + tile / 2;
  const py = playerY * tile + tile / 2;

  // Portrait: snap-center (old behavior — small screen, no deadzone needed)
  if (isPortrait()) {
    const rawX = px - vw / 2;
    const rawY = py - vh / 2;
    return {
      x: Math.max(0, Math.min(Math.max(0, mapW - vw), rawX)),
      y: Math.max(0, Math.min(Math.max(0, mapH - vh), rawY)),
    };
  }

  // Desktop: deadzone follow
  // Camera center starts centered on player; if previous offset exists,
  // only move when player leaves the deadzone around the current camera center.
  const DZ_W = tile * 4; // 4-tile wide deadzone
  const DZ_H = tile * 3; // 3-tile tall deadzone

  // The Record panel eats the RIGHT edge. To keep the character centered in
  // the VISIBLE play band (viewport − panel) instead of under the panel, we
  // aim the camera at (vw − reserve)/2 horizontally, not vw/2. reserve = 0 in
  // portrait (panel is bottom-docked), so this is a no-op there.
  const reserve = RECORD_RESERVE();
  const anchorX = (vw - reserve) / 2;  // where the player should sit on screen

  let camX, camY;
  if (prevOffset) {
    // Current camera center in world pixels (add back the same anchor bias)
    const camCenterX = prevOffset.x + anchorX;
    const camCenterY = prevOffset.y + vh / 2;
    // How far the player is from camera center
    const dx = px - camCenterX;
    const dy = py - camCenterY;
    // Only move camera if player exceeds deadzone
    let newCenterX = camCenterX;
    let newCenterY = camCenterY;
    if (dx >  DZ_W / 2) newCenterX = px - DZ_W / 2;
    if (dx < -DZ_W / 2) newCenterX = px + DZ_W / 2;
    if (dy >  DZ_H / 2) newCenterY = py - DZ_H / 2;
    if (dy < -DZ_H / 2) newCenterY = py + DZ_H / 2;
    camX = newCenterX - anchorX;
    camY = newCenterY - vh / 2;
  } else {
    // First frame: center player in the visible band
    camX = px - anchorX;
    camY = py - vh / 2;
  }

  // Clamp to map bounds. Horizontal centering happens in (viewport − the
  // Record's right reserve), NOT the full viewport — that's what places the
  // map flush-left of the panel instead of dead-center under it. Same
  // RECORD_RESERVE calcTile used to size the map, so map and gap agree.
  const avail = Math.max(0, vw - RECORD_RESERVE());
  // Clamp the RIGHT edge to the VISIBLE band (avail), not full vw. Using vw
  // let the world scroll far enough that map columns slid UNDER the backdrop
  // band and the player could walk into that dead zone. maxX in band-space
  // stops the last column flush at the backdrop edge. (caP'n, July 18)
  const maxX = Math.max(0, mapW - avail);
  const maxY = Math.max(0, mapH - vh);
  if (mapW <= avail) camX = (mapW - avail) / 2;   // center in the left-of-panel band
  else               camX = Math.max(0, Math.min(maxX, camX));
  if (mapH <= vh) camY = (mapH - vh) / 2;
  else            camY = Math.max(0, Math.min(maxY, camY));

  return { x: camX, y: camY };
}

// Module-level default — used by components outside Plaza
const TILE_DEFAULT = calcTile();

// ─────────────────────────────────────────────────────────────
// GEOMETRY — now lives in plazaZones.js
// ─────────────────────────────────────────────────────────────
// WHAT LEFT THIS FILE (caP'n, July 29 2026 — the new Plaza art;
// finished July 30 with the dead-decor sweep at the top of the file):
// PORTALS / SHOPS / NPCS, their three hand-maintained P_* portrait
// twins, buildMap(), buildPortraitMap(), the tile-type enum T, and
// the getXAt() family. Eleven separate declarations describing one
// room, six of them a second copy of the other five.
//
// They are one table now. Every object carries `solid` rects and
// `zone` rects measured off the painting, portrait is DERIVED from
// landscape rather than authored beside it, and the fountain can
// finally be a thing you cannot walk through and can still talk to
// — which the old centre+radius+clamp shape could not express at
// any setting. plazaZones.js has the full account.
//
// One name survives the sweep at its old spelling, because the ~20
// call sites downstream read better for it.

const isWalkable  = (x, y) => zIsWalkable(x, y, isPortrait());

const PLAYER_COLORS = ["#c9a84c", "#a78bfa", "#22d3ee", "#f87171"];

// Draw the collision table over the art. Dev tool — ship it false.
const SHOW_ZONES = false;

// ─────────────────────────────────────────────────────────────
// SPRITE COMPONENT
// ─────────────────────────────────────────────────────────────
function Sprite({ src, fallbackColor, size = TILE_DEFAULT, style = {}, label = "" }) {
  const [err, setErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => { if (prevSrc.current !== src) { prevSrc.current = src; setErr(false); } }, [src]);
  if (!src || err) {
    return (
      <div style={{ width: size, height: size, background: fallbackColor, borderRadius: 4,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 7, color: "rgba(255,255,255,0.6)", fontFamily: GM.font,
        overflow: "hidden", flexShrink: 0, ...style }}>
        {label && <span style={{ fontSize: 7, textAlign: "center", padding: 1 }}>{label}</span>}
      </div>
    );
  }
  return <img src={src} alt={label} onError={() => setErr(true)}
    style={{ width: size, height: size, imageRendering: "pixelated", ...style }} />;
}

// MobileDpad imported from engine

// ─────────────────────────────────────────────────────────────
// PLAYER ENTITY — gendered directional sprites + walk animation
// ─────────────────────────────────────────────────────────────
// getPlayerSprite, getRestSprite, getIdleSprite imported from engine

// ═════════════════════════════════════════════════════════════
// J'YNXI IN THE PLAZA  ⭐ caP'n: "J'ynxi in the Plaza, frequently,
//                        in the background."
// ═════════════════════════════════════════════════════════════
// Read that line twice, because both halves are load-bearing.
//
// "IN THE BACKGROUND" is the canon, not the art direction. J'ynxi is
// INDIFFERENT — true Chaos, nature's equaliser, never malicious and
// never kind. So she has NO zone, NO prompt, NO interact verb and NO
// line. You cannot talk to her. The RoomConsole never mentions her.
// She is not a secret to be found; she is weather. Giving her a
// "Speak" prompt would be the single fastest way to write her wrong,
// and it is exactly what the next person to touch this file will want
// to do. Don't.
//
// "FREQUENTLY" is not "always." A figure pinned at full opacity in the
// corner forever stops being noticed by the end of the first session
// and becomes furniture. She fades in somewhere, stands there for
// half a minute, fades out, and comes back somewhere else — so the
// player who glances up twice sees two different Plazas and is never
// told why. Present roughly 60% of the time by the numbers below.
//
// She is BEHIND the players (zIndex 2 against their 8/10) and in front
// of the painting (0). Slightly desaturated and slightly soft, because
// the Plaza art is painted and a crisp sprite standing on it reads as
// a UI element rather than as somebody who is there.
//
// PERF: the only state changes here are one every ~20-45 seconds. This
// is NOT a per-frame animation and must never become one — the fade is
// a CSS transition on opacity, which the compositor owns, and React is
// told about her four times a minute at the very most.

// Spots are FRACTIONS of the grid, not tiles, so one table serves both
// the 34x20 landscape map and the 20x12 portrait one without a second
// table to keep in step. Each is somewhere she reads as background:
// up against the far wall, at the edge of a canopy, in the gap
// between two structures. None of them is centre stage.
const LURKER_SPOTS = [
  { fx: 0.17, fy: 0.26 },   // the far wall, left of Willy's bench
  { fx: 0.66, fy: 0.27 },   // the open stone under V'yen's sign
  { fx: 0.90, fy: 0.62 },   // the gap between the stall and the tent
  { fx: 0.27, fy: 0.68 },   // mid-left, past the fountain
  { fx: 0.50, fy: 0.22 },   // dead centre back, above the fountain
  { fx: 0.07, fy: 0.66 },   // far left, by the shop's shoulder
];

// Pick a spot that is actually standable. A fraction that lands on the
// fountain basin or inside the tent canopy is not "close enough" — she
// would be standing in stone — so the spot is walked one tile at a
// time outward until it is walkable, and abandoned if it is not.
function pickLurkerSpot(cols, rows, portrait) {
  const order = LURKER_SPOTS.map((s, i) => ({ s, k: Math.random(), i }))
    .sort((a, b) => a.k - b.k);
  for (const { s } of order) {
    const bx = Math.min(cols - 1, Math.max(0, Math.round(s.fx * (cols - 1))));
    const by = Math.min(rows - 1, Math.max(0, Math.round(s.fy * (rows - 1))));
    for (const [dx, dy] of [[0,0],[0,1],[1,0],[-1,0],[0,-1],[1,1],[-1,1]]) {
      const x = bx + dx, y = by + dy;
      if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
      if (zIsWalkable(x, y, portrait)) return { x, y };
    }
  }
  return null;
}

// eslint-disable-next-line no-unused-vars -- MOUNT IS COMMENTED OUT, NOT
// DELETED. See the block in the Plaza render where <PlazaLurker/> used to
// stand: caP'n took the ambient J'ynxi out "for now" 2026-08-19 because
// the painted Plaza art hides her. The component is kept intact so that
// restoring her is deleting one comment, not rebuilding a feature.
function PlazaLurker({ tile, cols, rows, portrait }) {
  const [at, setAt] = useState({ x: null, y: null, on: false });

  useEffect(() => {
    let alive = true;
    let tCycle = null, tFlip = null;
    const rnd = (a, b) => a + Math.random() * (b - a);

    const appear = () => {
      if (!alive) return;
      const p = pickLurkerSpot(cols, rows, portrait);
      if (!p) { tCycle = setTimeout(appear, rnd(9000, 17000)); return; }
      // Move her while she is still at zero opacity, THEN light her.
      // Doing both in one commit makes her slide across the Plaza in
      // full view, which is a completely different character.
      setAt({ x: p.x, y: p.y, on: false });
      tFlip  = setTimeout(() => { if (alive) setAt(a => ({ ...a, on: true })); }, 60);
      tCycle = setTimeout(vanish, rnd(24000, 48000));
    };
    const vanish = () => {
      if (!alive) return;
      setAt(a => ({ ...a, on: false }));
      tCycle = setTimeout(appear, rnd(12000, 34000));
    };

    tCycle = setTimeout(appear, rnd(3000, 9000));
    return () => { alive = false; clearTimeout(tCycle); clearTimeout(tFlip); };
  }, [cols, rows, portrait]);

  if (at.x == null) return null;

  const w = Math.round(tile * 1.15);
  const h = Math.round(tile * 1.45);
  return (
    <img
      src={getCompanionSprite("jynxi")}
      alt=""
      aria-hidden="true"
      decoding="async"
      onError={e => { e.target.style.display = "none"; }}
      style={{
        position: "absolute",
        left: Math.round(at.x * tile - (w - tile) / 2),
        top:  Math.round(at.y * tile - (h - tile)),
        width: w, height: h,
        objectFit: "contain", objectPosition: "center bottom",
        imageRendering: "pixelated",
        zIndex: 2, pointerEvents: "none",
        opacity: at.on ? 0.62 : 0,
        transition: "opacity 2.8s ease",
        filter: "grayscale(0.28) brightness(0.82) saturate(0.85)",
      }}
    />
  );
}

function PlayerEntity({ player, isLocal, colorIndex, companionId, equipped, tile, localSpriteRef, localCompanionRef, compDir, radiantTier }) {
  const TILE = tile || TILE_DEFAULT;
  const color = PLAYER_COLORS[colorIndex % 4];
  const cx = player.x - (player.facingX || 0);
  const cy = player.y - (player.facingY || 0);

  // Position model:
  //   - Remote players: tile position with CSS transition for network smoothing.
  //   - Local player: tile position with NO transition — the rAF loop in Plaza
  //     applies a sub-tile translate3d via localSpriteRef every frame for smooth
  //     fractional movement. Doing both at once causes flicker (CSS + rAF fight).
  const vx = player.x;
  const vy = player.y;

  // ── Walk → rest → idle ────────────────────────────────────────
  // Three states, not two. The moment a step stops landing we drop to the
  // DIRECTIONAL rest sprite, so letting go of the d-pad leaves her facing
  // the way she was walking instead of snapping to front. Only after
  // IDLE_AFTER of genuine stillness does the true idle cycle take over —
  // and that cycle (and its frame order, which differs per character)
  // lives in SpriteManifest, not here.
  const REST_AFTER = 140;   // just longer than one movement tick
  const IDLE_AFTER = 7000;
  const IDLE_TICK  = 600;

  const walkFrameRef   = useRef(false);
  const lastPosRef     = useRef({ x: player.x, y: player.y });
  const [walkFrame, setWalkFrame] = useState(false);
  const [motion,   setMotion]     = useState("rest");   // walk | rest | idle
  const [idleStep, setIdleStep]   = useState(0);
  const restTimerRef  = useRef(null);
  const idleTimerRef  = useRef(null);
  const idleAnimRef   = useRef(null);

  const beginIdleCountdown = useCallback(() => {
    idleTimerRef.current = setTimeout(() => {
      setMotion("idle");
      setIdleStep(0);
      idleAnimRef.current = setInterval(() => setIdleStep(n => n + 1), IDLE_TICK);
    }, IDLE_AFTER);
  }, []);

  const clearAnimTimers = useCallback(() => {
    clearTimeout(restTimerRef.current);
    clearTimeout(idleTimerRef.current);
    clearInterval(idleAnimRef.current);
  }, []);

  useEffect(() => {
    if (player.x === lastPosRef.current.x && player.y === lastPosRef.current.y) return;
    lastPosRef.current = { x: player.x, y: player.y };
    walkFrameRef.current = !walkFrameRef.current;
    setWalkFrame(walkFrameRef.current);
    setMotion("walk");
    setIdleStep(0);
    clearAnimTimers();
    restTimerRef.current = setTimeout(() => {
      setMotion("rest");
      beginIdleCountdown();
    }, REST_AFTER);
  }, [player.x, player.y, clearAnimTimers, beginIdleCountdown]);

  // Standing still from mount still earns the true idle.
  useEffect(() => {
    beginIdleCountdown();
    return clearAnimTimers;
  }, [beginIdleCountdown, clearAnimTimers]);

  const playerSprite =
      motion === "idle" ? getIdleSprite(player.gender, idleStep)
    : motion === "rest" ? getRestSprite(player.gender, player.facingX ?? 0, player.facingY ?? 1)
    :                     getPlayerSprite(player.gender, player.facingX ?? 0, player.facingY ?? 1, walkFrame);

  const playerSize   = Math.floor(TILE * 2.6);
  const companionSize = Math.floor(TILE * 1.5);
  const gap = TILE * 1.5;

  const fx = player.facingX || 0;
  const fy = player.facingY || 1;
  // Companion offset: where the companion sits relative to the player.
  // Defaults to "opposite facing" (trails behind), but the LOCAL player
  // passes an explicit compDir that only updates on an actual step — so a
  // pure turn (turn-then-step) leaves the companion in place, letting you
  // pivot to FACE your companion to pet it.
  const cdx = (compDir && typeof compDir.x === "number") ? compDir.x : -fx;
  const cdy = (compDir && typeof compDir.y === "number") ? compDir.y : -fy;
  const compLeft = vx*TILE + cdx*gap*2 - companionSize/2 + TILE/2;
  const compTop  = vy*TILE + (cdy === 1 ? gap*2 : cdy*gap) - companionSize + TILE;

  // CompanionSprite handles placeholder → real-art fallthrough automatically.
  // `companionId` flows from Firebase presence (profile.equippedCompanion).
  const hasCompanion = !!companionId;

  // Smoothing source:
  //   Remote players: CSS transition smooths network throttling (~80ms cadence).
  //   Local player:   NO transition — rAF transforms the div directly via ref.
  const smoothTransition = isLocal ? "none" : "left 0.15s linear, top 0.15s linear";

  return (
    <>
      {hasCompanion ? (
        <div ref={isLocal ? localCompanionRef : null}
          style={{ position: "absolute",
          left: compLeft, top: compTop,
          width: companionSize, height: companionSize,
          zIndex: 8, transition: smoothTransition,
          willChange: isLocal ? "transform" : undefined,
          display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.9,
          pointerEvents: "none" }}>
          {/* SHIP 10 — the body walks with you. `body` swaps the webp for
              the kit's 3D being (companionBody3D.jsx); it faces where the
              player faces and bobs when the square under it moves. */}
          <CompanionSprite companionId={companionId} size={companionSize} radiantTier={radiantTier} body facing={{ x: fx, y: fy }} />
        </div>
      ) : null}
      <div ref={isLocal ? localSpriteRef : null}
        style={{ position: "absolute",
        left: vx*TILE - playerSize/2 + TILE/2,
        top:  vy*TILE - playerSize + TILE,
        width: playerSize, height: playerSize,
        zIndex: 10, transition: smoothTransition,
        willChange: isLocal ? "transform" : undefined,
        display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
          whiteSpace: "nowrap", fontSize: 8, color: isLocal ? GM.inkGoldBright : GM.vellumDim,
          background: "rgba(0,0,0,0.7)", padding: "1px 5px", borderRadius: 3,
          border: `1px solid ${isLocal ? GM.inkGold+"66" : GM.pageBorder}`, fontFamily: GM.font }}>
          {player.name}
          {(() => {
            const title = getEquippedTitle(equipped);
            return title ? (
              <span style={{ color: GM.inkGoldDim, fontStyle: "italic", marginLeft: 4 }}>
                · {title}
              </span>
            ) : null;
          })()}
        </div>
        <div style={{ position: "relative", width: playerSize, height: playerSize }}>
          <Sprite key={playerSprite} src={playerSprite} fallbackColor={isLocal ? color : color+"aa"} size={playerSize}
            style={{ borderRadius: 4, objectFit: "contain", objectPosition: "center bottom" }} />
          {/* Cosmetic overlay — no-op when equipped is null */}
          <CosmeticOverlay equipped={equipped} size={playerSize} gender={player.gender} facing={{ x: player.facingX || 0, y: player.facingY || 1 }} />
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTAL READY-UP OVERLAY
// ─────────────────────────────────────────────────────────────
function PortalReadyUp({ portal, players, localPlayerName, localUid, onReady, onCancel, readySet }) {
  const allPlayers = [{ name: localPlayerName }, ...Object.values(players).map(p => ({ name: p.name }))];
  const myReady = readySet[localUid || localPlayerName];
  const allReady = myReady && Object.keys(players).every(sid => readySet[sid]);
  const dungeon = DUNGEONS.find(d => d.id === portal.dungeonId);
  const isLocked = portal.id === "jynxi" ? true : (dungeon && !dungeon.unlocked);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter")     { e.preventDefault(); if (!isLocked && !myReady) onReady(); }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onCancel(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLocked, myReady]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,3,0,0.82)",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundImage: `url(${UI.boss})`, backgroundSize: "100% 100%",
        borderRadius: 8, padding: "36px 40px 28px", width: "min(92vw,480px)", textAlign: "center", minHeight: 280,
        boxSizing: "border-box" }}>
        <div style={{ fontSize: 10, color: GM.vellum, letterSpacing: 4, marginBottom: 6,
          fontWeight: 700, textShadow: `0 0 8px ${portal.color}, 0 1px 4px rgba(0,0,0,1)` }}>◆ EMPERIUM ENTRANCE</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, marginBottom: 4,
          textShadow: "0 1px 6px rgba(0,0,0,1)" }}>
          {portal.label}
        </div>
        <div style={{ fontSize: 11, color: GM.vellumDim, fontStyle: "italic", marginBottom: 20,
          textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{portal.subtitle}</div>

        {isLocked ? (
          <div style={{ color: GM.vellumDim, fontSize: 13, fontStyle: "italic", marginBottom: 20,
            textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
            ◈ This entrance is sealed.<br />
            <span style={{ fontSize: 11, color: GM.vellumFaint }}>Clear Minerium I to unlock.</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            {allPlayers.map((p, i) => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: PLAYER_COLORS[i%4], flexShrink: 0 }} />
                <span style={{ flex: 1, color: GM.vellum, fontSize: 13, textAlign: "left",
                  textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.name}</span>
                <span style={{ fontSize: 12, color: readySet[p.name] ? "#22c55e" : GM.vellumDim,
                  textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                  {readySet[p.name] ? "✓ READY" : "waiting..."}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 24, justifyContent: "center" }}>
          <button onClick={onCancel} style={{ padding: "8px 0", border: "none", background: "transparent",
            color: GM.vellumDim, cursor: "pointer", fontSize: 12, fontFamily: GM.font,
            textShadow: "0 1px 3px rgba(0,0,0,1)" }}>Cancel</button>
          {!isLocked && (
            <button onClick={onReady} style={{ padding: "8px 0", border: "none", background: "transparent",
              color: myReady ? "#22c55e" : GM.vellum, cursor: "pointer",
              fontSize: 13, fontWeight: 900, fontFamily: GM.font, transition: "all 0.2s",
              textShadow: myReady ? "0 0 8px #22c55e, 0 1px 3px rgba(0,0,0,1)" : `0 0 8px ${portal.color}, 0 1px 3px rgba(0,0,0,1)` }}>
              {myReady ? "✓ Ready!" : "Ready Up"}
            </button>
          )}
        </div>
        {allReady && !isLocked && (
          <div style={{ marginTop: 14, color: "#22c55e", fontSize: 12, fontStyle: "italic",
            textShadow: "0 0 8px #22c55e, 0 1px 3px rgba(0,0,0,1)" }}>
            All players ready — entering the Minerium...
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LOGIN OVERLAY
// ─────────────────────────────────────────────────────────────
function LoginOverlay({ onLogin }) {
  const [authMode, setAuthMode]               = useState("login");
  const [email, setEmail]                     = useState("");
  const [displayName, setDisplayName]         = useState("");
  const [password, setPassword]               = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [authError, setAuthError]             = useState("");
  const [loading, setLoading]                 = useState(false);
  const [rememberMe, setRememberMe]           = useState(true);

  const inputStyle = {
    width: "100%", padding: "13px 16px",
    background: `${GM.inkGold}08`, border: `1px solid ${GM.inkBrown}`,
    borderRadius: 2, color: GM.vellum, fontFamily: GM.font,
    fontSize: 15, outline: "none", boxSizing: "border-box", caretColor: GM.inkGold,
  };

  const handleLogin = async () => {
    setAuthError(""); setLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;
      const name = cred.user.displayName || email.split("@")[0];
      let profile = await getProfileByUid(uid);
      if (!profile) profile = await createProfile(uid, name);
      onLogin(uid, name, profile);
    } catch (e) {
      setAuthError(e.code === "auth/invalid-credential" ? "Incorrect email or password."
        : e.code === "auth/user-not-found" ? "No account found. Register below."
        : e.code === "auth/too-many-requests" ? "Too many attempts. Try again later."
        : "Login failed. Check your details.");
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setAuthError("");
    if (!displayName.trim()) { setAuthError("Enter your Aenarchist name."); return; }
    if (displayName.trim().length < 3) { setAuthError("Name must be at least 3 characters."); return; }
    if (!email.trim()) { setAuthError("Enter your email."); return; }
    if (!password) { setAuthError("Enter a password."); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    if (password !== passwordConfirm) { setAuthError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
      const uid = cred.user.uid;
      const profile = await createProfile(uid, displayName.trim());
      onLogin(uid, displayName.trim(), profile);
    } catch (e) {
      setAuthError(e.code === "auth/email-already-in-use" ? "Email already registered. Log in instead."
        : e.code === "auth/invalid-email" ? "Enter a valid email address."
        : e.code === "auth/weak-password" ? "Password must be at least 6 characters."
        : "Registration failed. Try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(5,3,0,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <div style={{ width: "min(92vw, 420px)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8, fontFamily: GM.font }}>◆ THE PLAZA ◆</div>
          <div style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic",
            background: `linear-gradient(135deg, ${GM.inkGoldBright}, ${GM.inkGold})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontFamily: GM.font, letterSpacing: 2 }}>Identify Yourself</div>
          <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
            "The Minerium remembers all who enter."
          </div>
        </div>

        <div style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
          border: `1px solid ${GM.inkBrown}`, borderRadius: 4, padding: "28px 24px",
          boxShadow: "0 0 40px rgba(0,0,0,0.8)" }}>

          <div style={{ display: "flex", marginBottom: 24, background: GM.page, borderRadius: 4, padding: 3, gap: 3 }}>
            {[["login","Returning"],["register","New Aenarchist"]].map(([mode, label]) => (
              <button key={mode} onClick={() => { setAuthMode(mode); setAuthError(""); }}
                style={{ flex: 1, padding: "10px", borderRadius: 2, border: "none", cursor: "pointer",
                  fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 1, transition: "all 0.2s",
                  background: authMode === mode ? `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid})` : "transparent",
                  color: authMode === mode ? GM.inkGoldBright : GM.vellumFaint,
                  boxShadow: "none" }}>
                {authMode === mode ? `◆ ${label} ◆` : label}
              </button>
            ))}
          </div>

          {authMode === "register" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>AENARCHIST NAME</div>
              <input placeholder="Your name in the Plaza..." value={displayName}
                onChange={e => { setDisplayName(e.target.value); setAuthError(""); }}
                style={inputStyle} autoFocus />
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>EMAIL</div>
            <input placeholder="your@email.com" value={email} type="email"
              onChange={e => { setEmail(e.target.value); setAuthError(""); }}
              onKeyDown={e => e.key === "Enter" && (authMode === "login" ? handleLogin() : handleRegister())}
              style={inputStyle} autoFocus={authMode === "login"} />
          </div>

          <div style={{ marginBottom: authMode === "register" ? 14 : 20 }}>
            <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>PASSWORD</div>
            <input type="password" placeholder={authMode === "register" ? "Min 6 characters..." : "Your password..."}
              value={password}
              onChange={e => { setPassword(e.target.value); setAuthError(""); }}
              onKeyDown={e => e.key === "Enter" && (authMode === "login" ? handleLogin() : handleRegister())}
              style={inputStyle} />
          </div>

          {authMode === "register" && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>CONFIRM PASSWORD</div>
              <input type="password" placeholder="Confirm..." value={passwordConfirm}
                onChange={e => { setPasswordConfirm(e.target.value); setAuthError(""); }}
                style={inputStyle} />
            </div>
          )}

          {authError && (
            <div style={{ fontSize: 12, color: SD.red, textAlign: "center", marginBottom: 12,
              padding: "8px 12px", background: `${GM.inkRed}18`, border: `1px solid ${GM.inkRed}`, borderRadius: 2 }}>
              {authError}
            </div>
          )}

          <div onClick={() => setRememberMe(r => !r)}
            style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer" }}>
            <div style={{ width: 18, height: 18, borderRadius: 2, flexShrink: 0,
              border: `2px solid ${rememberMe ? GM.inkGold : GM.pageBorder}`,
              background: rememberMe ? `${GM.inkGold}33` : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: GM.inkGoldBright, transition: "all 0.15s" }}>
              {rememberMe ? "✓" : ""}
            </div>
            <span style={{ fontSize: 11, color: rememberMe ? GM.vellumDim : GM.vellumFaint, fontFamily: GM.font }}>
              Remember me on this device
            </span>
          </div>

          <button onClick={authMode === "login" ? handleLogin : handleRegister}
            disabled={loading || !email.trim() || !password}
            style={{ width: "100%", padding: "16px",
              background: `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid})`,
              border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
              fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
              cursor: "pointer", boxShadow: `0 0 16px ${GM.inkGold}33`, transition: "all 0.2s",
              opacity: (loading || !email.trim() || !password) ? 0.35 : 1 }}>
            {loading ? "◆ Verifying..." : authMode === "login" ? "◆ Enter the Plaza" : "◆ Forge Your Identity"}
          </button>

          <div style={{ fontSize: 10, color: GM.vellumFaint, textAlign: "center", marginTop: 12, fontStyle: "italic", fontFamily: GM.font }}>
            Forgot your password? Use Firebase's reset link — coming soon.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STARTER DECK PICKER — gender step first
// ─────────────────────────────────────────────────────────────
function StarterPickerOverlay({ playerName, uid, onPick }) {
  const [step, setStep]         = useState("gender");
  const [gender, setGender]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);

  const handlePick = async () => {
    if (!selected || !gender) return;
    setLoading(true);
    const { update: fbUpdate, ref: fbRef } = await import("firebase/database");
    await fbUpdate(fbRef(db, `profiles/${uid}`), { starterDeckId: selected, gender });
    const { addCardToCollection } = await import("./progression");
    const deckData = getStarterDeck(selected);
    if (deckData) {
      for (const cardId of Object.keys(deckData.grimoire || {}))
        await addCardToCollection(uid, cardId, false, null, "starter");
      for (const curioId of (deckData.curios || []))
        if (curioId) await addCardToCollection(uid, curioId, false, null, "starter");
    }
    setLoading(false);
    onPick(selected, gender);
  };

  const GENDER_OPTS = [
    { id: "m", label: "Male",   icon: "♂", color: GM.inkGoldBright },
    { id: "f", label: "Female", icon: "♀", color: GM.inkGold },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(5,3,0,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <div style={{ width: "min(92vw, 480px)", maxHeight: "90vh", overflowY: "auto" }}>

        {step === "gender" && (<>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8 }}>◆ WELCOME, {playerName.toUpperCase()} ◆</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, fontFamily: GM.font }}>
              Who Are You?
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
              Choose your form. You can change this later.
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            {GENDER_OPTS.map(opt => (
              <div key={opt.id} onClick={() => setGender(opt.id)} style={{
                flex: 1, padding: "28px 16px", textAlign: "center", cursor: "pointer",
                background: gender === opt.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
                border: `1px solid ${gender === opt.id ? opt.color : GM.pageMid}`,
                borderRadius: 12, transition: "all 0.2s",
                boxShadow: gender === opt.id ? shimmerBorder(opt.color) : "none" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>{opt.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 900,
                  color: gender === opt.id ? opt.color : GM.vellumDim,
                  fontFamily: GM.font }}>{opt.label}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setStep("deck")} disabled={!gender}
            style={{ width: "100%", padding: "16px",
              background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`,
              border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
              fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
              cursor: gender ? "pointer" : "not-allowed", opacity: gender ? 1 : 0.35,
              boxShadow: `0 0 14px ${GM.inkGold}44` }}>
            ◆ Continue
          </button>
        </>)}

        {step === "deck" && (<>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8 }}>◆ WELCOME ◆</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, fontFamily: GM.font }}>
              Choose Your Starter Deck
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
              This is your foundation. Choose wisely.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {STARTER_DECKS.map(deck => (
              <div key={deck.id} onClick={() => setSelected(deck.id)} style={{
                background: selected === deck.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
                border: `1px solid ${selected === deck.id ? GM.inkGold : GM.inkBrown}55`,
                borderRadius: 12, padding: "14px 18px", cursor: "pointer", transition: "all 0.2s",
                boxShadow: selected === deck.id ? `0 0 14px ${GM.inkGold}44` : "none" }}>
                <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic",
                  color: selected === deck.id ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font }}>
                  {deck.name}
                </div>
                {deck.description && <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 4 }}>{deck.description}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep("gender")}
              style={{ padding: "16px 20px", background: "transparent",
                border: `1px solid ${GM.inkBrown}66`, borderRadius: 10, color: GM.vellumFaint,
                fontFamily: GM.font, fontSize: 13, cursor: "pointer" }}>← Back</button>
            <button onClick={handlePick} disabled={!selected || loading}
              style={{ flex: 1, padding: "16px",
                background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`,
                border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
                fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
                cursor: "pointer", opacity: (!selected || loading) ? 0.35 : 1,
                boxShadow: `0 0 14px ${GM.inkGold}44` }}>
              {loading ? "◆ Preparing..." : "◆ Enter the Plaza"}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GENDER SETTINGS
// ─────────────────────────────────────────────────────────────
function GenderSettings({ uid, currentGender, onClose, onChanged }) {
  const [saving, setSaving] = useState(false);

  const pick = async (g) => {
    if (g === currentGender || saving) return;
    setSaving(true);
    const { update: fbUpdate, ref: fbRef } = await import("firebase/database");
    await fbUpdate(fbRef(db, `profiles/${uid}`), { gender: g });
    setSaving(false);
    onChanged(g);
    onClose();
  };

  const OPTS = [
    { id: "m", label: "Male",   icon: "♂", color: GM.inkGoldBright },
    { id: "f", label: "Female", icon: "♀", color: GM.inkGold },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(5,3,0,0.90)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(88vw, 340px)",
        background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
        border: `1px solid ${GM.inkGold}44`, borderRadius: 16, padding: "28px 24px",
        boxShadow: `0 0 14px ${GM.inkGold}33` }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 4, marginBottom: 6 }}>◆ APPEARANCE ◆</div>
          <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum }}>Change Form</div>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {OPTS.map(opt => (
            <div key={opt.id} onClick={() => pick(opt.id)} style={{
              flex: 1, padding: "20px 12px", textAlign: "center",
              cursor: saving ? "not-allowed" : "pointer",
              background: currentGender === opt.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
              border: `1px solid ${currentGender === opt.id ? opt.color : GM.pageMid}`,
              borderRadius: 12, transition: "all 0.2s",
              boxShadow: currentGender === opt.id ? shimmerBorder(opt.color) : "none",
              opacity: saving ? 0.5 : 1 }}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>{opt.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 900,
                color: currentGender === opt.id ? opt.color : GM.vellumDim }}>
                {opt.label}
              </div>
              {currentGender === opt.id && (
                <div style={{ fontSize: 9, color: opt.color, marginTop: 4, letterSpacing: 1 }}>◆ ACTIVE</div>
              )}
            </div>
          ))}
        </div>
        <button onClick={onClose} style={{ width: "100%", padding: "10px", background: "transparent",
          border: `1px solid ${GM.inkBrown}66`, borderRadius: 8, color: GM.vellumFaint,
          cursor: "pointer", fontSize: 12, fontFamily: GM.font }}>Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREST BADGE
// ─────────────────────────────────────────────────────────────
// CrestBadge moved to CoinGlyph.js on 2026-07-28 alongside its new
// sibling CoinBadge, so the two pills can never drift apart. Both are
// imported at the top of this file. Nothing to declare here.

// The wallet: Terror's spending money first, the reserve beside it.
// Order is deliberate — ◕ is what the player actually uses, ◆ is what
// they're saving. Reading left to right should feel like that.
function Wallet({ profile, compact = false }) {
  if (!profile) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? 5 : 7 }}>
      <CoinBadge  amount={profile.chocolateCoins} />
      <CrestBadge amount={profile.divineCrests} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// WILLY DIALOGUE ENGINE
// ─────────────────────────────────────────────────────────────
// getWillyDialogue — returns { lines: [...], sweetTreat?: boolean, grantLineIdx?: number }
// - sweetTreat: true means the sweet-treat grant flow runs alongside this dialogue.
// - grantLineIdx: the line index AFTER which the +5 ◕ pour animation fires
//   and the atomic write happens. Defaults to undefined for non-grant branches.
function getWillyDialogue(profile) {
  if (!profile) return { lines: ["...","You shouldn't be here without a profile.","The Plaza doesn't accept ghosts."] };

  const wins       = profile.totalWins || 0;
  const stats      = profile.stats || {};
  const ilkWins    = stats.ilkWins || {};
  const streak     = stats.winStreak || 0;
  const collection = profile.collection || {};
  const collectionSize = Object.keys(collection).length;
  const defeated   = profile.storyProgress?.defeatedOpponents || [];
  const dungeons   = profile.storyProgress?.completedDungeons || [];
  const beatLord   = (stats.difficultyWins?.Lord || 0) > 0;
  const ownsDragon = collection["chaos_dragon_lord"] || collection["chaos_dragon_champion"] || collection["chaos_dragon_beb"];
  const losses     = (stats.totalGames || 0) - wins;

  // ── FIRST-VISIT SWEET TREAT ────────────────────────────────
  // Top of the cascade. Fires for any player who finished onboarding
  // but has not yet collected their five Coins at the front desk.
  // The grant fires at grantLineIdx (line "Sweet treat. As promised. Hold out your hand.").
  if (profile.hasCompletedOnboarding && !profile.hasReceivedSweetTreat) {
    return {
      sweetTreat: true,
      grantLineIdx: 2, // 0:"Ah!...", 1:"You came...", 2:"Sweet treat..." → grant after this line
      lines: [
        "Ah! There you are. There you are there you are.",
        "You came. Like I said. Good good.",
        "Sweet treat. As promised. Hold out your hand.",
        "There. Five Coins. Sweet treat. In this world.",
        "Mmm. Sweet.",
        "...And the real tutorial. The Solrend, proper. Bones and all.",
        "Want to learn now? Or come back later — I'll be right here.",
      ],
    };
  }

  // ── EASTER EGG BRANCHES ────────────────────────────────────
  // Tucked above the standard cascade. Fire on quirky thresholds the
  // win-count branches don't catch. Each is a small reward for noticing.
  if (collectionSize >= 100) return { lines: ["A hundred cards.","The Void doesn't make jokes.","But if it did. That'd be one."] };
  if (streak === 0 && wins >= 50)  return { lines: ["You lost.","After all that.","...That hurts more than the first one. Doesn't it."] };
  if ((stats.difficultyWins?.Beb || 0) >= 20 && (stats.difficultyWins?.Lord || 0) === 0) return { lines: ["Twenty Bebis. Beat into dust.","You're warm.","Try a Champion. Then a Lord.","Or don't. Bebis can't hurt you back."] };
  if (losses >= 30 && wins < 5)    return { lines: ["...","You keep coming back.","I respect that.","Don't tell anyone I said it."] };
  if (defeated.length === 1 && (stats.totalGames || 0) >= 15) return { lines: ["One opponent. Fifteen tries.","The Shatterfield doesn't grade for effort.","But I do. A little."] };
  if (collectionSize === 1)        return { lines: ["One card.","That's a start.","...That's a small start."] };

  // ── STANDARD CASCADE ───────────────────────────────────────
  if (streak >= 5) return { lines: [`${streak} wins in a row.`,"Don't get comfortable. The Void has a long memory.","...I'm watching you."] };
  if (dungeons.length >= 4) return { lines: ["Four Mineriums. Four.","I've been here a long time.","I have never seen anyone do that.","...Don't tell J'ynxi."] };
  if (dungeons.includes(1) && dungeons.length === 1) return { lines: ["You walked out of the Space Emperium.","I didn't think you would.","The stars noticed."] };
  if ((ilkWins["Space"] || 0) >= 3) return { lines: ["You keep running those star-born things.","H-01... Xulan... the void-walkers.","The Space Ilk is starting to remember your name."] };
  if ((ilkWins["Phantom"] || 0) >= 3) return { lines: ["Shadow beasts.","You really like things that hunt in the dark.","...So do I."] };
  if ((ilkWins["Time"] || 0) >= 3) return { lines: ["The machines. Always the machines.","Z4Z, DG-0... they're tools, you know.","Make sure you're the one using them."] };
  if ((ilkWins["Dragon"] || 0) >= 3) return { lines: ["Dragons.","Every single time.","Fine. But don't come to me when Chaos Dragon decides it doesn't like you anymore."] };
  if (beatLord) return { lines: ["That opponent you dismantled.","They weren't weak.","You're becoming something the Shatterfield hasn't seen in a while."] };
  if (ownsDragon) return { lines: ["I see it in your Grimoire.","The Dragon.","...Be careful with that one.","It has beaten players who thought they controlled it."] };
  if (collectionSize >= 60) return { lines: ["You've been collecting.","That many cards... the Void is paying attention to you.","Good."] };
  if (collectionSize >= 30) return { lines: ["Your collection grows.","Every card remembers who holds it.","Don't forget that."] };
  if (defeated.length >= 5) return { lines: [`${defeated.length} story opponents.`,"They all thought they were ready for you.","They were not."] };
  if (wins >= 20) return { lines: [`${wins} wins.`,"The Plaza has seen many Aenarchists.","Most of them stopped showing up.","You didn't."] };
  if (wins >= 10) return { lines: ["Ten wins.","Some people never get there.","Don't tell them I said that."] };
  if (wins >= 5) return { lines: ["Five wins.","You're starting to understand something.","I'm not sure what yet. Neither are you.","That's fine."] };
  if (wins === 1) return { lines: ["One win.","I heard what happened in the Shatterfield.","Beginner's luck, probably.","...Probably."] };
  if (wins === 0 && stats.totalGames > 0) return { lines: ["You keep losing.","The Shatterfield doesn't care about your feelings.","Neither do I.","Go again."] };
  return { lines: ["Ah.","Another soul finds the Plaza.","Don't touch anything.","...Welcome."] };
}

// ─────────────────────────────────────────────────────────────
// WILLY DIALOGUE MODAL
// ─────────────────────────────────────────────────────────────
function WillyDialogue({ profile, uid, onClose, onTutorial1, onRulebook, showToast, logLine }) {
  // Capture dialogue once on mount so re-renders during the grant flow
  // (which mutate profile.hasReceivedSweetTreat) don't yank the script
  // out from under us mid-conversation.
  const dialogueRef = useRef(null);
  if (dialogueRef.current === null) dialogueRef.current = getWillyDialogue(profile);
  const dialogue = dialogueRef.current;
  const lines = dialogue.lines;
  const isSweetTreat = !!dialogue.sweetTreat;
  const grantLineIdx = dialogue.grantLineIdx;

  const [lineIdx, setLineIdx] = useState(0);
  const [pourPulse, setPourPulse] = useState(false);  // +5 ◕ animation flag
  const grantFiredRef = useRef(false);                // double-fire guard inside session
  const finishedRef  = useRef(false);                 // prevent double-finish

  // Session record — every Willy line lands in the plaza console log
  // as it appears, and stays there for the whole browser session.
  const logLineRef = useRef(logLine);
  useEffect(() => { logLineRef.current = logLine; }, [logLine]);
  useEffect(() => {
    const l = lines[lineIdx];
    if (l && logLineRef.current) logLineRef.current(typeof l === "string" ? l : (l.text || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx]);

  // ── Stable refs for callbacks ──────────────────────────────
  // Plaza re-renders constantly (multiplayer presence + position rAF),
  // and onClose/onRulebook are inline arrows that get a fresh ref every
  // Plaza render. Without these refs, the keydown effect tears down and
  // re-attaches every frame, which under React 18 batching can cascade
  // into infinite update warnings during the close → navigate transition.
  const onCloseRef    = useRef(onClose);
  const onRulebookRef = useRef(onRulebook);
  const onTutorial1Ref = useRef(onTutorial1);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onRulebookRef.current = onRulebook; }, [onRulebook]);
  useEffect(() => { onTutorial1Ref.current = onTutorial1; }, [onTutorial1]);

  // ── Finish handler — close + route to the How To Play hub ──
  // Single source of truth for the post-dialogue tutorial picker.
  // Idempotent — guards against double-fire from rapid Enter presses.
  //
  // ⭐ onTutorial1, NOT onRulebook. This called onRulebook and the
  // comment underneath explained why: "onRulebook is wired to
  // goTo('howtoplay') in App.js — that's the hub." That was true right
  // up until 2026-08-20, when onRulebook was repointed at the actual
  // Rulebook so the menu row named "The Rulebook" would open the
  // Rulebook. The prop had TWO consumers and only one of them was
  // looking: finishing the groundskeeper's intro started dropping a
  // brand-new player into 150KB of reference text instead of the
  // tutorial picker it was written to open.
  //
  // It reads the RIGHT prop now, which was already being passed in and
  // sitting unused two lines up in the same destructure. The lesson is
  // the one PRIME §2.9 already carries as PARITY IS A CLASS OF BUG:
  // before repointing a shared prop, grep for its other consumers.
  const handleFinish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onCloseRef.current?.();
    onTutorial1Ref.current?.();
  }, []);

  // ── SWEET TREAT GRANT ──────────────────────────────────────
  // Fires exactly once when we step PAST the grant line.
  // Atomic write happens inside awardSweetTreat; we just trigger it.
  useEffect(() => {
    if (!isSweetTreat) return;
    if (grantFiredRef.current) return;
    if (lineIdx <= grantLineIdx) return;
    if (!uid) return;
    grantFiredRef.current = true;
    setPourPulse(true);
    setTimeout(() => setPourPulse(false), 1800);
    awardSweetTreat(uid).then((res) => {
      if (res && !res.alreadyClaimed) {
        showToast?.("✦ +5 Chocolate Coins · Sweet Treat", GM.inkGold);
      }
    }).catch((err) => {
      console.error("[Willy] sweet treat grant failed:", err);
    });
    // showToast intentionally excluded — it's a fresh ref every Plaza
    // render and we only need it once at grant time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx, isSweetTreat, grantLineIdx, uid]);

  // Keydown — bound once per dialogue lifetime, not once per Plaza render.
  // We read live state via refs so the handler always sees current lineIdx.
  const lineIdxRef    = useRef(lineIdx);
  const linesLenRef   = useRef(lines.length);
  useEffect(() => { lineIdxRef.current  = lineIdx;  }, [lineIdx]);
  useEffect(() => { linesLenRef.current = lines.length; }, [lines.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (lineIdxRef.current < linesLenRef.current - 1) setLineIdx(i => i + 1);
        else handleFinish();
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleFinish]);

  const cur = lines[lineIdx];
  const curText = typeof cur === "string" ? cur : (cur?.text || "");
  // ⭐ THE DIALOGUE WINDOW (caP'n, 2026-09-23: "when all characters are
  // spoken to from now on, I want this style of menu, where the character
  // model is here"). Willy stands on the NpcStage in his 3D body, you on
  // the other side. The script, the grant, the keys and the finish route
  // are untouched — only the shell changed. Click the well to advance.
  return (
    <NpcStage
      keeperId="willy"
      badge="Grounds Keeper"
      line={curText}
      profile={profile}
      backdrop="plaza_1.webp"
      onClose={onClose}
    >
      <div onClick={() => { if (lineIdx < lines.length - 1) setLineIdx(i => i + 1); else handleFinish(); }} style={{ position: "relative", cursor: "pointer", fontFamily: GM.font }}>
        {/* +5 ◕ POUR PULSE — sweet treat grant moment */}
        {pourPulse && (
          <div style={{
            position: "absolute", top: "-40%", left: "50%",
            transform: "translateX(-50%)",
            fontSize: 36, fontWeight: 900, color: GM.inkGold,
            fontFamily: GM.font, letterSpacing: 2,
            textShadow: `0 0 20px ${GM.inkGold}, 0 0 40px ${GM.inkGold}88`,
            pointerEvents: "none", zIndex: 5,
            animation: "willyCrestPour 1.8s ease-out forwards",
          }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              +5 <ChocolateCoin size={30} />
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 5 }}>
            {lines.map((_, i) => (
              <div key={i} style={{ width: 6, height: 6, borderRadius: "50%",
                background: i === lineIdx ? GM.inkGold : GM.pageBorder, transition: "background 0.2s" }} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: GM.vellumFaint, fontStyle: "italic" }}>
            {lineIdx < lines.length - 1
              ? "click / enter to continue ▶"
              : (isSweetTreat ? "click / enter to learn ▶" : "click / enter to continue ▶")}
          </div>
        </div>
      </div>
      <button onClick={onClose}
        style={{ width: "100%", padding: "8px", borderRadius: 8, marginTop: 14,
          border: `1px solid ${GM.pageBorder}`, background: "transparent",
          color: GM.vellumFaint, fontSize: 12, cursor: "pointer" }}>
        ← Leave
      </button>
      <style>{`
        @keyframes willyCrestPour {
          0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.7); }
          20%  { opacity: 1; transform: translate(-50%, 0) scale(1.15); }
          60%  { opacity: 1; transform: translate(-50%, -28px) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -56px) scale(0.95); }
        }
      `}</style>
    </NpcStage>
  );
}

// ─────────────────────────────────────────────────────────────
// BULLETIN BOARD
// ─────────────────────────────────────────────────────────────
const ILK_META = {
  space:   { label: "Space Emperium",   color: "#6d28d9", icon: "✦" },
  phantom: { label: "Phantom Emperium", color: "#c9a84c", icon: "◈" },
  time:    { label: "Time Emperium",    color: "#22d3ee", icon: "⏳" },
  dragon:  { label: "Dragon Emperium",  color: "#dc2626", icon: "🐉" },
};

const RANK_TITLE = (wins) =>
  wins === 0      ? "Unproven"
  : wins < 5      ? "◈ Escling"
  : wins < 20     ? "◆ Beb"
  : wins < 50     ? "⚔ Champion"
  : wins < 100    ? "👑 Lord"
  : "🌑 Escendant";

function BulletinBoard({ profile, playerName, onClose, onCollection, onEnterGame }) {
  const [tab, setTab]               = useState("emperium");
  const [unstable, setUnstable]     = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [ladder, setLadder]         = useState([]); // T16 — PvP ladder
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    setLoading(true);
    let done = 0;
    const check = () => { done++; if (done >= 2) setLoading(false); };
    onValue(ref(db, "world/unstableEmperium"), snap => {
      setUnstable(snap.exists() ? snap.val() : null); check();
    }, { onlyOnce: true });
    onValue(ref(db, "leaderboard"), snap => {
      if (!snap.exists()) { setLeaderboard([]); setLadder([]); check(); return; }
      const all = [];
      const lad = []; // T16 — same node carries pvpScore (mirrored by recordPvpResult)
      snap.forEach(child => {
        const p = child.val();
        if (p.name && (p.wins || 0) > 0) all.push({ name: p.name, wins: p.wins || 0, uid: child.key });
        if (p.name && (p.pvpScore || 0) > 0) lad.push({ name: p.name, score: p.pvpScore || 0, uid: child.key });
      });
      all.sort((a, b) => b.wins - a.wins);
      lad.sort((a, b) => b.score - a.score);
      setLeaderboard(all.slice(0, 10));
      setLadder(lad.slice(0, 10));
      check();
    }, { onlyOnce: true });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ilk = unstable?.ilk ? ILK_META[unstable.ilk] : null;
  const myRank = leaderboard.findIndex(p => p.uid === profile?.uid) + 1;
  const myLadderRank = ladder.findIndex(p => p.uid === profile?.uid) + 1;
  const myScore = profile?.pvp?.score || 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,3,0,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundImage: `url(${UI.bulletin})`, backgroundSize: "100% 100%",
        borderRadius: 8, width: "min(92vw, 500px)", minHeight: 480,
        display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        <div style={{ padding: "130px 32px 48px", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 4, marginBottom: 4, textShadow: "0 1px 4px rgba(0,0,0,1)" }}>◆ THE BULLETIN BOARD ◆</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, textShadow: "0 1px 6px rgba(0,0,0,1)" }}>Plaza Notices</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {[["emperium","🌑 Unstable Emperium"],["void_record","◆ Void Record"],["ladder","⚔ The Ladder"],["wars","⚔ Faction Wars"],["replays","🎞 Replays"],...(SHOW_TOURNAMENTS ? [["brackets","🏆 Brackets"]] : [])].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                flex: "1 1 40%", padding: "10px 6px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${tab === id ? GM.inkGold : GM.inkBrown}55`,
                background: tab === id ? `rgba(0,0,0,0.6)` : "rgba(0,0,0,0.3)",
                color: tab === id ? GM.inkGoldBright : GM.vellumDim,
                fontFamily: GM.font, fontSize: 10, fontWeight: 900,
                textShadow: "0 1px 3px rgba(0,0,0,1)", transition: "all 0.2s" }}>{label}</button>
            ))}
          </div>
          {loading && <div style={{ textAlign: "center", padding: "40px 0", color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>◆ Reading the board...</div>}
          {!loading && tab === "emperium" && (
            <div style={{ flex: 1 }}>
              {unstable && ilk ? (
                <>
                  <div style={{ padding: "16px", borderRadius: 12, marginBottom: 14, background: `rgba(0,0,0,0.65)`, border: `1px solid ${ilk.color}66` }}>
                    <div style={{ fontSize: 9, color: ilk.color, letterSpacing: 3, marginBottom: 6, textShadow: `0 0 8px ${ilk.color}, 0 1px 3px rgba(0,0,0,1)` }}>⚠ CURRENTLY UNSTABLE</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, marginBottom: 4, textShadow: "0 1px 6px rgba(0,0,0,1)" }}>{ilk.icon} {ilk.label}</div>
                    {unstable.bonus && <div style={{ fontSize: 12, color: ilk.color, marginBottom: 8, fontWeight: 700, textShadow: `0 0 6px ${ilk.color}, 0 1px 3px rgba(0,0,0,1)` }}>◆ {unstable.bonus}</div>}
                    {unstable.flavor && <div style={{ fontSize: 11, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.7, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>"{unstable.flavor}"</div>}
                  </div>
                  <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(0,0,0,0.5)", border: `1px solid ${GM.inkBrown}66` }}>
                    <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, marginBottom: 6, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>WHAT THIS MEANS</div>
                    <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.8, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                      The rift surrounding the {ilk.label} is tearing wider this cycle. Decks built around the {unstable.ilk ? unstable.ilk.charAt(0).toUpperCase() + unstable.ilk.slice(1) : ""} Ilk will feel the difference.{unstable.bonus ? ` ${unstable.bonus}.` : ""}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ padding: "20px", borderRadius: 12, textAlign: "center", background: "rgba(0,0,0,0.5)", border: `1px solid ${GM.inkBrown}66` }}>
                  <div style={{ fontSize: 28, marginBottom: 12 }}>◇</div>
                  <div style={{ fontSize: 13, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.8, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>The Shatterfield is calm.<br/>No Emperium is currently unstable.</div>
                  <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 10, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>Check back next cycle.</div>
                </div>
              )}
            </div>
          )}
          {!loading && tab === "void_record" && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                TOP AENARCHISTS BY VICTORIES
                {myRank > 0 && <span style={{ color: GM.inkGold, marginLeft: 8 }}>— YOU: #{myRank}</span>}
              </div>
              {leaderboard.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: GM.vellumFaint, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>◇ The Void Record is empty.<br/>Be the first to claim it.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leaderboard.map((p, i) => {
                    const isMe = p.uid === profile?.uid;
                    const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                    return (
                      <div key={p.uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: isMe ? "rgba(212,168,75,0.15)" : "rgba(0,0,0,0.5)", border: `1px solid ${isMe ? GM.inkGold + "66" : GM.pageBorder}` }}>
                        <div style={{ width: 22, textAlign: "center", flexShrink: 0 }}>
                          {medal ? <span style={{ fontSize: 14 }}>{medal}</span> : <span style={{ fontSize: 11, color: GM.vellumFaint }}>#{i+1}</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellum, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.name}{isMe ? " ◀ you" : ""}</div>
                          <div style={{ fontSize: 9, color: GM.vellumFaint, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{RANK_TITLE(p.wins)}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.wins}</div>
                          <div style={{ fontSize: 8, color: GM.vellumFaint }}>WINS</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* ── X4 · SOLREND TOURNAMENTS ─────────────────────────
              The board owns its own lobby, listener and bracket; this
              tab is one tag. What it cannot do — start a duel from a
              pairing — it says out loud rather than showing a button
              that would do nothing. */}
          {!loading && tab === "brackets" && SHOW_TOURNAMENTS && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                SOLREND TOURNAMENTS
              </div>
              <TournamentBoard uid={profile?.uid} name={playerName || profile?.name} profile={profile} onEnterGame={onEnterGame} />
            </div>
          )}
          {/* ── H6 · REPLAYS ─────────────────────────────────────
              The shelf owns its own load, its own prune and the
              projector it opens; this tab is one tag. */}
          {!loading && tab === "replays" && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                YOUR OWN SOLRENDS, KEPT
              </div>
              <ReplayShelf uid={profile?.uid} />
            </div>
          )}
          {/* ── H5 · FACTION WARS ────────────────────────────────
              The banner is a standalone component carrying its own
              listener AND the settlement walk that pays last week's
              spoils, so it works anywhere it is mounted and this tab
              owns none of it. */}
          {!loading && tab === "wars" && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                THE WEEK'S TALLY
                {factionOf(profile) && <span style={{ color: GM.inkGold, marginLeft: 8 }}>— YOU FIGHT FOR {String(factionOf(profile)).toUpperCase()}</span>}
              </div>
              <FactionWarBanner uid={profile?.uid} faction={factionOf(profile)} />
              {!factionOf(profile) && (
                <div style={{ textAlign: "center", padding: "22px 10px 0", color: GM.vellumFaint, fontSize: 11, fontStyle: "italic", lineHeight: 1.7, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                  You hold no allegiance, so your Solrends land in nobody's column.<br/>
                  The Faction Hall is where that changes.
                </div>
              )}
            </div>
          )}
          {!loading && tab === "ladder" && (
            <div style={{ flex: 1 }}>
              {/* T16 (July 17 2026) — the PvP ladder finally has a face.
                  Scores mirror from recordPvpResult (C8); this board is
                  the pre-season scaffold RANKED SEASONS (M1) inherits. */}
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                TOP WARLOCKS BY LADDER SCORE
                {myLadderRank > 0 && <span style={{ color: GM.inkGold, marginLeft: 8 }}>— YOU: #{myLadderRank}</span>}
              </div>
              {myScore > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, marginBottom: 10, background: "rgba(212,168,75,0.10)", border: `1px solid ${GM.inkGold}44` }}>
                  <span style={{ fontSize: 12 }}>⚔</span>
                  <span style={{ flex: 1, fontSize: 10, color: GM.vellumDim, letterSpacing: 1, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>YOUR LADDER</span>
                  <span style={{ fontSize: 14, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{myScore}</span>
                  <span style={{ fontSize: 9, color: GM.vellumFaint }}>{profile?.pvp?.wins || 0}W · {profile?.pvp?.losses || 0}L</span>
                </div>
              )}
              {ladder.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: GM.vellumFaint, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>◇ The Ladder stands empty.<br/>Win a Solrend against another warlock to climb it.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ladder.map((p, i) => {
                    const isMe = p.uid === profile?.uid;
                    const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                    return (
                      <div key={p.uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: isMe ? "rgba(212,168,75,0.15)" : "rgba(0,0,0,0.5)", border: `1px solid ${isMe ? GM.inkGold + "66" : GM.pageBorder}` }}>
                        <div style={{ width: 22, textAlign: "center", flexShrink: 0 }}>
                          {medal ? <span style={{ fontSize: 14 }}>{medal}</span> : <span style={{ fontSize: 11, color: GM.vellumFaint }}>#{i+1}</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellum, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.name}{isMe ? " ◀ you" : ""}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.score}</div>
                          <div style={{ fontSize: 8, color: GM.vellumFaint }}>SCORE</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <button onClick={onClose} style={{ width: "100%", marginTop: 16, padding: "9px", borderRadius: 2, border: `1px solid ${GM.inkBrown}66`, background: `${GM.page}88`, color: GM.vellumDim, fontSize: 12, cursor: "pointer", fontFamily: GM.font }}>← Leave</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VYREN KIOSK sub-components
// ─────────────────────────────────────────────────────────────
function VyrenModeSelect({ onSelect, onClose }) {
  // H29 — THE THIRD ROW. The Depths moved in with V'yen on July 28
  // and the mode was rebuilt as a totem on July 29; this is the door
  // it was rebuilt behind, and it is the last piece of that move.
  // The Solrend magenta is deliberate — Free Play and Ranked are both
  // ink-gold, and the gauntlet is not a third flavour of matchmaking,
  // it is the ladder. It should not look like its neighbours.
  // H30 — THE MULTIPLAYER DECREE LIFTS. Every id below is the same
  // kind of label THE VYREN PATTERN has always drawn: the code that
  // reads `mode` never sees "2v2 Free Play," it sees "2v2free," and
  // that string is free to keep meaning the same thing forever no
  // matter what this row calls it next. `gauntlet` is still
  // intercepted before `mode` is ever set (see the comment where
  // VyrenModeSelect is mounted below) — same reason as always, The
  // Gauntlet is a full screen Plaza already knows how to open, not a
  // branch this kiosk has to grow.
  const MODES = [
    { id:"free",       label:"Free Play",      sub:"J'ynxi's Cheat Chest — any card, no limits",                 color:GM.inkGold },
    { id:"ranked",     label:"Ranked",         sub:"Your real profile deck — Divine Crests on the line",          color:GM.inkGold, note:"◈ Crest betting coming soon" },
    { id:"ffa3",       label:"3-Way Free-For-All", sub:"Three chairs, one board — attack whoever you want",      color:GM.inkGold },
    { id:"ffa4",       label:"4-Way Free-For-All", sub:"Four chairs, one board — gang up if you dare",           color:GM.inkGold },
    { id:"2v2free",    label:"2v2 — Free Play",    sub:"Bring a partner — Cheat Chest rules, shared Anima",       color:GM.inkGold },
    { id:"2v2ranked",  label:"2v2 — Ranked",       sub:"Bring a partner — your real decks, shared Anima",        color:GM.inkGold, note:"◈ Crest betting coming soon" },
    { id:"coop",       label:"Story — Co-op",      sub:"Bring a partner into your own story fight, shared Anima", color:GM.inkGold },
    { id:"gauntlet",   label:"The Gauntlet",   sub:"Bouts back to back on one pool of Anima",                     color:"#ff2266", note:"◆ Your Anima carries between bouts" },
    { id:"gauntlet2v2", label:"The Gauntlet — Duo", sub:"Bring a partner — you share Anima, the ladder doesn't share mercy", color:"#ff2266", note:"◆ Your Anima carries between bouts" },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+MODES.length)%MODES.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%MODES.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onSelect(MODES[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {MODES.map((m,i) => (
        <div key={m.id} onClick={() => onSelect(m.id)} onMouseEnter={() => setIdx(i)}
          style={{ padding:"14px 18px", borderRadius:12, cursor:"pointer", border:`1px solid ${idx===i ? m.color+"88" : m.color+"22"}`, background: idx===i ? `${m.color}18` : "transparent", transition:"all 0.12s" }}>
          <div style={{ fontSize:13, fontWeight:900, color:idx===i?m.color:GM.vellumFaint, fontFamily:GM.font, letterSpacing:idx===i?1:0 }}>
            {idx===i ? `◆ ${m.label} ◆` : m.label}
          </div>
          {idx===i && <div style={{ fontSize:10, color:GM.vellumFaint, marginTop:4 }}>{m.sub}</div>}
          {idx===i && m.note && <div style={{ fontSize:9, color:m.color, marginTop:3, fontStyle:"italic" }}>{m.note}</div>}
        </div>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HOLO HELPERS
// ─────────────────────────────────────────────────────────────

// Build synthetic collection merging Firebase collection with DeckBuilder holo selections
function buildHoloCollectionFromDeck(profileCollection, deck) {
  const col = { ...(profileCollection || {}) };
  const selections = deck?.holoSelections || {};
  Object.entries(selections).forEach(([cardId, tier]) => {
    if (tier) col[cardId] = { ...(col[cardId] || { count: 1 }), holoTier: tier, holoCount: 1 };
  });
  return col;
}

// Assign random holo tier to every card in a deck — mixed silver/gold/mythic
function buildPresetHoloDeck(deck) {
  const rollTier = () => {
    const r = Math.random();
    if (r < 0.10) return "holo_mythic";
    if (r < 0.35) return "holo_gold";
    return "holo_silver";
  };
  const col = {};
  const allCards = [
    ...Object.keys(deck.grimoire || {}),
    ...Object.keys(deck.luxmoire || {}),
  ];
  allCards.forEach(id => {
    col[id] = { count: 1, holoCount: 1, holoTier: rollTier() };
  });
  return col;
}

// ─────────────────────────────────────────────────────────────
// STARTER DECK CHOOSER — shown before NPC battle in Free Play
// Pick a preset starter deck; every card gets a random holo tier
// ─────────────────────────────────────────────────────────────
function StarterDeckChoose({ onChooseOwn, onChoosePreset, onBack }) {
  const [view, setView] = useState("choice"); // "choice" | "decklist"
  const [deckIdx, setDeckIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (view === "choice") {
        if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onBack(); }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); onChooseOwn(); }
        if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); setView("decklist"); }
      } else {
        if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); setView("choice"); }
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); setDeckIdx(i => (i - 1 + STARTER_DECKS.length) % STARTER_DECKS.length); }
        if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); setDeckIdx(i => (i + 1) % STARTER_DECKS.length); }
        if (e.key === "Enter") { e.preventDefault(); onChoosePreset(STARTER_DECKS[deckIdx]); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, deckIdx, onChooseOwn, onChoosePreset, onBack]);

  if (view === "choice") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 3, textAlign: "center", marginBottom: 4 }}>◆ CHOOSE YOUR ARSENAL ◆</div>
        <button onClick={onChooseOwn} style={{
          padding: "14px 16px", borderRadius: 10, cursor: "pointer",
          border: `1px solid ${GM.inkGold}66`, background: `${GM.inkGold}12`,
          color: GM.inkGold, fontFamily: GM.font, fontSize: 13, fontWeight: 900,
          textAlign: "left", transition: "all 0.15s",
        }}>
          <div>◆ Use My Deck</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, fontWeight: 400, marginTop: 4 }}>Your equipped loadout, your rules.</div>
        </button>
        <button onClick={() => setView("decklist")} style={{
          padding: "14px 16px", borderRadius: 10, cursor: "pointer",
          border: `1px solid ${GM.inkGold}66`, background: `${GM.inkGold}10`,
          color: GM.inkGold, fontFamily: GM.font, fontSize: 13, fontWeight: 900,
          textAlign: "left", transition: "all 0.15s",
        }}>
          <div>✦ Preset Holo Deck</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, fontWeight: 400, marginTop: 4 }}>Pick a starter deck — every card comes holographic.</div>
        </button>
        <div style={{ fontSize: 9, color: GM.vellumFaint, textAlign: "center", letterSpacing: 2, marginTop: 4 }}>◈ BACKSPACE = BACK</div>
      </div>
    );
  }

  // Deck list view
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 3, textAlign: "center", marginBottom: 2 }}>✦ SELECT STARTER DECK ✦</div>
      <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
        {STARTER_DECKS.map((deck, i) => (
          <button key={deck.id} onClick={() => onChoosePreset(deck)} onMouseEnter={() => setDeckIdx(i)}
            style={{
              padding: "10px 14px", borderRadius: 8, cursor: "pointer", textAlign: "left",
              border: `1px solid ${deckIdx === i ? deck.color : deck.color + "33"}`,
              background: deckIdx === i ? `${deck.color}18` : "transparent",
              transition: "all 0.12s",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* The deck box, at bullet size. `plain` because a
                  10px dot's worth of space cannot carry a stack
                  shadow, an Ilk collar and a crest — only the crest
                  survives at this scale, so only the crest is drawn. */}
              <StarterArt deck={deck} w={26} h={36} plain dim={deckIdx === i ? 1 : 0.65} style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: deckIdx === i ? deck.color : GM.vellumDim, fontFamily: GM.font }}>
                  {deck.name}
                </div>
                {deckIdx === i && (
                  <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 2, lineHeight: 1.4 }}>{deck.ilk} · {deck.description?.slice(0, 60)}...</div>
                )}
              </div>
              {deckIdx === i && <div style={{ marginLeft: "auto", fontSize: 9, color: GM.inkGold, letterSpacing: 1, flexShrink: 0 }}>✦ ALL HOLO ✦</div>}
            </div>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 9, color: GM.vellumFaint, textAlign: "center", letterSpacing: 2, marginTop: 4 }}>▲ ▼ · ENTER · BACKSPACE</div>
    </div>
  );
}

function VyrenMatchSelect({ mode, onSelect, onBack }) {
  const accent = GM.inkGold;
  // H30 — team/FFA modes matchmake with V'yen exclusively; there's no
  // 3-4p or 2v2 bot on VyrenNPCSelect's ladder for this branch to
  // launch, so it's left off rather than shown as a button that does
  // nothing for these modes.
  const OPTS = [
    { label:"Create Match",     icon:"🌐", id:"create" },
    { label:"Join Match",       icon:"◆",  id:"join"   },
    ...(NPC_ELIGIBLE_MODES.has(mode) ? [{ label:"vs NPC Adversary", icon:"⚔", id:"npc" }] : []),
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+OPTS.length)%OPTS.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%OPTS.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onSelect(OPTS[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      <div style={{ height:2, borderRadius:2, background:accent, marginBottom:4 }} />
      {OPTS.map((opt,i) => (
        <button key={opt.id} onClick={() => onSelect(opt.id)} onMouseEnter={() => setIdx(i)}
          style={{ padding:"12px 16px", borderRadius:10, cursor:"pointer", border:`1px solid ${idx===i ? accent+"88" : accent+"22"}`, background: idx===i ? `${accent}18` : "transparent", color: idx===i ? accent : GM.vellumFaint, fontSize:13, fontWeight:idx===i?900:400, fontFamily:GM.font, transition:"all 0.12s", letterSpacing: idx===i ? 1 : 0 }}>
          {idx===i ? `◆ ${opt.icon} ${opt.label} ◆` : `${opt.icon} ${opt.label}`}
        </button>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

function VyrenNPCSelect({ difficulties, onConfirm, onBack }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+difficulties.length)%difficulties.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%difficulties.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onConfirm(difficulties[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:4 }}>
      {difficulties.map((d,i) => (
        <div key={d.id} onClick={() => { setIdx(i); onConfirm(d.id); }} onMouseEnter={() => setIdx(i)}
          style={{ padding:"12px 16px", borderRadius:10, cursor:"pointer", border:`1px solid ${idx===i ? d.color : GM.pageMid}`, background: idx===i ? `${d.color}18` : "rgba(124,58,237,0.06)", transition:"all 0.12s" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{d.icon}</span>
            <div>
              <div style={{ fontSize:13, fontWeight:900, color:idx===i?d.color:GM.vellumFaint, fontFamily:GM.font, letterSpacing:idx===i?1:0 }}>
                {idx===i ? `◆ ${d.id} ◆` : d.id}
              </div>
              {idx===i && <div style={{ fontSize:10, color:GM.vellumFaint, marginTop:2 }}>{d.desc}</div>}
            </div>
          </div>
        </div>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

// H30 — THE MULTIPLAYER DECREE LIFTS. One table, one lookup, instead
// of a `handleCreate` that grows an `if (mode === ...)` per new mode
// forever (THE EXCEPTION LAW — the string is the caller's word for
// itself, this file just reads it). `teams` uses "A"/"B" for a 2v2
// between friends and "players"/"boss" for the co-op shapes so a
// glance at DB data never has to guess which kind of match it's
// looking at. Interleaved seating (caP'n: "interleaved <3") is just
// {0:"A",1:"B",2:"A",3:"B"} — joinMatch always claims the lowest open
// seat, so the 2nd/3rd/4th arrivals land on B/A/B without anyone
// picking a side.
//
// `needsAiDeck: true` marks the two modes whose 3rd seat is an actual
// opponent (a Gauntlet rung, a story villain) rather than an empty
// chair — handleCreate resolves that seat's real deck before ever
// calling createMatch. See resolveAiOpponent below.
const MODE_OPTS = {
  free:         { seatCount: 2, mode: "1v1" },
  ranked:       { seatCount: 2, mode: "1v1ranked" },
  ffa3:         { seatCount: 3, mode: "ffa3" },
  ffa4:         { seatCount: 4, mode: "ffa4" },
  "2v2free":    { seatCount: 4, mode: "2v2",         teams: { 0:"A", 1:"B", 2:"A", 3:"B" }, sharedAnimaTeams: true },
  "2v2ranked":  { seatCount: 4, mode: "2v2ranked",   teams: { 0:"A", 1:"B", 2:"A", 3:"B" }, sharedAnimaTeams: true },
  // ⭐ THE BOSS SITS AT SEAT 1 (2026-09-06). NPC.js is a 7,900-line
  // brain hardcoded to NPC_IDX = 1 and there is no second brain. With
  // the AI at seat 2 nobody ever played its Turn — the grace-ender
  // force-ended every boss Turn after the timer and the partner faced a
  // statue. Seat the boss where the brain already lives and the humans
  // at 0 and 2; joinMatch claims the first OPEN seat, which is 2. The
  // partner's chair is the one that moved; nothing else changed shape.
  gauntlet2v2:  { seatCount: 3, mode: "gauntlet2v2", teams: { 0:"players", 1:"boss", 2:"players" }, sharedAnimaTeams: true, hostControllers: { 1:"ai" }, aiSeat: 1, needsAiDeck: true },
  coop:         { seatCount: 3, mode: "story-coop",  teams: { 0:"players", 1:"boss", 2:"players" }, sharedAnimaTeams: true, hostControllers: { 1:"ai" }, aiSeat: 1, needsAiDeck: true },
};

// Only the modes with a real second HUMAN to matchmake for offer a
// "vs NPC Adversary" branch. Team/FFA modes matchmake with V'yen
// exclusively — the option never did anything for them since
// VyrenNPCSelect's difficulty ladder has never had a 3-4p or 2v2 bot,
// so hiding it beats showing a button that would launch a duel
// nobody asked for and nothing in this file builds.
const NPC_ELIGIBLE_MODES = new Set(["free", "ranked"]);

// ════════════════════════════════════════════════════════════════
// ◆ CALL A FRIEND TO THE TABLE
// ════════════════════════════════════════════════════════════════
// The missing half of "invite a friend". The roster is mounted OUTSIDE
// <App/> (index.js, a sibling of the whole game) and the match code is
// created deep inside it, so until the bridge existed there was no
// screen in Terror that could see both at once — which is precisely why
// an invite could only ever say "come to Terror" and never "sit here."
//
// Deliberately only offered on the CREATED-CODE pane: an invite without
// a seat is the thing that was already broken, and this is the one
// moment a real seat exists to point at.
function InviteFriendsPanel({ code, label }) {
  const S = useSocialBridge();
  const [sent, setSent] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr]   = useState("");

  const online = useMemo(() => {
    const rows = (S?.friends || []).filter((f) => f.status === "accepted");
    // Online first, then alphabetical. A friend who is offline can still
    // be invited — the invite waits in their tray for two minutes, which
    // is exactly what INVITE_TTL_MS is for.
    return rows.sort((a, b) => (b.online === a.online ? String(a.name).localeCompare(String(b.name)) : (b.online ? 1 : -1)));
  }, [S?.friends]);

  if (!S || !S.invite) return null;

  const send = async (f) => {
    setBusy(f.uid); setErr("");
    try {
      const res = await S.invite(f.uid, { matchCode: code, label: `Solrend ${code}`, note: label || null });
      if (res?.ok) setSent((m) => ({ ...m, [f.uid]: true }));
      else setErr(res?.error || "Could not send that invite.");
    } catch (e) { setErr("Could not send that invite."); }
    setBusy(null);
  };

  return (
    <div style={{ marginTop: 18, textAlign: "left" }}>
      <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 8, textAlign: "center" }}>
        ◆ CALL A FRIEND TO THE TABLE
      </div>
      {!online.length && (
        <div style={{ fontSize: 11.5, color: GM.vellumFaint, textAlign: "center", fontStyle: "italic", padding: "6px 0 2px" }}>
          No friends yet — trade Marks from the friends panel and they'll show up here.
        </div>
      )}
      <div style={{ maxHeight: 172, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
        {online.map((f) => (
          <div key={f.uid} style={{
            display: "flex", alignItems: "center", gap: 9, padding: "7px 10px",
            border: `1px solid ${GM.pageBorder}`, borderRadius: 8,
            background: f.online ? "rgba(124,58,237,0.10)" : "transparent",
          }}>
            <span style={{ width: 7, height: 7, borderRadius: 7, flex: "0 0 auto",
              background: f.online ? "#4ade80" : GM.vellumFaint,
              boxShadow: f.online ? "0 0 7px #4ade80" : "none" }} />
            <span style={{ flex: 1, minWidth: 0, color: GM.vellumDim, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.name}
            </span>
            <button
              onClick={() => send(f)}
              disabled={!!sent[f.uid] || busy === f.uid}
              style={{
                padding: "5px 11px", borderRadius: 6, cursor: sent[f.uid] ? "default" : "pointer",
                border: `1px solid ${sent[f.uid] ? "#4ade8066" : GM.inkGold}`,
                background: sent[f.uid] ? "rgba(74,222,128,0.10)" : "transparent",
                color: sent[f.uid] ? "#4ade80" : GM.inkGoldBright,
                fontFamily: GM.font, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.4,
              }}>
              {sent[f.uid] ? "SENT" : busy === f.uid ? "…" : "INVITE"}
            </button>
          </div>
        ))}
      </div>
      {err && <div style={{ fontSize: 11, color: SD.red, textAlign: "center", marginTop: 7 }}>{err}</div>}
    </div>
  );
}

// The mode ids V'yen's own kiosk knows how to label. An invite carries a
// match whose `mode` was written by createMatch — usually one of these, but
// a tournament pairing or a future mode may not be, and an unknown id must
// degrade to a label rather than render an empty pane.
const KIOSK_MODES = new Set(["free", "ranked", "ffa3", "ffa4", "2v2free", "2v2ranked", "coop", "gauntlet2v2", "1v1", "1v1ranked", "2v2", "story-coop"]);
// What an invite tells the friend they are being called into. A note, not
// a switch — an unknown mode simply sends no note rather than no invite.
const MODE_LABELS = {
  free: "Free Play", ranked: "Ranked", ffa3: "3-Way Free-For-All", ffa4: "4-Way Free-For-All",
  "2v2free": "2v2 Free Play", "2v2ranked": "2v2 Ranked", coop: "Story Co-op", gauntlet2v2: "The Gauntlet — Duo",
};

function VyrenKiosk({ playerName, profile, onClose, onEnterGame, onEnterNPC, onGauntlet, autoJoinCode = null, onAutoJoinConsumed }) {
  const [mode, setMode]               = useState(null);
  const [matchMode, setMatchMode]     = useState(null);
  const [pendingDiff, setPendingDiff] = useState(null);   // held while choosing deck
  const [deckChoice, setDeckChoice]   = useState(false);  // show StarterDeckChoose
  const [joinCode, setJoinCode]       = useState("");
  const [difficulty, setDiff]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [createdCode, setCreatedCode] = useState(null);
  const [lobbySeats, setLobbySeats]   = useState(null); // H30 — { filled, total } while a 3-4p lobby waits

  // H30 — a 1v1 code screen never needed this: "share the code, wait
  // for one person" doesn't need a counter. "Share the code, wait for
  // however many of your three friends haven't joined yet" does.
  // Silent by design outside a real N-seat wait: it never subscribes
  // for a classic 2-seat match, so nothing changes there.
  useEffect(() => {
    if (!createdCode) { setLobbySeats(null); return; }
    const unsub = onValue(ref(db, `matches/${createdCode}`), (snap) => {
      const m = snap.val();
      if (!m) return;
      const total = m.seatCount || 2;
      if (total <= 2) { setLobbySeats(null); return; }
      const filled = Array.from({ length: total }, (_, i) => !!m.players?.[i]).filter(Boolean).length;
      setLobbySeats({ filled, total });
    });
    return () => unsub();
  }, [createdCode]);

  const STORAGE_KEY = "terror_tcg_grimoires";
  const savedDecks = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } })();

  const getDeck = () => {
    // Both ranked and free use the same deck — your equipped loadout for your starterDeckId.
    // NEVER grab Object.values()[0] — that returns a random deck from localStorage.
    return resolveDeck(profile);
  };

  const DIFFICULTIES = [
    { id:"Beb",      icon:"🌱", color:"#a78bfa", desc:"Random chaos. Good for learning."        },
    { id:"Champion", icon:"⚔",  color:"#d4a84b", desc:"Follows the rules. A real test."         },
    { id:"Lord",     icon:"👑", color:"#dc2626", desc:"Calculates and punishes. Brace yourself." },
  ];

  const accentColor = GM.inkGold;

  // ── H30 · THE AI SEAT'S REAL DECK ────────────────────────────
  // gauntlet2v2 and coop seat a genuine opponent at seat 2 — the
  // player's current Gauntlet rung, or the story chapter's own
  // villain — never a copy of the host's deck. Reading which rung/
  // villain that is, and pulling its grimoire, belongs to the files
  // that already own that data (gauntlet.js's ladder, storyData.js's
  // DUNGEONS), not to V'yen's kiosk, so this only calls into them.
  // Returns null (never throws) if that lookup isn't wired yet for a
  // given mode — handleCreate below refuses to seat an AI with the
  // host's own deck by accident; it says so and stops instead.
  const resolveAiOpponent = async (modeId) => {
    if (modeId === "gauntlet2v2") {
      const { getGauntletOpponentDeck } = await import("./gauntlet");
      return typeof getGauntletOpponentDeck === "function" ? getGauntletOpponentDeck(profile) : null;
    }
    if (modeId === "coop") {
      const { getStoryCoopOpponentDeck } = await import("./storyData");
      return typeof getStoryCoopOpponentDeck === "function" ? getStoryCoopOpponentDeck(profile) : null;
    }
    return null;
  };

  const handleCreate = async () => {
    setLoading(true); setError("");
    try {
      const deck = getDeck();
      if (!deck) { setError("No deck found. Visit The Forge or Cheat Chest first."); setLoading(false); return; }
      const cfg = MODE_OPTS[mode] || MODE_OPTS.free;
      const { createMatch, drawAndDepleteCards } = await import("./gameManager");
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const opts = { seatCount: cfg.seatCount, mode: cfg.mode, teams: cfg.teams, sharedAnimaTeams: cfg.sharedAnimaTeams };
      if (cfg.needsAiDeck) {
        const aiDeck = await resolveAiOpponent(mode);
        if (!aiDeck) {
          setError("That opponent isn't ready yet — try again shortly.");
          setLoading(false);
          return;
        }
        const aiSeat = cfg.aiSeat ?? 1;
        opts.hostControllers = cfg.hostControllers;
        opts.aiNames = { [aiSeat]: aiDeck.name || "The Gauntlet" };
        opts.aiDecks = { [aiSeat]: {
          grimoire: aiDeck.grimoire || {}, luxmoire: aiDeck.luxmoire || {}, prophecy: aiDeck.prophecy || null,
          hand: aiDeck.hand || [], curios: aiDeck.curios || [null, null], esclings: aiDeck.esclings || null,
        } };
        // THE BRAIN'S BRIEF. App.handleEnterGame reads these off the
        // match to attach the NPC listener on the host's client and to
        // stamp the right ledger at exit (Gauntlet bout / story win).
        // Top-level match fields are not $extra-locked in rules.json.
        opts.aiMeta = {
          name: aiDeck.name || "The Gauntlet",
          difficulty: aiDeck.difficulty || "Champion",
          storyOpponentId: aiDeck.storyOpponentId || null,
          storyDungeonId:  aiDeck.storyDungeonId  || null,
        };
      }
      const code = await createMatch(playerName, depletedGrimoire, deck.luxmoire||{}, deck.prophecy||null, startingHand, deck.curios||[null,null], deck.esclings||null, opts);
      setCreatedCode(code);
    } catch(e) { setError("Something went wrong."); }
    setLoading(false);
  };

  // ⭐ THE INVITE LANDS HERE. An invite is a code plus a reason to hurry —
  // it opens the JOIN pane with the code already typed and fires the same
  // handleJoin a thumb would. Deliberately the SAME function: an invite
  // that took its own shortcut into the match would be a second join path
  // to keep in step with the first, and the first is the one with the deck
  // check, the seat-claim transaction and the resume branch in it.
  const autoJoinFired = useRef(false);
  useEffect(() => {
    if (!autoJoinCode || autoJoinFired.current) return;
    autoJoinFired.current = true;
    const c = String(autoJoinCode).slice(0, 4);
    setJoinCode(c);
    setMatchMode("join");
    // The kiosk header names the mode, so read the real one off the match
    // rather than inventing a label. `getDeck` does not branch on it (both
    // ranked and free use the equipped loadout) so this is presentation
    // only — and "free" is the honest fallback for a match we cannot read.
    (async () => {
      let m = null;
      try { m = (await get(ref(db, `matches/${c}`))).val(); } catch (e) {}
      setMode(m?.mode && KIOSK_MODES.has(m.mode) ? m.mode : "free");
    })();
    onAutoJoinConsumed && onAutoJoinConsumed();
  }, [autoJoinCode]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async () => {
    if (joinCode.length !== 4) return;
    setLoading(true); setError("");
    try {
      const deck = getDeck();
      if (!deck) { setError("No deck found."); setLoading(false); return; }
      // ⭐ joinOrResume, NOT joinMatch (2026-09-05). Typing the code of a
      // duel you are ALREADY SEATED IN used to be answered with "Match
      // already in progress." — the correct reply to a stranger and
      // exactly the wrong one to the player whose chair it is. The door
      // now asks whose seat it is before it decides which answer to give.
      const { joinOrResume, drawAndDepleteCards } = await import("./gameManager");
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const joined = await joinOrResume(joinCode, playerName, depletedGrimoire, deck.luxmoire||{}, deck.prophecy||null, startingHand, deck.curios||[null,null], deck.esclings||null);
      // joinMatch returns { error } on failure or { success, seatIdx } on
      // success — ALWAYS a truthy object, so `if (!joined)` never caught
      // anything and a full/nonexistent code dropped the joiner onto a
      // dead board. Inspect the shape instead.
      //
      // H30 — SEAT 1 WAS A LIE THE MOMENT A THIRD CHAIR EXISTED.
      // joinMatch claims whatever seat is actually open (any of them in
      // a 3-4p lobby, not always seat 1), so the joiner has to enter
      // Shatterfield AS the seat it actually landed in, not a hardcoded
      // guess that happened to be right only while every match had
      // exactly two chairs.
      if (joined?.error) { setError(joined.error); setLoading(false); return; }
      const seatIdx = Number.isInteger(joined?.seatIdx) ? joined.seatIdx : 1;
      onEnterGame(joinCode, seatIdx, playerName);
      onClose();
    } catch(e) { setError("Something went wrong."); }
    setLoading(false);
  };

  const handleNPC = (diff) => {
    const d = diff || difficulty;
    if (!d) return;
    setPendingDiff(d);
    setDeckChoice(true); // show deck choice before launching
  };

  // ⭐ THE SETS' ESCLINGS TRAVEL. Every launch object in this file was
  // built without an `esclings` key, so App.handleEnterNPC forwarded
  // `deckData.esclings || null`, createMatch padded three nulls, and
  // Shatterfield's buildInitialEsclings filled all three rifts with ONE
  // auto-derived Escling off the deck's first Escendant.
  //
  // That is the EXACT bug PRIME §2.7 records THE SETS as having fixed —
  // "every deck in the game opened on three identical Esclings" — and
  // it was silently back, in Free Play, in Ranked, in every Endless
  // Depths bout, every Gauntlet bout and the Inner Emperium. The World
  // Map, the Dungeon and both PvP paths pass it correctly, so a player
  // who chose a Set saw it honoured in Story Mode and quietly discarded
  // everywhere else — which reads as the Set not doing anything rather
  // than as a bug.
  const launchNPC = (deck, collection, extraMeta = {}) => {
    onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null], esclings:deck.esclings||null }, deck.prophecy||null, null, pendingDiff, { collection, ...extraMeta });
    onClose();
  };

  const handleChooseOwn = () => {
    const deck = getDeck();
    if (!deck) { setError("No deck found. Visit The Forge or Cheat Chest first."); setDeckChoice(false); return; }
    launchNPC(deck, buildHoloCollectionFromDeck(profile?.collection, deck));
  };

  const handleChoosePreset = (starterDeck) => {
    // starterDeckId rides the meta so a WIN with this preset stamps
    // starterWins/{deckId} on the profile (terror:all_starters tracker).
    // Only PRESET starter picks carry it — a forged personal deck is not
    // "winning with the starter deck," even if it grew from one.
    launchNPC(starterDeck, buildPresetHoloDeck(starterDeck), { starterDeckId: starterDeck.id });
  };

  const handleMatchSelect = (id) => {
    if (id === "create") { setMatchMode("create"); handleCreate(); }
    else setMatchMode(id);
  };

  // ── EASTER EGG: AENERGY ARTS LORE ──────────────────────────
  // V'yen is named after the Vyren — original Serpentine
  // Aenergy practitioners. Their opposites: Aeternavis (Aegle
  // Phoenix), masters of O'mnia and Solaea. Rare drops on the
  // opening line, gated by win count. Lore reward for the deep.
  // ──────────────────────────────────────────────────────────
  const vyrenLine = () => {
    const wins = profile?.wins || 0;
    const eggRoll = Math.random();

    if (!mode) {
      if (wins >= 100 && eggRoll < 0.08) {
        return '"My mother\'s mother coiled with the first Vyren. Do not mistake the name for the bloodline, Aenarchist."';
      }
      if (wins >= 50 && eggRoll < 0.12) {
        return '"You think the name is mine? The Vyren wove Aenergy before your kind had words for it."';
      }
      if (wins >= 25 && eggRoll < 0.10) {
        return '"An Aeternavis would not last three turns at this table. Soft feathers. Softer wills."';
      }
      return '"Looking for a fight, Aenarchist?"';
    }

    if (mode==="free" && !matchMode) return '"Cheat Chest rules — anything goes."';
    if (mode==="ranked" && !matchMode) return '"Ranked battle. Your real deck. No excuses."';
    if (mode==="ffa3" && !matchMode) return '"Three chairs, one board. Trust no one, Aenarchist."';
    if (mode==="ffa4" && !matchMode) return '"Four chairs. Watch every hand, not just the one across from you."';
    if (mode==="2v2free" && !matchMode) return '"Bring a partner. Your Anima becomes theirs the moment you sit."';
    if (mode==="2v2ranked" && !matchMode) return '"Ranked, and shared. Fall together or not at all."';
    if (mode==="coop" && !matchMode) return '"Two against the story. It will not go easier on you for the company."';
    if (mode==="gauntlet2v2" && !matchMode) return '"The Gauntlet does not care that there are two of you."';
    if (matchMode==="create" && createdCode) return '"Share the code. Your opponent is waiting."';
    if (matchMode==="join") return '"Enter the code your opponent gave you."';
    if (matchMode==="npc" && !deckChoice) return '"Choose your adversary."';
    if (deckChoice) return '"Which arsenal will you bring?"';
    return '"Ready when you are."';
  };

  // ⚠ MEMOISED, and the twin in the Emporium is memoised for the same
  // reason. vyrenLine() ROLLS for its easter eggs, so calling it raw in
  // the JSX re-rolls her opening line on every parent re-render — and
  // now that the window is a STAGE rather than a small panel, her line
  // is the most prominent text on screen. A sentence that changes while
  // you are reading it does not read as flavour, it reads as a bug.
  // The deps are every branch vyrenLine() actually looks at.
  const spokenLine = useMemo(
    () => vyrenLine(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, matchMode, createdCode, deckChoice, profile?.wins]
  );

  const Btn = ({ label, color, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled} style={{ width:"100%", padding:"14px",
      background:`linear-gradient(135deg,${GM.pageMid},${GM.pageMid})`,
      border:`1px solid ${color||accentColor}`, borderRadius:10, color:color||accentColor,
      fontFamily:GM.font, fontSize:13, fontWeight:900, fontStyle:"italic",
      cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.35:1, transition:"all 0.2s" }}>{label}</button>
  );

  return (
    // ⭐ THE DIALOGUE WINDOW (caP'n, roadmap): "V'yen's Sweet Treats
    // as a dialogue window. HER ON SCREEN WITH YOU, not a bulletin
    // board. J'ynxi always in the background."
    //
    // What changed is the SHELL and nothing else. Every branch below
    // — mode select, match select, the code pad, the adversary
    // picker, the deck picker — is byte-for-byte what it was inside
    // the old `a_npc_box` panel. That is deliberate: a treatment pass
    // that also rewrites the flow underneath it can never be told
    // apart from a regression.
    //
    // The kiosk kicker is gone with the box. She is not a KIOSK, she
    // is a woman with a stall, and the badge now says what the stall
    // actually is.
    <NpcStage
      keeperId="vyen"
      badge="Sweet Treats"
      line={spokenLine}
      profile={profile}
      lurker="jynxi"
      backdrop="plaza_1.webp"
      onClose={onClose}
    >
        {/* T16 — your ladder standing, shown the moment stakes appear */}
        {mode === "ranked" && (profile?.pvp?.score || 0) > 0 && (
          <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"5px 14px", borderRadius:20, background:"rgba(212,168,75,0.10)", border:`1px solid ${GM.inkGold}44` }}>
              <span style={{ fontSize:10 }}>⚔</span>
              <span style={{ fontSize:12, fontWeight:900, color:GM.inkGoldBright, fontFamily:GM.font }}>{profile.pvp.score}</span>
              <span style={{ fontSize:9, color:GM.vellumFaint, letterSpacing:1 }}>{profile.pvp?.wins || 0}W · {profile.pvp?.losses || 0}L</span>
            </div>
          </div>
        )}
        {/* The gauntlet is intercepted here rather than handled as a
            kiosk `mode`, and that is the whole integration. Free Play
            and Ranked are matchmaking — they belong inside this modal
            and flow on into deck and adversary pickers. The Gauntlet is a
            full screen that Plaza already knows how to mount. Letting
            `mode` become "gauntlet" would put the kiosk into a state it
            has no branch for and render an empty panel. */}
        {!mode && <VyrenModeSelect onClose={onClose}
          onSelect={(id) => { if (id === "gauntlet") { onGauntlet && onGauntlet(); return; } setMode(id); }} />}
        {mode && !matchMode && !createdCode && <VyrenMatchSelect mode={mode} onSelect={handleMatchSelect} onBack={() => setMode(null)} />}
        {matchMode==="create" && (
          <div style={{ textAlign:"center" }}>
            {loading && <div style={{ color:GM.vellumFaint, fontSize:13, marginBottom:16 }}>◆ Creating match...</div>}
            {createdCode && (
              <>
                <div style={{ fontSize:11, color:GM.vellumFaint, marginBottom:8, letterSpacing:2 }}>SHARE THIS CODE</div>
                <div style={{ fontSize:52, fontWeight:900, color:GM.inkGoldBright, letterSpacing:16, fontFamily:GM.font, textShadow:`0 0 24px ${GM.inkGold}`, marginBottom:20 }}>{createdCode}</div>
                {lobbySeats && (
                  <div style={{ fontSize:12, color: lobbySeats.filled >= lobbySeats.total ? GM.inkGoldBright : GM.vellumFaint, marginBottom:16, letterSpacing:1 }}>
                    {lobbySeats.filled} / {lobbySeats.total} seated
                    {lobbySeats.filled < lobbySeats.total ? ` — waiting on ${lobbySeats.total - lobbySeats.filled} more` : " — everyone's here"}
                  </div>
                )}
                <Btn label="◆ Enter Shatterfield" color={GM.inkGoldBright} onClick={() => { onEnterGame(createdCode, 0, playerName); onClose(); }} />
                <InviteFriendsPanel code={createdCode} label={MODE_LABELS[mode] || null} />
              </>
            )}
          </div>
        )}
        {matchMode==="join" && (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <input placeholder="CODE" value={joinCode} onChange={e => { setJoinCode(e.target.value.toUpperCase()); setError(""); }} maxLength={4} autoFocus
              style={{ width:"100%", padding:"13px 16px", background:"rgba(124,58,237,0.08)", border:`1px solid ${GM.pageBorder}`, borderRadius:10, color:GM.vellumDim, fontFamily:GM.font, fontSize:22, outline:"none", boxSizing:"border-box", textAlign:"center", letterSpacing:10, fontWeight:900 }} />
            <Btn label="◆ Join Match" color={GM.inkGoldBright} onClick={handleJoin} disabled={joinCode.length!==4||loading} />
          </div>
        )}
        {matchMode==="npc" && !deckChoice && <VyrenNPCSelect difficulties={DIFFICULTIES} onConfirm={handleNPC} onBack={() => setMatchMode(null)} />}
        {deckChoice && (
          <StarterDeckChoose
            onChooseOwn={handleChooseOwn}
            onChoosePreset={handleChoosePreset}
            onBack={() => setDeckChoice(false)}
          />
        )}
        {error && <div style={{ fontSize:12, color:SD.red, textAlign:"center", marginTop:10, padding:"8px", background:`${SD.red}10`, borderRadius:8 }}>{error}</div>}
        <button onClick={() => {
          if (deckChoice) { setDeckChoice(false); }
          else if (matchMode && matchMode !== "create") { setMatchMode(null); setError(""); }
          else if (mode && !matchMode) { setMode(null); setError(""); }
          else onClose();
        }} style={{ width:"100%", marginTop:12, padding:"8px", borderRadius:8, border:`1px solid ${GM.pageBorder}`, background:"transparent", color:GM.vellumFaint, fontSize:12, cursor:"pointer" }}>
          {(matchMode && matchMode !== "create") || (mode && !matchMode) ? "← Back" : "← Leave"}
        </button>
        <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>BACKSPACE = BACK</div>
    </NpcStage>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTAL CINEMATIC
// ─────────────────────────────────────────────────────────────
function PortalCinematic({ portal, dungeon, onComplete }) {
  const [phase, setPhase]   = useState("blackout");
  const [wordIdx, setWordIdx] = useState(0);
  const color    = portal?.color || "#6d28d9";
  const flavor   = dungeon?.flavor || "◆ The gate opens. ◆";
  const words    = flavor.split(" ");

  useEffect(() => {
    if (phase === "dialogue") return;
    const timings = { blackout: 500, bleed: 900, name: 1000, title: 700 };
    const t = setTimeout(() => {
      const next = { blackout:"bleed", bleed:"name", name:"title", title:"dialogue" };
      setPhase(p => next[p] || p);
    }, timings[phase] || 600);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "dialogue") return;
    if (wordIdx >= words.length) return;
    const t = setTimeout(() => setWordIdx(i => i + 1), 110);
    return () => clearTimeout(t);
  }, [phase, wordIdx, words.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === "q" || e.key === "Q" || e.key === " ") {
        e.preventDefault();
        if (wordIdx >= words.length) onComplete();
        else setWordIdx(words.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [wordIdx, words.length, onComplete]);

  const showName     = ["name","title","dialogue"].includes(phase);
  const showTitle    = ["title","dialogue"].includes(phase);
  const showDialogue = phase === "dialogue";
  const done         = wordIdx >= words.length;

  return (
    <div onClick={() => { if (done) onComplete(); else setWordIdx(words.length); }}
      style={{ position:"fixed", inset:0, zIndex:400, background:"#000", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, pointerEvents:"none", background:`radial-gradient(ellipse at center, ${color}22 0%, transparent 70%)`, opacity: phase === "blackout" ? 0 : 1, transition:"opacity 1.4s ease" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:64, background:"#000", zIndex:1 }} />
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:64, background:"#000", zIndex:1 }} />
      <div style={{ fontSize:11, color, letterSpacing:6, fontFamily:GM.font, textShadow:`0 0 16px ${color}`, zIndex:2, marginBottom:12, opacity: showName ? 1 : 0, transition:"opacity 0.8s ease" }}>{portal?.subtitle?.toUpperCase()}</div>
      <div style={{ fontSize:30, fontWeight:900, color:"#fff", fontFamily:GM.font, letterSpacing:4, textShadow:`0 0 30px ${color}, 0 0 60px ${color}55`, zIndex:2, marginBottom:10, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(24px)", transition:"all 0.9s ease" }}>{dungeon?.name?.toUpperCase()}</div>
      <div style={{ fontSize:9, color:"#666", letterSpacing:4, fontFamily:GM.font, zIndex:2, marginBottom:44, opacity: showTitle ? 1 : 0, transition:"opacity 0.7s ease 0.3s" }}>{dungeon?.subtitle?.toUpperCase()}</div>
      <div style={{ maxWidth:380, textAlign:"center", zIndex:2, minHeight:50, padding:"0 32px", opacity: showDialogue ? 1 : 0, transition:"opacity 0.4s ease" }}>
        <div style={{ fontSize:13, color:"#aaa", fontStyle:"italic", lineHeight:2, textShadow:"0 1px 4px rgba(0,0,0,1)" }}>
          {words.slice(0, wordIdx).join(" ")}
          {!done && <span style={{ opacity:0.4 }}>▌</span>}
        </div>
      </div>
      {done && (
        <div style={{ marginTop:44, zIndex:2, animation:"fightPulse 1.2s ease-in-out infinite alternate" }}>
          <div style={{ fontSize:13, fontWeight:900, color, letterSpacing:4, fontFamily:GM.font, textShadow:`0 0 20px ${color}, 0 0 40px ${color}88`, border:`1px solid ${color}`, padding:"12px 40px", borderRadius:4, background:`${color}11` }}>◆ ENTER ◆</div>
        </div>
      )}
      <div style={{ position:"absolute", bottom:72, zIndex:2, fontSize:9, color:"#333", letterSpacing:3, fontFamily:GM.font }}>{done ? "CLICK OR PRESS ENTER" : "PRESS ENTER TO SKIP"}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BOSS CINEMATIC
// ─────────────────────────────────────────────────────────────
function BossCinematic({ villain, onComplete }) {
  const [phase, setPhase]     = useState("blackout");
  const [wordIdx, setWordIdx] = useState(0);
  const dialogue = villain?.dialogue?.approach?.[0] || "";
  const words    = dialogue.split(" ");
  const color    = villain?.accentColor || "#ff2266";

  useEffect(() => {
    const timings = { blackout:600, bleed:900, name:1000, title:800, dialogue:0 };
    if (phase === "dialogue") return;
    const t = setTimeout(() => {
      const next = { blackout:"bleed", bleed:"name", name:"title", title:"dialogue" };
      setPhase(p => next[p] || p);
    }, timings[phase] || 600);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "dialogue") return;
    if (wordIdx >= words.length) return;
    const t = setTimeout(() => setWordIdx(i => i + 1), 120);
    return () => clearTimeout(t);
  }, [phase, wordIdx, words.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === "q" || e.key === "Q" || e.key === " ") {
        e.preventDefault();
        if (wordIdx >= words.length || phase === "fight") onComplete();
        else setWordIdx(words.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, wordIdx, words.length, onComplete]);

  const showName     = ["name","title","dialogue"].includes(phase);
  const showTitle    = ["title","dialogue"].includes(phase);
  const showDialogue = phase === "dialogue";
  const dialogueDone = wordIdx >= words.length;

  return (
    <div onClick={() => { if (dialogueDone) onComplete(); else setWordIdx(words.length); }}
      style={{ position:"fixed", inset:0, zIndex:400, background:"#000", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, background:`radial-gradient(ellipse at center, ${color}18 0%, transparent 70%)`, opacity: phase === "blackout" ? 0 : 1, transition:"opacity 1.2s ease", pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:60, background:"#000", zIndex:1 }} />
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:60, background:"#000", zIndex:1 }} />
      <div style={{ fontSize:48, marginBottom:20, zIndex:2, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(20px)", transition:"all 0.8s ease", textShadow:`0 0 40px ${color}` }}>{villain?.avatarIcon}</div>
      <div style={{ fontSize:32, fontWeight:900, color:"#fff", fontFamily:GM.font, letterSpacing:6, textShadow:`0 0 30px ${color}, 0 0 60px ${color}66`, zIndex:2, marginBottom:8, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(30px)", transition:"all 0.9s ease" }}>{villain?.name?.toUpperCase()}</div>
      <div style={{ fontSize:10, color, letterSpacing:5, fontFamily:GM.font, textShadow:`0 0 12px ${color}`, zIndex:2, marginBottom:40, opacity: showTitle ? 1 : 0, transition:"opacity 0.8s ease 0.2s" }}>{villain?.title?.toUpperCase()}</div>
      <div style={{ maxWidth:400, textAlign:"center", zIndex:2, minHeight:60, opacity: showDialogue ? 1 : 0, transition:"opacity 0.4s ease", padding:"0 32px" }}>
        <div style={{ fontSize:13, color:"#ccc", fontStyle:"italic", lineHeight:2, textShadow:"0 1px 4px rgba(0,0,0,1)" }}>
          {words.slice(0, wordIdx).join(" ")}
          {wordIdx < words.length && <span style={{ opacity:0.4 }}>▌</span>}
        </div>
      </div>
      {dialogueDone && (
        <div style={{ marginTop:40, zIndex:2, animation:"fightPulse 1.2s ease-in-out infinite alternate" }}>
          <div style={{ fontSize:14, fontWeight:900, color, letterSpacing:4, fontFamily:GM.font, textShadow:`0 0 20px ${color}, 0 0 40px ${color}88`, border:`1px solid ${color}`, padding:"12px 40px", borderRadius:4, background:`${color}11` }}>◆ FIGHT ◆</div>
        </div>
      )}
      <div style={{ position:"absolute", bottom:72, zIndex:2, fontSize:9, color:"#444", letterSpacing:3, fontFamily:GM.font }}>{dialogueDone ? "CLICK OR PRESS ENTER" : "PRESS ENTER TO SKIP"}</div>
      <style>{`@keyframes fightPulse { from { opacity:0.7; transform:scale(1); } to { opacity:1; transform:scale(1.04); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// INNER EMPERIUM SELECT
// ─────────────────────────────────────────────────────────────
function InnerMineriumSelect({ playerName, profile, onClose, onEnterNPC, onDepths }) {
  const [cinematic, setCinematic]     = useState(false);
  const [pendingVillain, setPendingVillain] = useState(null);
  const innerWins = profile?.innerWins || 0;
  const accentColor = "#ff2266";

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCinematicComplete = () => {
    setCinematic(false);
    if (pendingVillain) onEnterNPC(pendingVillain.difficulty, pendingVillain);
  };

  if (cinematic && pendingVillain) return <BossCinematic villain={pendingVillain} onComplete={handleCinematicComplete} />;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(5,3,0,0.94)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:GM.font }}>
      <div style={{ width:"min(92vw,440px)", maxHeight:"90vh", overflowY:"auto", background:`linear-gradient(145deg,#1a000a,#0d0005)`, border:`1px solid ${accentColor}44`, borderRadius:20, padding:"28px 24px", boxShadow:`0 0 60px ${accentColor}22` }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:9, color:accentColor, letterSpacing:4, marginBottom:6, textShadow:`0 0 12px ${accentColor}` }}>◆ THE INNER EMPERIUM ◆</div>
          <div style={{ fontSize:20, fontWeight:900, color:GM.vellum, fontFamily:GM.font, textShadow:"0 1px 6px rgba(0,0,0,1)" }}>Endless Depths</div>
          <div style={{ fontSize:10, color:accentColor, marginTop:6 }}>{innerWins} {innerWins===1?"victory":"victories"} recorded within</div>
        </div>
        <button onClick={onDepths} style={{ width:"100%", padding:"14px", borderRadius:10, cursor:"pointer", background:`${accentColor}15`, border:`1px solid ${accentColor}`, color:accentColor, fontFamily:GM.font, fontSize:14, fontWeight:900, marginBottom:16, textShadow:`0 0 8px ${accentColor}` }}>
          ◆ Enter the Gauntlet — ELO Ranked
        </button>

        {/* ═══════════════════════════════════════════════════
            ◆ THE LADDER — restored 2026-08-20
            ═══════════════════════════════════════════════════
            This picker was WRITTEN, MOUNTED AND UNREACHABLE. The
            component still held `pendingVillain`, `BossCinematic` and
            the `onEnterNPC(pendingVillain.difficulty, pendingVillain)`
            hand-off — and nothing in the file ever called
            setPendingVillain, so the whole cast of eight was dead data
            behind a single Gauntlet button.

            It gates on `innerWins`, which until today was written by
            nothing and therefore permanently 0 — so even if the list
            had rendered, every rung above the first would have been
            locked forever. Both ends are live now; see
            progression.recordDepthResult.

            LADDER_VILLAINS and not INNER_VILLAINS: the three H'osh
            Vrown rows belong to Story Mode's door, interception and
            final, and a grindable ladder must not sell the ending in a
            side menu. See the banner in storyData. */}
        <div style={{ fontSize:9, color:GM.vellumFaint, letterSpacing:3, textAlign:"center", margin:"4px 0 10px" }}>
          ◆ THOSE WHO NEVER LEFT ◆
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
          {LADDER_VILLAINS.map((v) => {
            const need = v.innerWinsRequired || 0;
            const open = innerWins >= need;
            // `locked: true` on a row is the AUTHOR saying "not yet",
            // independent of the count — the_deep carries it and has
            // no lore written. It stays a silhouette either way.
            const hidden = !!v.locked;
            return (
              <button key={v.id} disabled={!open || hidden}
                onClick={() => { if (open && !hidden) { setPendingVillain(v); setCinematic(true); } }}
                style={{
                  width:"100%", padding:"11px 13px", borderRadius:8, textAlign:"left",
                  display:"flex", alignItems:"center", gap:11,
                  cursor:(open && !hidden) ? "pointer" : "default",
                  background: open ? `${v.accentColor}12` : "rgba(255,255,255,.03)",
                  border:`1px solid ${open ? v.accentColor + "88" : GM.inkBrown + "55"}`,
                  color: open ? GM.vellum : GM.vellumFaint,
                  fontFamily:GM.font, opacity: open ? 1 : 0.55,
                }}>
                <span style={{ fontSize:17, color: open ? v.accentColor : GM.vellumFaint }}>
                  {open ? (v.avatarIcon || "◆") : "◇"}
                </span>
                <span style={{ minWidth:0, flex:1 }}>
                  <span style={{ display:"block", fontSize:12, fontWeight:900 }}>
                    {open ? v.name : "———"}
                  </span>
                  <span style={{ display:"block", fontSize:9, letterSpacing:1.5, opacity:.8, marginTop:2 }}>
                    {open
                      ? `${v.title || ""}${v.title ? " · " : ""}${v.difficulty}`
                      : `${need} ${need === 1 ? "victory" : "victories"} within`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginBottom:14 }}>BACKSPACE = LEAVE</div>
        <button onClick={onClose} style={{ width:"100%", padding:"10px", borderRadius:2, border:`1px solid ${GM.inkBrown}66`, background:"transparent", color:GM.vellumFaint, fontSize:12, cursor:"pointer", fontFamily:GM.font }}>← Turn Back</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DECK LOOKUP — safe on mobile (localStorage can be sandboxed)
// Falls back through: loadout → starterDeck in storage → any saved → starterDeck definition
// getStarterDeck is imported at top of file from starterDecks.js
// ─────────────────────────────────────────────────────────────
// Exported (H30) so TournamentBoard.js's auto-join effect can resolve
// the SAME deck V'yen's kiosk would have handed a human match — a
// bracket pairing isn't a different deck-resolution question, it's
// the same one asked from a different screen.
export function resolveDeck(profile) {
  // 1. Use Firebase loadout if set (highest priority — user explicitly saved this)
  if (profile?.loadout?.grimoire) {
    const deck = profile.loadout;
    const starterDeck = profile?.starterDeckId ? getStarterDeck(profile.starterDeckId) : null;
    return {
      ...deck,
      curios:   (deck.curios?.some(c => c !== null)) ? deck.curios   : (starterDeck?.curios   || [null, null]),
      prophecy: deck.prophecy || starterDeck?.prophecy || null,
    };
  }
  // 2. Try localStorage with exact starterDeckId key match
  if (profile?.starterDeckId) {
    const STORAGE_KEY = "terror_tcg_grimoires";
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (saved[profile.starterDeckId]) {
        const deck = saved[profile.starterDeckId];
        const starterDeck = getStarterDeck(profile.starterDeckId);
        return {
          ...deck,
          curios:   (deck.curios?.some(c => c !== null)) ? deck.curios   : (starterDeck?.curios   || [null, null]),
          prophecy: deck.prophecy || starterDeck?.prophecy || null,
        };
      }
    } catch (e) {}
    // 3. Always fall back to the correct starter deck definition — never a random saved deck
    return getStarterDeck(profile.starterDeckId) || null;
  }
  return null;
}

// Build synthetic collection merging Firebase collection with DeckBuilder holo selections

// ─────────────────────────────────────────────────────────────
// MAIN PLAZA COMPONENT
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// PLAZA HUB — The Sign Board
// Visual novel style — same system as dungeon interactables
// Houses: The Forge + The Codex + future sections
// ─────────────────────────────────────────────────────────────

function ForgeSection({ profile, onClose }) {
  // Story Variants, Grimoire Pages, Companions earned from dungeons
  const storyVariants  = profile?.storyVariants  || [];
  const grimPages      = profile?.grimoirePages   || [];
  const companions     = profile?.companions      || [];
  const easterEggs     = profile?.easterEggVariants || [];

  const [tab, setTab] = useState("variants"); // variants | pages | companions

  const tabStyle = (id) => ({
    padding: "8px 16px", borderRadius: 6, cursor: "pointer",
    fontFamily: GM.font, fontSize: 10, letterSpacing: 2,
    background: tab === id ? `${GM.inkGold}22` : "transparent",
    border: `1px solid ${tab === id ? GM.inkGold : GM.pageBorder}`,
    color: tab === id ? GM.inkGold : GM.vellumFaint,
    transition: "all 0.2s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 16 }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 8 }}>
        <button style={tabStyle("variants")} onClick={() => setTab("variants")}>
          ◈ Story Variants ({storyVariants.length + easterEggs.length})
        </button>
        <button style={tabStyle("pages")} onClick={() => setTab("pages")}>
          {/* ◇ THE DENOMINATOR IS REAL NOW. It was the literal string
              "???" because nothing wrote a page and there was nothing
              to count. Six Lord's Chambers, six pages, derived — see
              GRIMOIRE_PAGE_TOTAL in worldData. */}
          ◇ Grimoire Pages ({grimPages.length}/{GRIMOIRE_PAGE_TOTAL})
        </button>
        <button style={tabStyle("companions")} onClick={() => setTab("companions")}>
          ✦ Companions ({companions.length})
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {tab === "variants" && (
          <div>
            {storyVariants.length === 0 && easterEggs.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic",
                lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◈</div>
                No Story Variants yet.<br />
                Defeat dungeon bosses to earn variants<br />
                that remember where they came from.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {/* ⚠ THE FLAG WAS READ OFF THE ITEM AND THE ITEM DID NOT
                    CARRY IT. This merged both lists and then asked each
                    row for `v.isEasterEgg`, while the Codex's twin
                    tagged by WHICH ARRAY the row came from — so the two
                    screens would have disagreed the moment anything was
                    written. The array is the truth; it always was. */}
                {[...storyVariants.map(v => ({ ...v, isEasterEgg: false })),
                  ...easterEggs.map(v => ({ ...v, isEasterEgg: true }))].map((v, i) => (
                  <div key={i} style={{
                    width: 80, padding: 8, borderRadius: 8,
                    background: `${GM.pageMid}44`,
                    border: `1px solid ${v.isEasterEgg ? GM.inkGoldBright : GM.inkGold}44`,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                    boxShadow: v.isEasterEgg ? `0 0 12px ${GM.inkGold}44` : "none",
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 6,
                      background: `${GM.pageWarm}`,
                      border: `1px solid ${v.isEasterEgg ? GM.inkGoldBright : GM.inkGold}33`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>
                      {v.isEasterEgg ? "◈" : "◆"}
                    </div>
                    <div style={{
                      fontSize: 8, color: v.isEasterEgg ? GM.inkGold : GM.inkGold,
                      textAlign: "center", fontFamily: GM.font,
                      letterSpacing: 0.5, lineHeight: 1.4,
                    }}>
                      {v.name || v.cardId}
                    </div>
                    {v.isEasterEgg && (
                      <div style={{ fontSize: 7, color: GM.inkGold, opacity: 0.7 }}>◈ EGG</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "pages" && (
          <div>
            {grimPages.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◇</div>
                No Grimoire Pages collected.<br />
                They hide in places you weren't supposed to go.<br />
                <span style={{ fontSize: 10, opacity: 0.5 }}>??? pages total. Or maybe more.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {grimPages.map((page, i) => (
                  <div key={i} style={{
                    width: 100, padding: 12, borderRadius: 8,
                    background: `${GM.inkGold}11`,
                    border: `1px solid ${GM.inkGold}33`,
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{
                      fontSize: 10, color: GM.inkGold,
                      fontFamily: GM.font, letterSpacing: 1,
                    }}>
                      Page {page.pageId}
                    </div>
                    <div style={{
                      fontSize: 8, color: GM.vellumFaint,
                      fontStyle: "italic", lineHeight: 1.6,
                    }}>
                      {page.fragment || "The text fades when you try to read it."}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "companions" && (
          <div>
            {companions.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>✦</div>
                No companions yet.<br />
                Clear dungeons to unlock companions<br />
                that follow you through the Plaza.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {companions.map((c, i) => (
                  <div key={i} style={{
                    width: 80, padding: 8, borderRadius: 8,
                    background: `${GM.inkGoldBright}11`,
                    border: `1px solid ${GM.inkGoldBright}33`,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: "50%",
                      background: GM.pageWarm,
                      border: `1px solid ${GM.inkGoldBright}44`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>✦</div>
                    <div style={{
                      fontSize: 8, color: GM.inkGoldBright,
                      textAlign: "center", fontFamily: GM.font,
                    }}>
                      {c.name || c.id}
                    </div>
                    {c.active && (
                      <div style={{
                        fontSize: 7, color: GM.inkGoldBright,
                        background: `${GM.inkGoldBright}22`,
                        border: `1px solid ${GM.inkGoldBright}44`,
                        padding: "2px 6px", borderRadius: 4,
                      }}>ACTIVE</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PLAZA HUB MODAL ──────────────────────────────────────────
// Visual novel style — p_forge.webp fills top panel
// Willie the Forger lives here (Willy in disguise, new apron)
// Four buttons positioned over scene objects:
//   Cauldron (far left) → The Cauldron — card/item creation
//   Fireplace (center)  → The Forge    — earned story items
//   Grimoire (right)    → The Codex    — lore & cards
//   Crystal (far right) → The Crystal  — re-roll/fate (TBA)
function PlazaHub({ profile, section: initialSection, onClose, onOpenCodex, onForge, logLine }) {
  const [phase, setPhase]         = useState(initialSection ? "section" : "sign");
  const [activeSection, setActiveSection] = useState(initialSection || null);
  const [typed, setTyped]         = useState("");
  const [typing, setTyping]       = useState(true);
  const [lineIdx, setLineIdx]     = useState(0);
  const [willieClicked, setWillieClicked] = useState(false);

  // Willie the Forger dialogue — he is DEFINITELY not Willy
  const WILLIE_LINES = [
    "◆ Ah, a visitor! Welcome to my establishment.",
    "I am Willie. Willie the Forger. Not to be confused with anyone else.",
    "The apron is new. The name is new. Everything is new.",
    "...The broom is also new. Very different broom.",
  ];

  // Scene description lines — shown before Willie speaks
  const SCENE_LINES = [
    "The air smells of coal and something older.",
    "A hunched figure tends the fireplace without looking up.",
    "He is wearing an apron. It says 'WILLIE' on it in fresh ink.",
  ];

  const LINES = willieClicked ? WILLIE_LINES : SCENE_LINES;

  // Session record — scene + Willie-the-Forger lines land in the plaza log
  const logLineRef = useRef(logLine);
  useEffect(() => { logLineRef.current = logLine; }, [logLine]);
  useEffect(() => {
    if (phase !== "sign") return;
    const l = LINES[lineIdx];
    if (l && logLineRef.current) {
      logLineRef.current({
        label: willieClicked ? "Willie the Forger" : "The Forge",
        color: willieClicked ? "#c9a84c" : GM.inkGold,
        text: l,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx, willieClicked, phase]);

  useEffect(() => {
    if (phase !== "sign") return;
    setTyped(""); setTyping(true);
    let i = 0;
    const line = LINES[lineIdx] || "";
    const t = setInterval(() => {
      i++;
      setTyped(line.slice(0, i));
      if (i >= line.length) { clearInterval(t); setTyping(false); }
    }, 22);
    return () => clearInterval(t);
  }, [lineIdx, phase, willieClicked]);

  const advance = () => {
    if (typing) { setTyped(LINES[lineIdx]); setTyping(false); return; }
    if (lineIdx < LINES.length - 1) setLineIdx(i => i + 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      if ((e.key === "Enter" || e.key === " ") && phase === "sign") { e.preventDefault(); advance(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase, lineIdx, typing]);

  // Four scene objects — positioned as % of image width/height
  // Adjust these values to match where objects sit in p_forge.webp
  const SECTIONS = [
    {
      id: "cauldron",
      label: "The Cauldron",
      symbol: "🫧",
      color: "#22d3ee",      // cyan — brew/create
      pos: { left: "17%", top: "55%" }, // far left — over the cauldron
      comingSoon: true,
      desc: "Brew something new. Card and item creation. Coming soon.",
    },
    {
      id: "forge",
      label: "The Forge",
      symbol: "◈",
      color: GM.inkGoldBright,
      pos: { left: "42%", top: "55%" }, // center — over the fireplace
      comingSoon: false,
      desc: "Your story. Variants earned, pages found, companions unlocked.",
    },
    {
      id: "codex",
      label: "The Codex",
      symbol: "◆",
      color: GM.inkGold,
      pos: { left: "68%", top: "55%" }, // right — over the grimoire
      comingSoon: false,
      desc: "Lore, cards, records. Everything the Minerium left behind.",
    },
    {
      id: "crystal",
      label: "The Crystal",
      symbol: "✦",
      color: "#9333ea",      // purple — fate/re-roll
      pos: { left: "88%", top: "55%" }, // far right — over the crystal
      comingSoon: true,
      desc: "Fate manipulation. Re-rolling the Unstable World. Coming soon.",
    },
  ];

  if (phase === "section" && activeSection === "forge") {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,5,0.97)",
        display: "flex", flexDirection: "column",
        fontFamily: GM.font,
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${GM.inkGoldBright}33`,
          display: "flex", alignItems: "center", gap: 16,
        }}>
          <button onClick={() => { setPhase("sign"); setActiveSection(null); }} style={{
            background: "transparent", border: `1px solid ${GM.inkBrown}66`,
            color: GM.vellumFaint, padding: "4px 10px", borderRadius: 6,
            fontSize: 10, cursor: "pointer", fontFamily: GM.font,
          }}>← Back</button>
          <div style={{
            fontSize: 11, color: GM.inkGoldBright,
            letterSpacing: 4, fontWeight: 900,
            textShadow: `0 0 12px ${GM.inkGoldBright}`,
          }}>◈ THE FORGE</div>
          <button onClick={onClose} style={{
            marginLeft: "auto", background: "transparent",
            border: `1px solid ${GM.inkBrown}66`, color: GM.vellumFaint,
            padding: "4px 10px", borderRadius: 6, fontSize: 10,
            cursor: "pointer", fontFamily: GM.font,
          }}>ESC</button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <ForgeSection profile={profile} onClose={onClose} />
        </div>
      </div>
    );
  }

  // Sign close-up view
  const atEnd = lineIdx >= LINES.length - 1 && !typing;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      fontFamily: GM.font,
      overflow: "hidden",
    }}>
      {/* ── Full screen image ── */}
      <div style={{
        position: "absolute", inset: 0,
        cursor: "pointer",
      }} onClick={advance}>
        <img
          src={`${CDN}/p_forge.webp`}   /* was a hardcoded domain — flagged in the ledger July 28, closed July 29 */
          alt="The Forge"
          onError={e => { e.target.style.display="none"; e.target.parentNode.style.background="linear-gradient(160deg,#0a0414,#130a2a)"; }}
          style={{
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center top",
            display: "block",
          }}
        />
        {/* Subtle vignette so text is readable */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }} />

        {/* Willie the Forger — center-bottom of image */}
        {/* swap: willie_forger.webp — Willy, same pose, apron that says WILLIE, different hat */}
        <div
          onClick={(e) => { e.stopPropagation(); setWillieClicked(true); setLineIdx(0); setTyped(""); setTyping(true); }}
          style={{
            position: "absolute", bottom: "8%", left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3, cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
          {/* Willie CSS placeholder — same as Willy but with apron */}
          <div style={{
            width: 40, height: 48,
            background: "linear-gradient(180deg, #3a2a1a, #2a1a0a)",
            border: "2px solid #c9a84c66",
            borderRadius: "40% 40% 30% 30%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, position: "relative",
            boxShadow: "0 0 12px rgba(201,168,76,0.3)",
          }}>
            🧑‍🔧
            {/* Apron badge */}
            <div style={{
              position: "absolute", bottom: -6, left: "50%",
              transform: "translateX(-50%)",
              background: "#c9a84c", color: "#000",
              fontSize: 5, fontWeight: 900, letterSpacing: 1,
              padding: "1px 4px", borderRadius: 2, whiteSpace: "nowrap",
            }}>WILLIE</div>
          </div>
          {/* Speak bubble */}
          <div style={{
            marginTop: 6,
            background: "rgba(0,0,0,0.85)",
            border: `1px solid #c9a84c66`,
            borderRadius: 8, padding: "3px 10px",
            fontSize: 9, color: "#c9a84c",
            fontFamily: GM.font, letterSpacing: 1,
            backdropFilter: "blur(4px)",
            whiteSpace: "nowrap",
          }}>◆ Speak</div>
        </div>

        {/* 4 Section buttons — absolutely positioned over scene objects */}
        {SECTIONS.map(s => (
          <div key={s.id}
            onClick={(e) => {
              e.stopPropagation();
              if (s.comingSoon) return;
              if (s.id === "codex") { onOpenCodex && onOpenCodex(); onClose(); return; }
              if (s.id === "forge") { onForge && onForge(); onClose(); return; }
              setActiveSection(s.id); setPhase("section");
            }}
            style={{
              position: "absolute",
              left: s.pos.left, top: s.pos.top,
              transform: "translate(-50%, -50%)",
              zIndex: 4,
              padding: "8px 14px",
              background: s.comingSoon ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.82)",
              border: `2px solid ${s.comingSoon ? s.color + "44" : s.color + "99"}`,
              borderRadius: 8,
              display: "flex", flexDirection: "column",
              alignItems: "center", gap: 3,
              cursor: s.comingSoon ? "default" : "pointer",
              transition: "all 0.2s",
              boxShadow: s.comingSoon ? "none" : `0 0 16px ${s.color}55, 0 4px 12px rgba(0,0,0,0.9)`,
              backdropFilter: "blur(6px)",
              opacity: s.comingSoon ? 0.5 : 1,
            }}>
            <div style={{ fontSize: 16, color: s.color, textShadow: s.comingSoon ? "none" : `0 0 10px ${s.color}` }}>
              {s.symbol}
            </div>
            <div style={{ fontSize: 7, color: s.color, fontWeight: 900, letterSpacing: 2, whiteSpace: "nowrap" }}>
              {s.label.toUpperCase()}
            </div>
            {s.comingSoon && (
              <div style={{ fontSize: 6, color: s.color, opacity: 0.6, letterSpacing: 1 }}>SOON</div>
            )}
          </div>
        ))}

        {/* Title */}
        <div style={{
          position: "absolute", top: 12, left: "50%",
          transform: "translateX(-50%)", zIndex: 5,
          fontSize: 9, color: "rgba(255,255,255,0.85)",
          letterSpacing: 5, fontFamily: GM.font,
          background: "rgba(0,0,0,0.55)",
          padding: "3px 14px", borderRadius: 4,
          backdropFilter: "blur(4px)",
          textShadow: "0 1px 4px rgba(0,0,0,1)",
          pointerEvents: "none",
        }}>◆ THE PLAZA ◆</div>

        {/* ESC */}
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{
          position: "absolute", top: 10, right: 10, zIndex: 6,
          background: "rgba(0,0,0,0.7)", border: `1px solid ${GM.inkBrown}66`,
          color: GM.vellumFaint, padding: "4px 10px",
          borderRadius: 6, fontSize: 10, cursor: "pointer",
        }}>ESC</button>
      </div>

      {/* ── Dialogue bubble — absolute, bottom center ── */}
      <div style={{
        position: "absolute",
        bottom: 32, left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        width: "min(680px, 90vw)",
      }}>
        {/* Dialogue bubble — wraps tightly around text */}
        <div style={{
          background: willieClicked
            ? "linear-gradient(135deg, rgba(201,168,76,0.08), rgba(4,2,14,0.95))"
            : "linear-gradient(135deg, rgba(109,40,217,0.08), rgba(4,2,14,0.95))",
          border: `1px solid ${willieClicked ? "#c9a84c55" : "#4a208866"}`,
          borderRadius: 12,
          padding: "12px 20px",
          maxWidth: "min(640px, 90vw)",
          width: "fit-content",
          boxShadow: willieClicked
            ? "0 0 20px rgba(201,168,76,0.12)"
            : "0 0 20px rgba(109,40,217,0.12)",
          transition: "all 0.3s",
        }}>
          {/* Speaker label */}
          <div style={{
            fontSize: 9, letterSpacing: 3, fontWeight: 900,
            color: willieClicked ? "#c9a84c99" : `${GM.inkGold}88`,
            marginBottom: 6, fontFamily: GM.font,
          }}>
            {willieClicked ? "◆ WILLIE THE FORGER" : "◆ THE FORGE"}
          </div>

          {/* Text — wraps naturally */}
          <div style={{
            fontSize: 13, lineHeight: 1.75, fontStyle: "italic",
            color: willieClicked ? "#c9a84c" : GM.vellum,
            textShadow: willieClicked ? "0 0 8px #c9a84c22" : "none",
            transition: "color 0.3s",
            whiteSpace: "pre-wrap",
          }}>
            {typed}
            {typing && <span style={{ opacity: 0.35, animation: "pulse 1s infinite" }}>▌</span>}
          </div>
        </div>

        {/* Progress dots + buttons — below the bubble */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          {/* Dots */}
          <div style={{ display: "flex", gap: 5 }}>
            {LINES.map((_, i) => (
              <div key={i} style={{
                width: i === lineIdx ? 16 : 6,
                height: 4, borderRadius: 2,
                background: i <= lineIdx
                  ? (willieClicked ? "#c9a84c" : GM.inkGold)
                  : GM.pageBorder,
                transition: "all 0.25s",
              }} />
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {!atEnd ? (
              <button onClick={advance} style={{
                padding: "7px 18px", borderRadius: 20, cursor: "pointer",
                border: `1px solid ${willieClicked ? "#c9a84c55" : GM.inkGold + "55"}`,
                background: "transparent",
                color: willieClicked ? "#c9a84caa" : `${GM.inkGold}aa`,
                fontSize: 10, fontFamily: GM.font, letterSpacing: 1,
              }}>◆ Continue</button>
            ) : (
              <button onClick={onClose} style={{
                padding: "7px 20px", borderRadius: 20, cursor: "pointer",
                border: `1px solid ${GM.pageBorder}44`, background: "transparent",
                color: GM.vellumFaint, fontSize: 10, fontFamily: GM.font,
              }}>Leave</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Plaza({
  onEnterDungeon, onEnterShop, onRulebook, onTutorial1, onDeckBuilder,
  onForge, onCollection, onTrading, onVersus, onEnterGame, onEnterNPC, onBack,
  onFactionHall, onPierre, onLibrary, onDebugger,
  depthResult, onClearDepthResult, gauntletResult, onClearGauntletResult,
  pvpResult, onClearPvpResult,
  // A friend's invite, or a duel this player was already in — App resolves
  // both to a 4-digit code and hands it here. The kiosk opens itself.
  inviteCode = null, onInviteConsumed, pendingTradeId = null,
}) {
  // ── Portrait lock — try OS-level hard lock; RotateGate is the
  //    fallback curtain when a plain browser tab won't grant it. ──
  const { needsRotate } = useOrientationLock();

  // ── Dynamic tile + portrait camera ────────────────────────
  const [tile, setTile] = useState(calcTile);
  const [portrait, setPortrait] = useState(isPortrait);
  const [camOffset, setCamOffset] = useState(null);
  // ── THE 3D SWITCH — persisted in plazaCamera, off by default ──
  const [plaza3D, setPlaza3DState] = useState(getPlaza3D);
  useEffect(() => subscribePlazaCam(() => setPlaza3DState(getPlaza3D())), []);
  useEffect(() => {
    const update = () => {
      setTile(calcTile());
      setPortrait(isPortrait());
    };
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  const TILE = tile;
  const mobile = isMobile();
  const activeCOLS    = portrait ? P_COLS    : COLS;
  const activeROWS    = portrait ? P_ROWS    : ROWS;
  // One table, filtered — no portrait twins to keep in step.
  const activeZONES   = zonesFor(portrait);
  const activePORTALS = activeZONES.filter(z => z.kind === "portal");
  // Plaza 3D reads WHERE everyone is out of a ref in its frame loop, and
  // reconciles only when WHO changes (rosterKey). `players` stays the
  // React-state source of truth for everything else in this file.
  const world3D = useMemo(() => ({
    cols: activeCOLS, rows: activeROWS, zones: activeZONES,
    spawn: portrait ? P_SPAWN : SPAWN, portrait,
  }), [activeCOLS, activeROWS, activeZONES, portrait]);

  // ── Auth & profile ─────────────────────────────────────────
  const [uid, setUid]                               = useState(null);
  const [playerName, setPlayerName]                 = useState("");
  const [profile, setProfile]                       = useState(null);
  const [authReady, setAuthReady]                   = useState(false);
  // ♪ The login song keeps playing from the boot sequence until a player
  // name exists — i.e. until you are actually signed in. Same track id as
  // the boot screen, so it continues instead of restarting.
  useMusicOverride(playerName ? null : "login");
  const [needsStarterPick, setNeedsStarterPick]     = useState(false);
  const [needsOnboarding, setNeedsOnboarding]       = useState(false);
  const [gender, setGender]                         = useState("m");
  const [showGenderSettings, setShowGenderSettings] = useState(false);
  const profileUnsubRef                             = useRef(null);

  // ── LEAKED PROFILE-ROOT LISTENER (same bug fixed in Annihilation) ──
  // listenToProfileByUid subscribes to profiles/{uid} — the ROOT of the
  // shared IE profile. Nothing unsubscribed on unmount, so it outlived
  // this screen for the whole session, and every write anywhere in the
  // Empire delivered a full snapshot deserialized synchronously on the
  // main thread. That is the freeze-then-catch-up stutter.
  //
  // Also: the assignment sites overwrote .current without unsubscribing
  // the previous listener, stacking one more root listener per auth
  // bounce. attachProfileListener closes that too.
  const attachProfileListener = (nextUid, cb) => {
    profileUnsubRef.current?.();
    profileUnsubRef.current = listenToProfileByUid(nextUid, cb);
  };
  useEffect(() => () => {
    profileUnsubRef.current?.();
    profileUnsubRef.current = null;
  }, []);

  // ── H30 · THE TOURNAMENT STARTER SEAM ───────────────────────
  // tournament.js's PLAY button calls onPairingReady, which calls
  // whatever function `setPairingStarter` was last handed — and does
  // NOTHING if nobody ever called it (that's the honest "AWAITING THE
  // PILOT" state TournamentBoard shows). This registers that function
  // the moment a profile is loaded: create a real match, seated with
  // MY OWN deck, using the opts the bracket already worked out
  // (seatCount/mode/deckMode/teams) — exactly the same createMatch
  // V'yen's kiosk calls, just triggered from a bracket pairing instead
  // of a kiosk click. Whoever ELSE is in this pairing is not seated by
  // this call — they each see the resulting code land on their own
  // "YOUR MATCH" card (BulletinBoard's join-in-progress effect claims
  // their seat), the same manual-code-then-join shape every human
  // match in this game already uses; nothing here invents a new kind
  // of matchmaking.
  //
  // Deregistered on unmount so leaving the Plaza doesn't leave a stale
  // starter answering for a screen that's gone.
  //
  // AUDIT FIX (2026-08-22): this used to depend on [profile, playerName]
  // — `profile` is a whole object rebuilt on EVERY write anywhere in the
  // Empire (see the listener's own comment above), so the effect was
  // tearing down and re-registering constantly, each pass through two
  // independent async `import().then()` chains. A PLAY tap landing in
  // the gap between the teardown's setPairingStarter(null) and the
  // re-register's setPairingStarter(fn) would silently no-op. Depend on
  // profile?.uid instead — a real login/logout, not every profile
  // write — and read the latest profile/playerName through refs so the
  // registered closure still never goes stale between those rare
  // re-registrations.
  const pairingStarterProfileRef = useRef(profile);
  const pairingStarterNameRef = useRef(playerName);
  useEffect(() => { pairingStarterProfileRef.current = profile; }, [profile]);
  useEffect(() => { pairingStarterNameRef.current = playerName; }, [playerName]);
  useEffect(() => {
    if (!profile?.uid) return;
    let live = true;
    import("./tournament").then(({ setPairingStarter }) => {
      if (!live) return;
      setPairingStarter(async (match, opts) => {
        const deck = resolveDeck(pairingStarterProfileRef.current);
        if (!deck) return null;
        const { createMatch, drawAndDepleteCards } = await import("./gameManager");
        const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
        return createMatch(
          pairingStarterNameRef.current, depletedGrimoire, deck.luxmoire || {}, deck.prophecy || null,
          startingHand, deck.curios || [null, null], deck.esclings || null, opts
        );
      });
    });
    return () => {
      live = false;
      import("./tournament").then(({ setPairingStarter }) => setPairingStarter(null)).catch(() => {});
    };
  }, [profile?.uid]);

  // ── Local companion — SINGLE SOURCE OF TRUTH ────────────────
  // Resolve the equipped companion the SAME way useEquippedCompanion
  // (the failsafe hook every other room uses) does, so the Plaza shows
  // the exact same companion as the Affinity hall, dungeons, workshop,
  // etc. Priority: equipped (if it still exists) → the player's starter
  // companion → null. This is why the companion no longer "shapeshifts"
  // room-to-room: every surface now resolves from this same rule, and
  // renders from the same CDN art source.
  const myCompanionId = useMemo(() => {
    if (!profile) return null;
    const eq = profile.equippedCompanion;
    if (eq && COMPANIONS[eq]) return eq;
    const starter = getStarterCompanionId
      ? getStarterCompanionId(profile.starterDeckId)
      : null;
    return (starter && COMPANIONS[starter]) ? starter : null;
  }, [profile]);
  // KING'S GRACE — validated: a Grace the profile does not hold for the
  // companion at your side reads as nothing (kingsGrace.equippedGraceOf).
  const myCompanionRadiant = (() => { const w = equippedGraceOf(profile); return w && w.companionId === profile?.equippedCompanion ? renderableGrace(w.graceId) : null; })();

  // ── Movement ───────────────────────────────────────────────
  const [pos, setPos]       = useState(() => isPortrait() ? P_SPAWN : SPAWN);
  const [facing, setFacing] = useState({ x: 0, y: 1 });
  const posRef              = useRef(isPortrait() ? P_SPAWN : SPAWN);

  // ── Visual (sub-tile) position — smooth walking ────────────
  // pos is the authoritative tile position (collision, proximity, NPC zones).
  // visualPosRef is the rendered fractional position that lerps toward pos
  // each frame. We do NOT keep this in React state — the rAF loop drives
  // the camera DOM transform directly via worldRef.current.style. This is
  // the only sane way to do per-frame animation under React 18, which
  // throws "max update depth" if it can't flush per-frame setStates fast
  // enough on slower devices.
  const spawn = isPortrait() ? P_SPAWN : SPAWN;
  const visualPosRef              = useRef({ x: spawn.x, y: spawn.y });
  const rafRef                    = useRef(null);
  const worldRef                  = useRef(null);     // the translate3d world layer
  const localSpriteRef            = useRef(null);     // local PlayerEntity's outer sprite div
  const localCompanionRef         = useRef(null);     // local PlayerEntity's companion div (if any)
  const camOffsetInternalRef      = useRef(null);     // rAF-internal cam offset; separate from camOffset state

  // ── Multiplayer ────────────────────────────────────────────
  const [players, setPlayers] = useState({});
  const othersRef = useRef({});                       // Plaza 3D's frame-loop view of `players`
  useEffect(() => { othersRef.current = players; }, [players]);
  const rosterKey = Object.keys(players).join("|"); // who is here — not where they are

  // ── Chat ───────────────────────────────────────────────────
  // One server-wide chat: Plaza joins the same "lobby" channel every
  // RoomOverlay-wrapped room uses, so conversation follows players across
  // rooms. Plaza keeps its own in-world floaties either way.
  const chat = useChatSystem({ zoneId: "lobby", uid, playerName, pos });
  // THE RAIL (2026-09-23) — the Plaza hosts the backdrop band. Panels start
  // at 156 here (the title pill owns 112). Desktop/landscape only.
  useRailHost(!!uid && !mobile && !portrait, 156);
  const railState = useRail();
  const railFolded = railState.active && railState.collapsed;

  // ── UI ─────────────────────────────────────────────────────
  const [showWilly, setShowWilly]           = useState(false);
  const [showBoard, setShowBoard]           = useState(false);
  const [showHub, setShowHub]               = useState(false);
  const [hubSection, setHubSection]         = useState(null); // null | "forge" | "codex"
  const [showVyren, setShowVyren]           = useState(false);
  // An invite code held until the player is logged in and the Plaza is up.
  // The kiosk is V'yen's, and every match in Terror is rendezvoused there —
  // an invite opens the same door a typed code does, so a friend's duel and
  // a hand-shared code arrive by exactly one path and there is no second
  // join flow to keep in step with the first.
  const [pendingInvite, setPendingInvite]   = useState(null);
  useEffect(() => {
    if (!inviteCode || !uid) return;
    setPendingInvite(inviteCode);
    setShowVyren(true);
  }, [inviteCode, uid]);
  // A TRADE invite, held the same way: once the player and profile are
  // up, walk them into the Exchange Post with that trade on the table.
  // ⚠ `profile` MUST be a dependency: setUid lands a render before the
  // async getProfileByUid resolves, so on a cold `?trade=` arrival the
  // first run sees profile === null and — without it in the deps —
  // never ran again. The ref keeps it to one door per trade id.
  const tradeOpenedRef = useRef(null);
  useEffect(() => {
    if (!pendingTradeId || !uid || !profile) return;
    if (tradeOpenedRef.current === pendingTradeId) return;
    tradeOpenedRef.current = pendingTradeId;
    onTrading && onTrading({ ...profile, uid, name: playerName });
  }, [pendingTradeId, uid, !!profile]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [portalPrompt, setPortalPrompt]     = useState(null);
  const [portalActive, setPortalActive]     = useState(false);
  const [portalCinematicData, setPortalCinematicData] = useState(null);
  const [showDepths, setShowDepths]         = useState(false);
  const [showMenu, setShowMenu]             = useState(false);  // Start menu (Pokémon-style)
  const [showGallery, setShowGallery]       = useState(false);  // Deeds & Renown wall
  // Session tap count for the companion close-up — flushed to
  // progression.recordCompanionPet on close. A ref, not state: the
  // number changes on every tap and nothing renders off it.
  const petCountRef = useRef(0);
  const [showPet, setShowPet]               = useState(false);  // companion petting close-up

  // ── Achievement toast feed ─────────────────────────────────
  // The visible payoff for the whole achievement engine. Grants are
  // written to profiles/{uid}/achievements/{id} from all over the app
  // (story wins, pack opens, petting, the Forge). Rather than thread a
  // setter through every one of those call sites, we watch the live
  // achievement map here — the single place that always holds the uid —
  // and toast any id that appears AFTER the first snapshot. This catches
  // grants no matter where they fired, including a cascade (a single win
  // can also trip an empire meta achievement) and even cross-tab earns.
  const { map: achMap } = useAchievements(uid);
  const [toastIds, setToastIds] = useState([]);
  const achSeenRef = useRef(null); // null until the first snapshot is banked
  useEffect(() => {
    const ids = Object.keys(achMap || {});
    // First snapshot just banks what's already owned — never toasts history.
    if (achSeenRef.current === null) {
      achSeenRef.current = new Set(ids);
      return;
    }
    const fresh = ids.filter((id) => !achSeenRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => achSeenRef.current.add(id));
    setToastIds((q) => [...q, ...fresh]);
  }, [achMap]);
  // Reset the bank when the player changes (logout / handoff).
  useEffect(() => { achSeenRef.current = null; setToastIds([]); }, [uid]);
  const [petPrompt, setPetPrompt]           = useState(null);   // {x,y} tile when facing companion
  const [compOffset, setCompOffset]         = useState({ x: 0, y: -1 }); // companion offset; freezes on a pure turn
  const [readySet, setReadySet]             = useState({});
  const [shopPrompt, setShopPrompt]         = useState(null);
  const [npcPrompt, setNpcPrompt]           = useState(null);
  const [landmarkPrompt, setLandmarkPrompt] = useState(null);  // the fountain
  const [showFountain, setShowFountain]     = useState(false);
  const [lockedNote, setLockedNote]         = useState(null);  // a shut door speaking

  // ── ROOM CONSOLE (the treatment — July 8) ─────────────────
  // Session-persistent dialogue log for the "plaza" zone + the
  // ◀ ▶ cycling selection across every prompt in range at once.
  const [plazaLog, setPlazaLog] = useState(() => getSessionLog("plaza").slice());
  const addPlazaLog = useCallback((entry) => {
    setPlazaLog(prev => {
      const next = [...prev, entry];
      setSessionLog("plaza", next);
      return next;
    });
  }, []);
  const [consoleIdx, setConsoleIdx] = useState(0);
  const consoleIdsRef = useRef("");
  const [toast, setToast]                   = useState(null);
  // ⚔ THE CHALLENGE LIVES IN ChallengeSystem NOW (2026-08-20).
  // Two state fields, a Firebase listener, a proximity scan, a send, an
  // accept and a decline — ~173 lines — used to sit inline in this file
  // doing exactly what ChallengeSystem.js already claimed to do for
  // "Plaza, FactionHall, or any future MMO zone." That file was
  // imported by nothing and its deck lookup was broken; this file's was
  // fixed and its proximity scan was not. The merge went both ways.
  // See the header of ChallengeSystem.js.
  //
  // ⚠ `resolveDeck` IS PASSED IN, not read. Where a deck comes from is
  // the Plaza's business; the hook must never learn it again.
  //
  // Declared LOW in this component on purpose — it closes over
  // `showToast`, `profile` and `onEnterGame`, all of which are above.

  const keysRef     = useRef({});
  const facingRef    = useRef({ x: 0, y: 1 });   // current facing, readable synchronously in the step loop
  const compOffsetRef = useRef({ x: 0, y: -1 }); // mirrors compOffset, updated only on a real step
  const menuOpenRef = useRef(false);
  const sessionId   = useRef(`${Date.now()}_${Math.random().toString(36).slice(2)}`);

  // ── Camera follow — runs on BOTH desktop and portrait ────
  // Deadzone follow on desktop, snap-center on portrait.
  // Camera offset state — used by ChatSystem (chat bubbles need a React
  // state value to position themselves). Updated only when `pos` changes
  // (tile-cross), so this fires ~1-2 times per second of walking, not 60.
  // The world's actual visual translate3d is driven directly via worldRef
  // in the rAF loop — that's what gives smooth sub-tile scrolling. The
  // camOffset state just tracks a coarse "where is the camera roughly?"
  // for non-animation consumers.
  useEffect(() => {
    setCamOffset(prev => {
      const next = calcCamOffset(pos.x, pos.y, TILE, prev, {
        cols: activeCOLS, rows: activeROWS,
      });
      if (prev && next.x === prev.x && next.y === prev.y) return prev;
      return next;
    });
  }, [pos, portrait, TILE, activeCOLS, activeROWS]);

  // ── Teleport guard — snap visualPosRef to pos on large jumps ──
  // Covers: portrait↔desktop swap, portal respawn, edge cases.
  // Normal tile walking (delta 1) does not trigger this. The rAF loop
  // picks up the new ref value on its next tick — no setState needed.
  useEffect(() => {
    const cur = visualPosRef.current;
    if (Math.abs(pos.x - cur.x) > 2 || Math.abs(pos.y - cur.y) > 2) {
      visualPosRef.current = { x: pos.x, y: pos.y };
    }
  }, [pos]);

  // ── Smooth movement — lerp visualPosRef toward pos every frame ──
  // pos is tile-indexed (authoritative). visualPosRef is fractional,
  // catches up via exponential lerp. Tuned so a single-tile step
  // takes ~130ms to visually complete — snappy but smooth.
  //
  // CRITICAL: this loop does NOT call setState. It writes the world's
  // translate3d directly to worldRef.current.style.transform and the
  // local player's sub-tile offset to localSpriteRef/localCompanionRef.
  // Driving 60fps animation through React state under React 18 is
  // unsafe — when the scheduler can't flush per-frame setStates fast
  // enough (cold starts, busy CPU), it raises "max update depth".
  // DOM-direct animation sidesteps that entirely.
  useEffect(() => {
    let last = performance.now();
    const LERP_SPEED = 14; // higher = snappier; ~14 feels EarthBound-ish
    const SNAP_EPSILON = 0.01; // snap when within 1% of a tile

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); // cap dt at 50ms to avoid jumps on tab-return
      last = now;
      const target = posRef.current;
      const cur    = visualPosRef.current;
      const dx = target.x - cur.x;
      const dy = target.y - cur.y;

      let nx, ny;
      if (Math.abs(dx) < SNAP_EPSILON && Math.abs(dy) < SNAP_EPSILON) {
        // Close enough — snap
        nx = target.x; ny = target.y;
        visualPosRef.current = { x: nx, y: ny };
      } else {
        // Exponential lerp: frame-rate independent via dt
        const t = 1 - Math.exp(-LERP_SPEED * dt);
        nx = cur.x + dx * t;
        ny = cur.y + dy * t;
        visualPosRef.current = { x: nx, y: ny };
      }

      // Camera transform — direct DOM write, no React.
      const camNow = calcCamOffset(nx, ny, TILE, camOffsetInternalRef.current, {
        cols: activeCOLS, rows: activeROWS,
      });
      camOffsetInternalRef.current = camNow;
      if (worldRef.current) {
        worldRef.current.style.transform = `translate3d(${-camNow.x}px, ${-camNow.y}px, 0)`;
      }

      // Local player sub-tile transform — applied to the sprite div directly.
      // Sprite's `left/top` is at the integer tile position (player.x * TILE),
      // so the rAF transform supplies the fractional remainder. Net visual
      // position is at (visualPosRef.x, visualPosRef.y) in tile coords.
      // CSS transition is `none` for local sprite so this doesn't fight CSS.
      const subX = (nx - target.x) * TILE;
      const subY = (ny - target.y) * TILE;
      const subTransform = `translate3d(${subX}px, ${subY}px, 0)`;
      if (localSpriteRef.current) {
        localSpriteRef.current.style.transform = subTransform;
      }
      if (localCompanionRef.current) {
        localCompanionRef.current.style.transform = subTransform;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [TILE, activeCOLS, activeROWS]);

  // Mirror facing/compOffset into refs so the step loop can read them
  // synchronously without re-subscribing every turn.
  useEffect(() => { facingRef.current = facing; }, [facing]);
  useEffect(() => { compOffsetRef.current = compOffset; }, [compOffset]);

  // Open/close the Start menu with M. Ignored while typing in an input or
  // when another modal owns the screen (M isn't a movement or bubble key).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "m" && e.key !== "M") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      // THE THIRD COPY OF THE SAME LIST. Deeds & Renown and the
      // Fountain were missing here too, so M opened the Start menu on
      // top of them. There are now three of these lists in this file
      // and they must be kept in step by hand — a single
      // `anyOverlayOpen` derived once would be the right shape, and is
      // deliberately NOT done in this pass because the three lists are
      // subtly different (this one wants portalActive, the console one
      // does not) and collapsing them on a guess is how a movement bug
      // gets shipped. Noted as debt rather than fixed blind.
      // ⚠ AND THE DEBT IS BIGGER THAN IT LOOKS: each of the four lists
      // ALSO has a dependency array that has to say the same thing, so
      // it is really eight places, not four. A derived `anyOverlayOpen`
      // memo would collapse all eight into one — that is the fix, and
      // it wants a live lap in the browser rather than a blind edit.
      if (showVyren || showBoard || showHub || showWilly || showGenderSettings || showDepths || portalActive || showPet || showGallery || showFountain) return;
      e.preventDefault();
      setShowMenu(v => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showVyren, showBoard, showHub, showWilly, showGenderSettings, showDepths, portalActive, showPet, showGallery, showFountain]);

  // ── Firebase Auth ──────────────────────────────────────────
  useEffect(() => { setPersistence(auth, browserLocalPersistence).catch(() => {}); }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUid(user.uid);
        const name = user.displayName || user.email?.split("@")[0] || "Aenarchist";
        setPlayerName(name);
        const p = await getProfileByUid(user.uid);
        if (!p) {
          // Brand-new profile — never seen anything. Welcome them to the Unstable World.
          const newP = await createProfile(user.uid, name);
          setProfile({ ...newP, uid: user.uid });
          setNeedsOnboarding(true);
        } else {
          setProfile({ ...p, uid: user.uid });
          if (p.gender) setGender(p.gender);
          if (!p.starterDeckId) {
            // No deck yet. Did they go through onboarding? Decide which overlay to show.
            if (!p.hasCompletedOnboarding) {
              // Legacy user or interrupted onboarding — give them the proper welcome.
              setNeedsOnboarding(true);
            } else {
              // Edge case: completed onboarding but no deck. Fall back to classic picker.
              setNeedsStarterPick(true);
            }
          } else {
            // Companion system: auto-equip starter Beb + backfill story trophies.
            // Both are idempotent and safe to await in parallel.
            try {
              await Promise.all([
                ensureStarterCompanion(user.uid),
                backfillStoryTrophies(user.uid),
              ]);
            } catch (e) { console.warn("[companion migration]", e); }

            attachProfileListener(user.uid, (updated) => {
              setProfile(updated);
              if (updated?.gender) setGender(updated.gender);
            });
          }
        }
      } else {
        setUid(null); setPlayerName(""); setProfile(null);
        setNeedsStarterPick(false); setNeedsOnboarding(false); setGender("m");
      }
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const handleLogin = (newUid, name, p) => {
    setUid(newUid); setPlayerName(name);
    if (!p || !p.starterDeckId) {
      // No deck: send to onboarding unless they've already completed it.
      if (!p || !p.hasCompletedOnboarding) {
        setNeedsOnboarding(true);
      } else {
        setNeedsStarterPick(true);
      }
      setProfile(p || null);
    } else {
      setProfile(p);
      if (p.gender) setGender(p.gender);
      attachProfileListener(newUid, (updated) => {
        setProfile(updated);
        if (updated?.gender) setGender(updated.gender);
      });
    }
  };

  const handleStarterPicked = async (deckId, pickedGender) => {
    setNeedsStarterPick(false);
    if (pickedGender) setGender(pickedGender);
    if (uid) {
      // Grant starter Beb immediately on starter pick
      try { await ensureStarterCompanion(uid); } catch (e) { console.warn("[starter companion]", e); }
      const p = await getProfileByUid(uid);
      if (p) {
        setProfile(p);
        if (p.gender) setGender(p.gender);
        attachProfileListener(uid, (updated) => {
          setProfile(updated);
          if (updated?.gender) setGender(updated.gender);
        });
      }
    }
  };

  // ── Onboarding completion ────────────────────────────────────
  // Fires when the new player finishes (or dev-skips) the Onboarding
  // overlay. The Onboarding component itself already wrote the profile
  // fields (gender, starterDeckId, hasCompletedOnboarding) and granted
  // the starter deck's cards to the collection. We just need to:
  //   1. Flip the onboarding flag off (reveals the Plaza)
  //   2. Grant the starter companion (same as starter-pick flow)
  //   3. Reload the profile + subscribe to live updates
  const handleOnboardingComplete = async ({ deckId, gender: pickedGender } = {}) => {
    setNeedsOnboarding(false);
    if (pickedGender) setGender(pickedGender);
    if (uid) {
      try { await ensureStarterCompanion(uid); } catch (e) { console.warn("[starter companion]", e); }
      const p = await getProfileByUid(uid);
      if (p) {
        setProfile(p);
        if (p.gender) setGender(p.gender);
        attachProfileListener(uid, (updated) => {
          setProfile(updated);
          if (updated?.gender) setGender(updated.gender);
        });
      }
    }
  };

  const handleLogout = async () => {
    profileUnsubRef.current?.();
    // ⚠ BEFORE signOut, NOT AFTER — see releasePresence's banner. The
    // social layer's own cleanup runs after the auth listener has already
    // fired, by which point the write that would clear this row is
    // refused, and friends keep seeing a logged-out player as online.
    try { await releasePresence(auth.currentUser?.uid); } catch (e) {}
    await signOut(auth);
    setUid(null); setPlayerName(""); setProfile(null);
    setNeedsStarterPick(false); setNeedsOnboarding(false); setGender("m");
  };

  // ── Firebase presence ──────────────────────────────────────
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    const playerRef = ref(db, `plaza/players/${uid}`);
    set(playerRef, { name: playerName, x: (isPortrait() ? P_SPAWN : SPAWN).x, y: (isPortrait() ? P_SPAWN : SPAWN).y, facingX: 0, facingY: 1, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null, ts: Date.now() });
    const unsub = onValue(ref(db, `plaza/players`), snap => {
      const data = snap.val() || {};
      const cutoff = Date.now() - 15000;
      const others = {};
      Object.entries(data).forEach(([id, p]) => {
        if (id === uid) return;
        if (!p.ts || p.ts < cutoff) { remove(ref(db, `plaza/players/${id}`)); return; }
        others[id] = p;
      });
      const capped = {};
      Object.entries(others).slice(0, 3).forEach(([id, p]) => { capped[id] = p; });
      setPlayers(capped);
    });
    const heartbeat = setInterval(() => { set(ref(db, `plaza/players/${uid}/ts`), Date.now()); }, 5000);
    return () => { unsub(); clearInterval(heartbeat); remove(playerRef); };
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding]);

  // ── Companion swap push — lightweight update so other players see
  // the new companion immediately after a Sol Convert, without tearing
  // down the presence listener. Only writes the companionId field.
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    set(ref(db, `plaza/players/${uid}/companionId`), myCompanionId || null);
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding, myCompanionId]);

  // ── Cosmetics swap push — same pattern as companion. When the player
  // equips/unequips from any IE app, push the updated `equipped` map to
  // the Plaza presence node so other players see cosmetics change live.
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    set(ref(db, `plaza/players/${uid}/equipped`), profile?.equipped || null);
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding, profile?.equipped]);

  const lastSync = useRef(0);
  const syncPos = useCallback((x, y, fx, fy) => {
    if (!uid) return;
    const now = Date.now();
    if (now - lastSync.current < 80) return;
    lastSync.current = now;
    set(ref(db, `plaza/players/${uid}`), { name: playerName, x, y, facingX: fx, facingY: fy, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null, ts: now });
  }, [uid, playerName, gender, myCompanionId, profile?.equipped]);

  // ── WALKING PAST A DOOR IS NOT OPENING IT (July 29 fix) ──────
  // This effect used to launch Pierre the instant `portalPrompt`
  // became the Pierre arch — that is, on PROXIMITY, not on a
  // keypress. It survived because the old arches were spread across
  // row 3 and you rarely brushed one by accident.
  //
  // The new art puts all five arches on one shelf and row 5 is the
  // corridor beneath them, so under the old code simply WALKING TO
  // THE STORY PORTAL would fling you into another game on the way
  // past. Activation now belongs to activateConsoleTarget() and to
  // nothing else, which is where the Q key already sent it.
  useEffect(() => {
    if (!portalPrompt || !portalPrompt.readyUp) return;
    const unsub = onValue(ref(db, `plaza/ready/${portalPrompt.id}`), snap => setReadySet(snap.val() || {}));
    return () => unsub();
  }, [portalPrompt]);

  // Co-op ready-up. No portal carries `readyUp` today — the arch it
  // belongs to is the purple one, and the Mineriums are sealed. The
  // day dungeons come back it is one field on one row, which is the
  // whole reason PortalReadyUp is still standing.
  useEffect(() => {
    if (!portalPrompt?.readyUp) return;
    const dungeon = DUNGEONS.find(d => d.id === portalPrompt.dungeonId);
    if (!dungeon?.unlocked) return;
    const allKeys = [uid, ...Object.keys(players)];
    if (allKeys.length > 0 && allKeys.every(k => readySet[k])) {
      setTimeout(() => {
        remove(ref(db, `plaza/ready/${portalPrompt.id}`));
        const portal = portalPrompt;
        setPortalPrompt(null); setReadySet({}); setPortalActive(false);
        setPortalCinematicData({ portal, dungeon, profile });
      }, 1200);
    }
  }, [readySet, portalPrompt, players, playerName, profile, onEnterDungeon]);

  useEffect(() => {
    // ⚠ EVERY FULL-SCREEN OVERLAY BELONGS IN THIS LIST OR THE PLAZA
    // KEEPS PLAYING UNDERNEATH IT. showGallery (Deeds & Renown) and
    // showDepths (The Gauntlet) were both missing, so with either of them
    // open the avatar still walked to the arrow keys and Enter still
    // fired whatever portal it was standing on — the player closes an
    // achievement wall and finds themselves somewhere else. Both were
    // added AFTER this line was written, which is exactly how a list
    // like this rots: it is correct on the day it is authored and
    // nothing ever tells you when it stops being.
    const isOpen = showWilly || showBoard || showVyren || showGenderSettings || portalActive || showHub || showMenu || showPet || showFountain || showGallery || showDepths;
    menuOpenRef.current = isOpen;
    if (isOpen) keysRef.current = {};
  // ⚠ THE DEP ARRAY IS PART OF THE LIST. Adding an overlay to the
  // condition above and not here makes the fix INERT: the effect never
  // re-runs, so the ref it writes keeps yesterday's answer. That is
  // exactly how this bug was introduced in the first place — a list
  // that was correct on the day it was authored, in two places, and
  // only one of them noticed when a new overlay arrived.
  }, [showWilly, showBoard, showVyren, showGenderSettings, portalActive, showHub, showMenu, showPet, showFountain, showGallery, showDepths]);

  useEffect(() => {
    keysRef.current = {};
    const MOVE_KEYS = new Set(["ArrowUp","ArrowDown","ArrowLeft","ArrowRight","w","W","a","A","s","S","d","D"]);
    const down  = e => { if (menuOpenRef.current && MOVE_KEYS.has(e.key)) return; keysRef.current[e.key] = true; };
    const up    = e => { keysRef.current[e.key] = false; };
    const clear = ()  => { keysRef.current = {}; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    window.addEventListener("blur",    clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup",   up);
      window.removeEventListener("blur",    clear);
    };
  }, []);

  useEffect(() => {
    if (!playerName || needsStarterPick || needsOnboarding) return;
    let lastMove = 0;
    const loop = setInterval(() => {
      const menuOpen = showWilly || showBoard || showVyren || showGenderSettings || portalActive || showHub || showMenu || showPet || showFountain || showGallery || showDepths;
      if (menuOpen) { keysRef.current = {}; return; }
      const now = Date.now();
      if (now - lastMove < 130) return;
      const k = keysRef.current;
      let dx = 0, dy = 0;
      if (k["ArrowUp"]    || k["w"] || k["W"]) dy = -1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) dy =  1;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) dx = -1;
      if (k["ArrowRight"] || k["d"] || k["D"]) dx =  1;
      if (dx === 0 && dy === 0) return;
      lastMove = now;
      // ⭐ MOVEMENT FOLLOWS THE EYE (Plaza 3D, camera contract piece 4).
      // The 3D rig publishes its ground-forward to plazaCamera; the key
      // vector is rotated by it so W is always "away from the camera".
      // In 2D nobody publishes a forward and this is the IDENTITY —
      // proven in plazaCamera.selftest.mjs, not assumed.
      ({ x: dx, y: dy } = rotateGridInput(dx, dy));

      const f = facingRef.current;
      const facingThisWay = (f.x === dx && f.y === dy);

      // ── TURN-THEN-STEP ──────────────────────────────────────
      // Press a direction you are NOT facing → only turn to face it
      // (no step). Press (or hold) the direction you ARE facing → step.
      // A single tap = turn; holding = turn, then walk.
      if (!facingThisWay) {
        const nf = { x: dx, y: dy };
        setFacing(nf);
        facingRef.current = nf;
        // Update presence facing without moving.
        syncPos(posRef.current.x, posRef.current.y, dx, dy);
        // The companion offset is frozen on turns — so if this turn points
        // us AT the companion, surface the Pet prompt. Otherwise clear it.
        const co = compOffsetRef.current;
        const myCompanion = myCompanionId;
        if (myCompanion && co.x === dx && co.y === dy) {
          const px = posRef.current.x, py = posRef.current.y;
          setPetPrompt({ x: px + co.x, y: py + co.y });
        } else {
          setPetPrompt(null);
        }
        return; // turn only — no step this beat
      }

      // ── STEP (already facing this way) ───────────────────────
      setPos(prev => {
        const nx = prev.x + dx, ny = prev.y + dy;
        if (!isWalkable(nx, ny)) return prev;
        posRef.current = { x: nx, y: ny };
        // Companion trails to the tile we just left. This is the ONLY place
        // compOffset changes, so a pure turn never repositions the companion.
        const co = { x: -dx, y: -dy };
        compOffsetRef.current = co;
        setCompOffset(co);
        setPetPrompt(null);               // walking away from the companion
        setFacing({ x: dx, y: dy });
        facingRef.current = { x: dx, y: dy };
        syncPos(nx, ny, dx, dy);
        // EVERY lane at once. The old chain was `if portal … else if
        // shop … else if npc`, so only one prompt could ever exist —
        // a leftover from the days of one floating bubble, even
        // though the console has cycled between several targets ever
        // since those were retired. The new boxes overlap ON PURPOSE
        // (the purple arch and the bulletin board share tiles), so
        // one-at-a-time would now silently eat a door.
        const here     = zonesAt(nx, ny, isPortrait());
        const portal   = here.find(z => z.kind === "portal")   || null;
        const shop     = here.find(z => z.kind === "shop")     || null;
        const npc      = here.find(z => z.kind === "npc")      || null;
        const landmark = here.find(z => z.kind === "landmark") || null;
        // Keep the SAME object identity while you stand still, so the
        // console's target list doesn't churn and reset its selection.
        setPortalPrompt(  pv => (portal   && pv?.id === portal.id)   ? pv : portal);
        setShopPrompt(    pv => (shop     && pv?.id === shop.id)     ? pv : shop);
        setNpcPrompt(     pv => (npc      && pv?.id === npc.id)      ? pv : npc);
        setLandmarkPrompt(pv => (landmark && pv?.id === landmark.id) ? pv : landmark);
        if (!portal) setPortalActive(false);
        return { x: nx, y: ny };
      });
      // Proximity check OUTSIDE setPos — posRef is always current, players closure is fresh.
      // ⭐ AND IT GUARDS ITS OWN IDENTITY NOW. This block used to write a
      // FRESH prompt object every 50ms tick, which is twenty re-renders
      // a second of the whole room for a value that had not changed.
      // ChallengeSystem's checkProximity had already solved that and
      // this file never got the fix; now there is one copy of it.
      challengeRef.current(posRef.current, players);
    }, 50);
    return () => clearInterval(loop);
  }, [players, syncPos, playerName, needsStarterPick, needsOnboarding, showWilly, showBoard, showVyren, showGenderSettings, portalActive, showHub, showMenu, showPet, showFountain, showGallery, showDepths]);

  function showToast(msg, color = GM.inkGold) {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (!depthResult) return;
    const { humanWon, eloChange, newElo, opponentName, isGatekeeper, dcEarned } = depthResult;
    const color = humanWon ? "#22c55e" : "#dc2626";
    const icon  = humanWon ? "✓" : "✗";
    const sign  = eloChange >= 0 ? "+" : "";
    const gkTag = isGatekeeper ? " ⚔ GATEKEEPER" : "";
    // ◕ NOT DC. These faucets pay CHOCOLATE COINS — awardCoins, every
    // one of them — and the Faucet Law forbids a repeatable win from
    // paying the Empire-wide reserve at all. Calling coins "DC" in a
    // toast is how a player learns the wrong thing about the economy
    // in the one moment they are definitely reading.
    const dcTag = dcEarned > 0 ? ` · +${dcEarned} ◕` : "";
    showToast(`${icon} ${opponentName}${gkTag} · ${sign}${eloChange} ELO · ${newElo} total${dcTag}`, color);
    onClearDepthResult && onClearDepthResult();
  }, [depthResult]);

  // ── H29 — YOU DO NOT WALK BACK TO THE CABINET ───────────────
  // A gauntlet bout dumps you in the Plaza, which is correct for a
  // one-off Depths match and wrong for a run: bout two is the whole
  // point, and making the player cross the floor and reopen a kiosk
  // between bouts is exactly the friction the totem format exists to
  // remove. So the moment a bout resolves inside a run, the Gauntlet
  // comes back up on its own.
  //
  // This is the consumer for the `gauntletResult` prop that App.js
  // has been passing since the mode shipped — the outcome itself is
  // NOT shown here. `gauntlet.js` parks it on the profile and the
  // Gauntlet screen collects it on mount, so the celebration lands on
  // the screen it lives on rather than as a toast in a plaza.
  // This effect only decides WHERE you are standing, not what you
  // are told.
  //
  // The ELO toast above still renders over the top: it sits at the
  // same z-index and later in the DOM, so the receipt is readable
  // against the reopened screen rather than buried by it.
  useEffect(() => {
    if (!gauntletResult) return;
    setShowDepths(true);
    onClearGauntletResult && onClearGauntletResult();
  }, [gauntletResult]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── C4/C8 (July 17 2026): the PvP homecoming toast ──────────
  // Same pattern as the Depths toast above — the match already
  // celebrated on the victory screen; this is the receipt.
  useEffect(() => {
    if (!pvpResult) return;
    const { won, crests, scoreDelta, newScore, coop, opponentName } = pvpResult;
    const color = won ? "#22c55e" : "#dc2626";
    const icon  = won ? "✓" : "✗";
    const sign  = (scoreDelta || 0) >= 0 ? "+" : "";
    // A co-op story win is a story receipt, not a ladder one — no score
    // line, because no ladder moved (2026-09-06).
    if (coop) showToast(`${icon} STORY CO-OP · ${opponentName || "the boss"} falls · +${crests} ◕`, color);
    else showToast(`${icon} SOLREND ${won ? "WON" : "LOST"} · ${sign}${scoreDelta} score · ${newScore} total · +${crests} DC`, color);
    onClearPvpResult && onClearPvpResult();
  }, [pvpResult]);

  const handleNPCEnter = () => {
    const id = npcPrompt?.id;
    const label = npcPrompt?.label;
    const color = npcPrompt?.color;
    setNpcPrompt(null);
    if (id === "herald")         setShowWilly(true);
    else if (id === "bulletin")  setShowBoard(true);

    else if (id === "vyren")     setShowVyren(true);
    else showToast(`${label} speaks...`, color);
  };

  const handleShopEnter = () => {
    const id = shopPrompt?.id;
    setShopPrompt(null);
    if (id === "deck_forge")       onForge && onForge({ ...profile, uid, name: playerName });
    else if (id === "cheat_chest") onDeckBuilder && onDeckBuilder({ ...profile, uid, name: playerName });
    else if (id === "pack_shop")   onEnterShop && onEnterShop({ ...profile, uid, name: playerName });
    else if (id === "faction_hall") onFactionHall && onFactionHall({ ...profile, uid, name: playerName });
  };

  // ── CONSOLE TARGET LIST — every prompt in range, cycled with ◀ ▶ / Tab ──
  // Replaces the old one-at-a-time floating bubbles (portal > shop > npc >
  // pet precedence). All are now simultaneously reachable via cycling.
  const consoleTargets = useMemo(() => {
    // Same list, same reason — the console must not offer a prompt for
    // a tile the player cannot currently see.
    if (showMenu || showPet || showGallery || showDepths || showFountain) return [];
    const list = [];
    // Three stacked `id === "x" ? verb :` ladders used to live here,
    // one per lane, and every new object meant editing all three and
    // remembering the default at the bottom. `verb` is a field in
    // plazaZones.js now — the console just reads it.
    if (portalPrompt && !portalActive) list.push({
      id: `portal_${portalPrompt.id}`, kind: "portal",
      verb: portalPrompt.locked ? "Try" : (portalPrompt.verb || "Enter"),
      label: portalPrompt.label || portalPrompt.subtitle || "Portal",
      color: portalPrompt.color,
    });
    if (shopPrompt) list.push({
      id: `shop_${shopPrompt.id}`, kind: "shop",
      verb: shopPrompt.verb || "Enter",
      label: shopPrompt.label || "Shop",
      color: shopPrompt.color,
    });
    if (npcPrompt) list.push({
      id: `npc_${npcPrompt.id}`, kind: "npc",
      verb: npcPrompt.verb || "Talk",
      label: npcPrompt.label || "NPC",
      color: npcPrompt.color,
    });
    if (landmarkPrompt) list.push({
      id: `landmark_${landmarkPrompt.id}`, kind: "landmark",
      verb: landmarkPrompt.verb || "Use",
      label: landmarkPrompt.label || "Landmark",
      color: landmarkPrompt.color,
    });
    if (petPrompt) list.push({
      id: "pet", kind: "pet", verb: "🩷 Pet", label: "Companion", color: "#ff7fa3",
    });
    return list;
  // ⚠ SAME LAW AS THE TWO ABOVE, and this one is the worst of the three
  // to get wrong: a MEMO that reads a value it does not list can LATCH.
  // With the Gallery open it returns [] and, without these deps, never
  // recomputes when the Gallery closes — leaving the Plaza console
  // permanently empty until something else in the list happens to move.
  }, [showMenu, showPet, showGallery, showDepths, showFountain, portalPrompt, portalActive, shopPrompt, npcPrompt, petPrompt, landmarkPrompt]);

  // Reset the ◀ ▶ selection whenever the in-range set changes
  useEffect(() => {
    const ids = consoleTargets.map(t => t.id).join(",");
    if (ids !== consoleIdsRef.current) {
      consoleIdsRef.current = ids;
      setConsoleIdx(0);
    }
  }, [consoleTargets]);

  const cycleConsole = useCallback((dir) => {
    setConsoleIdx(i => {
      const n = consoleTargets.length;
      return n ? (i + dir + n) % n : 0;
    });
  }, [consoleTargets.length]);

  const activateConsoleTarget = useCallback((t) => {
    if (!t) return;
    if (t.kind === "portal") {
      const p = portalPrompt;
      if (!p) return;
      // A shut door speaks instead of opening. caP'n's line, verbatim.
      if (p.locked) { setLockedNote({ id: p.id, text: p.locked, color: p.color }); return; }
      // ROUTE, never id. App.js used to switch on `portal.id === "space"`
      // — a name left over from Dungeon 1's door, which is why Story
      // Mode's arch was secretly called "space". One data field ends it,
      // and the next portal is a table row instead of an App.js branch.
      switch (p.route) {
        case "pierre":
          setPortalPrompt(null);
          onPierre && onPierre({ ...profile, uid, name: playerName });
          break;
        case "faction":
          setPortalPrompt(null);
          onFactionHall && onFactionHall({ ...profile, uid, name: playerName });
          break;
        case "worldmap":
          setPortalPrompt(null);
          setPortalCinematicData({
            portal: p,
            dungeon: { name: p.label, subtitle: p.subtitle },
            profile,
          });
          break;
        default:
          if (p.readyUp) setPortalActive(true);
      }
    } else if (t.kind === "landmark") {
      // Clear the prompt before opening, exactly as every sibling
      // branch does. Leaving it set kept the Plaza console live behind
      // the overlay and eating keys.
      if (landmarkPrompt?.id === "fountain") { setLandmarkPrompt(null); setShowFountain(true); }
    } else if (t.kind === "shop") {
      handleShopEnter();
    } else if (t.kind === "npc") {
      handleNPCEnter();
    } else if (t.kind === "pet") {
      setPetPrompt(null); setShowPet(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalPrompt, profile, uid, playerName, shopPrompt, npcPrompt, landmarkPrompt]); // handlers close over prompt state

  const dismissConsoleTarget = useCallback((t) => {
    if (!t) return;
    if (t.kind === "portal") {
      setPortalPrompt(null); setPortalActive(false);
      remove(ref(db, `plaza/ready/${portalPrompt?.id}/${uid}`));
    }
    else if (t.kind === "shop")     setShopPrompt(null);
    else if (t.kind === "npc")      setNpcPrompt(null);
    else if (t.kind === "landmark") setLandmarkPrompt(null);
    else if (t.kind === "pet")      setPetPrompt(null);
  }, [portalPrompt, uid]);

  // Console keys — Q/Enter activate · Tab cycle · Esc/Backspace dismiss.
  // 400ms arming delay mirrors the old bubbles (the walk-up keypress
  // can't instantly activate). Listener only exists while targets exist.
  useEffect(() => {
    if (consoleTargets.length === 0) return;
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 400);
    const onKey = (e) => {
      if (!armed) return;
      if (e.key === "q" || e.key === "Q" || e.key === "Enter") {
        e.preventDefault();
        activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0]);
      } else if (e.key === "Tab" && consoleTargets.length > 1) {
        e.preventDefault(); cycleConsole(1);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        dismissConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(armTimer); window.removeEventListener("keydown", onKey); };
  }, [consoleTargets, consoleIdx, activateConsoleTarget, dismissConsoleTarget, cycleConsole]);

  // ⚔ THE CHALLENGE — see the note where its state used to live.
  const challenge = useChallenge({
    uid, playerName, zoneId: "plaza",
    resolveDeck: useCallback(() => resolveDeck(profile), [profile]),
    onEnterGame, showToast,
  });
  const { challengePrompt, incomingChallenge } = challenge;
  const handleSendChallenge    = challenge.sendChallenge;
  const handleAcceptChallenge  = challenge.acceptChallenge;
  const handleDeclineChallenge = challenge.declineChallenge;
  const setChallengePrompt     = challenge.dismissPrompt;

  // ⚠ THE MOVEMENT LOOP RUNS EVERY 50ms AND ITS DEPS DO NOT LIST THIS.
  // `checkProximity` is a stable useCallback, but the loop's effect is
  // declared ABOVE this line and adding a fifteenth dependency to it to
  // reach a function defined below would re-create the interval on
  // every change to any of them. A ref is written on every render and
  // read by the loop — one indirection, no re-subscription.
  const challengeRef = useRef(() => {});
  challengeRef.current = challenge.checkProximity;

  // ═══════════════════════════════════════════════════════════
  // THE FRONT DOOR
  // ═══════════════════════════════════════════════════════════
  // Rebuilt 2026-08-20 against 4xS, after auditing what the old one
  // could and could not reach. Six things were wrong and none of them
  // was cosmetic:
  //
  // 1 · IT LED WITH THE ONE MODE THAT IS TURNED OFF. "Battle →
  //     Multiplayer" was the first row a new player highlighted, and
  //     Terror is single-player for now by caP'n's own decree, with
  //     multiplayer paused. Story Mode — the actual game — sat second.
  //     The first row of a front door is a recommendation whether or
  //     not you meant it as one.
  //
  // 2 · FOUR SCREENS HAD NO MENU ENTRY AT ALL, reachable only by
  //     walking onto a specific tile: the Bulletin Board (which is
  //     where the tournament brackets and the whole Replay Shelf
  //     live), the Fountain, and — two levels deep behind a bubble on
  //     a wall — Willee's Library, the 48-book RECORD and the largest
  //     single piece of authored lore in Terror. A player who never
  //     happens to walk into the top-left corner of the Plaza never
  //     learns any of it exists.
  //
  //     ⚠ THE IN-WORLD DOORS ALL STAY. Finding the Library by
  //     noticing a door is strictly the better experience and none of
  //     it changed. This is the SECOND way in, for the player who
  //     already knows.
  //
  // 3 · TWO CATEGORIES EXISTED TO HOLD ONE ROW EACH. "Settings →
  //     Appearance" and "Log out → Confirm Log Out" each spent a
  //     keypress to reveal a single option. They are now one category
  //     with three rows, and the confirm survives, because the row
  //     that ends your session is still not the row your thumb lands
  //     on first.
  //
  // 4 · "COLLECTION" WAS A DRAWER, NOT A CATEGORY. It held the Codex
  //     and the Deck Builder, which are cards — and also the Faction
  //     Hall and the achievement gallery, which are not. Allegiance
  //     and renown are things you ARE. They sit together under
  //     STANDING now, and the split is legible without reading the
  //     labels.
  //
  // 5 · EVERY CATEGORY WAS NAMED IN GENERIC. Battle / Collection /
  //     Shop / Help / Settings is the menu of any game ever made.
  //     Terror has its own words for most of that and was not using
  //     them at its own front door. SOLREND is what a duel is called.
  //     THE MARKET is where you spend. SETTINGS stays SETTINGS,
  //     because a settings menu called anything else is a settings
  //     menu nobody can find.
  //
  // 6 · THE TOURNAMENT ROW APPEARS ONLY WHEN IT WORKS. SHOW_TOURNAMENTS
  //     is off by decree until the duel-start seam lands, so the row
  //     is built conditionally rather than shipped as a door onto an
  //     empty room. The day the flag flips it appears by itself.
  //
  // Every leaf still fires the exact opener its in-world prompt or
  // portal fires. Nothing below is a second implementation of
  // anything — that is the whole contract of this array, and it is
  // why re-ordering it is safe.
  const menuSections = [
    {
      // ⚔ THE FIGHTS. Story first: it is the game, it is what the
      // player came for, and it is the one mode that is finished.
      id: "solrend", label: "Solrend", icon: "⚔",
      items: [
        { id: "story",       label: "Story Mode",    run: () => onEnterDungeon && onEnterDungeon({ id: "story", route: "worldmap" }, { ...profile, uid, name: playerName }) },
        { id: "depths",      label: "The Gauntlet",  run: () => setShowDepths(true) },
        // Renamed from "Multiplayer", which was never what this row
        // opened. V'yen's stall is matchmaking — Free Play, Ranked,
        // and the code pad for a friend. Calling it Multiplayer
        // promised a paused feature and hid three working ones.
        { id: "matchmaking", label: "V'yen's Stall", run: () => setShowVyren(true) },
        ...(SHOW_TOURNAMENTS ? [
          { id: "brackets",  label: "Tournaments",   run: () => setShowBoard(true) },
        ] : []),
      ],
    },
    {
      // 🗃 THE CARDS, and the three rooms that are about cards.
      id: "cards", label: "Cards", icon: "🗃",
      items: [
        { id: "decks",  label: "Deck Builder", run: () => onDeckBuilder && onDeckBuilder({ ...profile, uid, name: playerName }) },
        { id: "forge",  label: "The Forge",    run: () => onForge && onForge({ ...profile, uid, name: playerName }) },
        { id: "codex",  label: "Codex",        run: () => onCollection && onCollection({ ...profile, uid, name: playerName }) },
        // THE RECORD — Willee's Library, beneath the Workshop.
        { id: "record", label: "The Record",   run: () => onLibrary && onLibrary({ ...profile, uid, name: playerName }) },
      ],
    },
    {
      // ✦ WHERE THE MONEY GOES. All three are a counter with somebody
      // behind it, which is why the Fountain belongs here and not
      // under a landmark heading of its own.
      id: "market", label: "The Market", icon: "✦",
      items: [
        { id: "packs",    label: "Booster Packs", run: () => onEnterShop && onEnterShop({ ...profile, uid, name: playerName }) },
        { id: "pier",     label: "W'ily's Pier",  run: () => onPierre && onPierre({ ...profile, uid, name: playerName }) },
        { id: "fountain", label: "The Fountain",  run: () => setShowFountain(true) },
        // THE EXCHANGE POST (2026-09-06) — trade cards with a friend, or
        // sell dupes to the house through the Scrap Lane.
        { id: "trading",  label: "The Exchange Post", run: () => onTrading && onTrading({ ...profile, uid, name: playerName }) },
      ],
    },
    {
      // ◈ WHO YOU ARE. Not what you own — what you have done, what you
      // swore to, and what the Plaza is saying about it.
      id: "standing", label: "Standing", icon: "◈",
      items: [
        { id: "deeds",    label: "Deeds & Renown",     run: () => setShowGallery(true) },
        // Its in-world name. The portal on the shelf says "Hall of
        // Allegiance"; the menu used to say "Faction Hall", and a
        // player who read both had no way to know they were one room.
        { id: "faction",  label: "Hall of Allegiance", run: () => onFactionHall && onFactionHall({ ...profile, uid, name: playerName }) },
        { id: "bulletin", label: "Bulletin Board",     run: () => setShowBoard(true) },
      ],
    },
    {
      id: "help", label: "How to Play", icon: "📖",
      items: [
        { id: "howto",    label: "The Rulebook", run: () => onRulebook && onRulebook({ ...profile, uid, name: playerName }) },
        { id: "tutorial", label: "Tutorial",     run: () => onTutorial1 && onTutorial1({ ...profile, uid, name: playerName }) },
      ],
    },
    // ♪ Same builder RoomOverlay uses, so the mute in the Plaza and
    // the mute in the Forge can never disagree. NOT folded into
    // Settings, for exactly that reason: it is a shared contract with
    // another screen and inlining it here would fork it.
    soundMenuSection(),
    {
      // ⚙ SETTINGS, and it has more than one thing in it now.
      id: "settings", label: "Settings", icon: "⚙",
      items: [
        { id: "appearance", label: "Appearance", run: () => setShowGenderSettings(true) },
        // MOTION had a live setter (strikeLanguage.setMotion) and a
        // label table, and the only door onto it was inside the 3D
        // Shatterfield — so a player who found the animation too busy
        // in the PLAZA had to enter a duel to turn it down. Cycles in
        // place, same pattern as the Volume row above it.
        {
          id: "motion",
          label: `Motion: ${MOTION_LABELS[getMotion()] || getMotion()}`,
          run: () => {
            const i = MOTION_ORDER.indexOf(getMotion());
            setMotion(MOTION_ORDER[(i + 1) % MOTION_ORDER.length]);
          },
        },
        // Log out keeps its place at the BOTTOM of the LAST category
        // (caP'n, July 8 — off the HUD, into the front door, where
        // session-level actions belong). It used to be a category of
        // its own holding one row called "Confirm Log Out"; the drill
        // WAS the confirm. Being last, under Settings, behind two
        // keypresses, is the same protection without the empty room.
        // ⭐ THE DEBUGGER GETS A DOOR — in dev builds only.
        // It has had a router branch (`screen === "debugger"`) and no
        // caller anywhere for the entire life of the project: 56KB of
        // rule simulator, the WORLD GRAPH audit and the rule-coverage
        // report, all of it unreachable without editing App.js by hand.
        //
        // Gated on IS_DEV_BUILD rather than hidden behind a key combo,
        // because a secret door is a door people forget exists, and
        // this one has just been proved to be the thing that catches
        // the map falling apart. Same signal the Surveyor uses, so
        // there is exactly one idea of "am I in development".
        ...(IS_DEV_BUILD ? [
          { id: "debugger", label: "◆ Debugger (dev)", run: () => onDebugger && onDebugger({ ...profile, uid, name: playerName }) },
        ] : []),
        { id: "logout", label: "⊗  Log Out", run: () => handleLogout() },
      ],
    },
  ];

  const allPlayers = [
    { sid: sessionId.current, data: { name: playerName, x: pos.x, y: pos.y, facingX: facing.x, facingY: facing.y, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null }, isLocal: true, colorIndex: 0 },
    ...Object.entries(players).map(([sid, data], i) => ({ sid, data, isLocal: false, colorIndex: i+1 })),
  ];

  // Motion OFF holds the Plaza on frame one — a still room, not a
  // stuttering one. QUICK keeps the full one-second beat: this is
  // ambience, not a strike, and halving it would read as a flicker.
  const plazaCycling = getMotion() !== MOTION.OFF;

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "transparent", fontFamily: GM.font, overflow: "hidden" }}>
      {/* LETTERBOX BACKDROP — equipped in the Backdrop Atelier */}
      <RoomBackdrop uid={uid} />
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes bubbleFloat { from{transform:translateX(-50%) translateY(0px)} to{transform:translateX(-50%) translateY(-5px)} }
        @keyframes fightPulse { from{opacity:0.7;transform:scale(1)} to{opacity:1;transform:scale(1.04)} }
      `}</style>

      {/* HUD — desktop: a compact pill cluster top-LEFT (controls) plus
          a title/crest/count pill docked at the TOP of the right letterbox
          column (caP'n, July 8 — chrome lives in the vertical band now, no
          more full-width veil eating the top of the screen). Mobile keeps
          its slim full-width bar. */}
      {!mobile ? (
        <>
          {/* Controls — top-left. ONLY Exit Plaza now: Log out moved to
              the main menu's footer, gender to Settings → Appearance. */}
          <div style={{ position: "absolute", top: 12, left: 14, zIndex: 55,
            display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={onBack} style={{ background: OBSIDIAN_SLAB, border: "none",
              clipPath: facetTB(6),
              boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
              color: GM.silver, padding: "5px 12px", textShadow: TEXT_SET,
              fontFamily: GM.font, letterSpacing: 1,
              fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
              {/* Not "Exit Plaza" — there is no main menu behind it.
                  App.js's `screen === "menu"` renders BootSequence, so
                  this button replays the opening cinematic. Named for
                  what it does. */}
              ← Title Screen
            </button>
          </div>

          {/* Title + crest + player-count — a pill in the right column,
              docked BELOW the chrome rail (MENU + chat bubble sit above
              it). Same 24vw band as the console. */}
          <div style={{ position: "absolute", top: 112, right: 12, zIndex: 55,
            width: "min(280px, 24vw)",
            // folds with the band — must come AFTER `display: "flex"`, or
            // flex (last key wins) silently un-hides it. That happened once.
            display: railFolded ? "none" : "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, padding: "6px 14px",
            background: "linear-gradient(175deg, rgba(5,3,12,0.95) 0%, rgba(13,9,24,0.92) 100%)",
            border: `1px solid ${GM.pageBorder}`, borderRadius: 999,
            boxShadow: "0 8px 26px rgba(0,0,0,0.65), inset 0 1px 0 rgba(226,232,244,0.08)" }}>
            <span style={{ fontSize: 12, color: "#f0cc60", fontWeight: 700,
              letterSpacing: 2.5, fontFamily: GM.font, whiteSpace: "nowrap",
              textShadow: textGlow(GM.inkGoldBright) }}>◆ THE PLAZA</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {/* ⬡ THE 3D SWITCH — same button the Shatterfield wears */}
              <button onClick={togglePlaza3D} title="Toggle the 3D Plaza" style={{
                padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                background: plaza3D ? `linear-gradient(135deg, ${GM.inkGold}2a, transparent)` : "transparent",
                border: `1px solid ${plaza3D ? GM.inkGold : GM.inkGold + "44"}`,
                color: plaza3D ? GM.inkGoldBright : GM.vellumDim,
                fontFamily: GM.font, fontSize: 9.5, letterSpacing: 1.5, fontWeight: 700,
              }}>{plaza3D ? "⬡ 3D" : "▤ 2D"}</button>
              <Wallet profile={profile} />
              <div style={{ display: "flex", alignItems: "center", gap: 4,
                background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`,
                borderRadius: 20, padding: "2px 8px",
                boxShadow: "inset 0 2px 6px rgba(0,0,0,0.7)",
                fontSize: 9.5, fontWeight: 700, fontFamily: GM.fontUI,
                color: GM.vellumDim, textShadow: TEXT_SET }}>
                <span style={{ color: "#22c55e", textShadow: "0 0 6px #22c55e" }}>●</span>
                {Object.keys(players).length + 1}/4
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 50,
          padding: "6px 8px",
          display: "flex", alignItems: "center", gap: 6,
          background: "linear-gradient(180deg, rgba(7,4,16,0.94) 0%, rgba(7,4,16,0.5) 60%, transparent 100%)" }}>
          <div style={{ position: "absolute", left: "6%", right: "6%", bottom: 0, height: 1, background: GOLD_METAL_H, opacity: 0.35, pointerEvents: "none" }} />
          <button onClick={onBack} style={{ background: OBSIDIAN_SLAB, border: "none",
            clipPath: facetTB(6),
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
            filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
            color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
            fontFamily: GM.font, letterSpacing: 1,
            fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}>
            ← Exit
          </button>
          {playerName && (
            <button aria-label="Log out" onClick={handleLogout} style={{ background: OBSIDIAN_SLAB, border: "none",
              clipPath: facetTB(6),
              boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
              color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
              fontFamily: GM.font, letterSpacing: 1,
              fontSize: 10, cursor: "pointer" }}>
              ⊗
            </button>
          )}
          {uid && playerName && !needsStarterPick && !needsOnboarding && (
            <button onClick={() => setShowGenderSettings(true)}
              style={{ background: OBSIDIAN_SLAB, border: "none",
                clipPath: facetTB(6),
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
                filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
                color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
                fontFamily: GM.font,
                fontSize: 10, cursor: "pointer" }}>
              {gender === "f" ? "♀" : "♂"}
            </button>
          )}
          <div style={{ flex: 1, textAlign: "center" }}>
            <span style={{ fontSize: 10, color: "#f0cc60", fontWeight: 700,
              letterSpacing: 2, fontFamily: GM.font,
              textShadow: textGlow(GM.inkGoldBright) }}>◆ THE PLAZA</span>
          </div>
          <button onClick={togglePlaza3D} aria-label="Toggle the 3D Plaza" style={{
            padding: "3px 7px", borderRadius: 4, cursor: "pointer",
            background: plaza3D ? `linear-gradient(135deg, ${GM.inkGold}2a, transparent)` : "transparent",
            border: `1px solid ${plaza3D ? GM.inkGold : GM.inkGold + "44"}`,
            color: plaza3D ? GM.inkGoldBright : GM.vellumDim,
            fontFamily: GM.font, fontSize: 9, letterSpacing: 1, fontWeight: 700,
          }}>{plaza3D ? "⬡ 3D" : "▤ 2D"}</button>
          <Wallet profile={profile} compact />
          <div style={{ display: "flex", alignItems: "center", gap: 5,
            background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`,
            borderRadius: 20, padding: "3px 10px",
            boxShadow: "inset 0 2px 6px rgba(0,0,0,0.7)",
            fontSize: 10, fontWeight: 700, fontFamily: GM.fontUI,
            color: GM.vellumDim, textShadow: TEXT_SET }}>
            <span style={{ color: "#22c55e", textShadow: "0 0 6px #22c55e" }}>●</span>
            {Object.keys(players).length + 1}/4
          </div>
        </div>
      )}

      {/* MAP — in portrait, clipped to the TOP world zone; the bottom
          becomes the reserved dock (rendered after this block). On
          desktop/landscape the container's RIGHT edge stops short of the
          backdrop band (RECORD_RESERVE) so the equipped RoomBackdrop shows
          through there and frames the zoomed-in map — same reserve the
          camera anchors to, so window and character stay in lock-step. */}
      <div style={{ position:"absolute",
        left:0,
        // Band folded + 3D → the canvas takes the full width; there is no band
        // left to reserve. 2D keeps its reserve (tile math — the 2D/3D pass).
        right: (portrait || (plaza3D && railFolded)) ? 0 : RECORD_RESERVE(),
        top:0,
        bottom: portrait ? "auto" : 0,
        height: portrait ? getViewportH() : "auto",
        overflow:"hidden",
        cursor: plaza3D ? "grab" : (mobile ? "default" : "none"), background:"transparent" }}>
        {portrait && (
          <div style={{ position:"absolute", left:0, right:0, bottom:0, height:20,
            pointerEvents:"none", zIndex:6,
            background:"linear-gradient(180deg, transparent, #050308)" }} />
        )}
        {/* ── PLAZA 3D — the switch. ON: the same table, the same refs,
            painted in three dimensions by a pure renderer; the painted
            world div below is not mounted at all. OFF: the room exactly
            as it shipped. The rAF loop above keeps running either way —
            it writes to refs that are simply null in 3D. ── */}
        {plaza3D && uid && playerName && (
          <Plaza3D
            world={world3D}
            posRef={visualPosRef}
            facingRef={facingRef}
            gender={gender}
            companionId={myCompanionId || null}
            playerName={playerName}
            title={getEquippedTitle(profile?.equipped)}
            radiantTier={myCompanionRadiant || null}
            compOffsetRef={compOffsetRef}
            othersRef={othersRef}
            rosterKey={rosterKey}
            floaties={chat.floaties}
            mobile={mobile}
          />
        )}
        {!plaza3D && <div ref={worldRef} style={{
          position: "absolute",
          left: 0,
          top: 0,
          width:  activeCOLS * TILE,
          height: activeROWS * TILE,
          transform: `translate3d(${-(camOffset?.x || 0)}px, ${-(camOffset?.y || 0)}px, 0)`,
          willChange: "transform",
          transition: "none",
        }}>

          {PLAZA_BG
            ? PLAZA_FRAMES.map((src, i) => (
                <img key={src} src={src}
                  alt={i === 0 ? "plaza" : ""} aria-hidden={i !== 0}
                  decoding="async"
                  onError={e => e.target.style.display="none"}
                  style={{ position:"absolute", left:0, top:0,
                    width: activeCOLS * TILE,
                    height: activeROWS * TILE,
                    imageRendering:"pixelated", objectFit:"fill",
                    zIndex:0, pointerEvents:"none",
                    // Frame 1 is the floor and is always lit. Frames 2/3
                    // sit dark on top and light up on their beats — which
                    // is also the base state when the dial reads OFF.
                    opacity: i === 0 ? 1 : 0,
                    animation: (plazaCycling && i > 0)
                      ? `plazaCycle${i + 1} 4s step-end infinite`
                      : "none",
                  }} />
              ))
            : <div style={{ position:"absolute", inset:0, background:`
                repeating-linear-gradient(0deg, transparent, transparent ${TILE-1}px, #1a154008 ${TILE}px),
                repeating-linear-gradient(90deg, transparent, transparent ${TILE-1}px, #1a154008 ${TILE}px),
                linear-gradient(135deg, #06041a, #030212)` }} />
          }

          {/* ⭐ J'YNXI — frequently, in the background, never
              addressed. Inside the world div so she rides the camera
              with everything else; behind the players by zIndex. */}
          {/* ⭐ THE AMBIENT J'YNXI IS OUT. caP'n, 2026-08-19: "I guess I
              couldn't see her because the Plaza asset was in the way xD
              — yeah, sadly, let's take her out for now regardless."

              The art beat her. She stands BEHIND the painted stalls at
              zIndex 2 and 62% opacity, which is exactly what "in the
              background" was asked for, and the consequence is that on
              the real Plaza art she is not visible at all. A presence
              nobody can see is not subtle, it is absent — with a timer
              attached. So the mount comes out and the component stays:
              PlazaLurker, LURKER_SPOTS and pickLurkerSpot are all still
              above, unchanged and byte-for-byte what shipped.

              "FOR NOW" is caP'n's word and it is the whole reason this
              is a comment and not a delete. Restoring her is deleting
              this block. What she actually needs before that is worth
              writing down while it is fresh: either a zIndex above the
              painted furniture, or LURKER_SPOTS re-pinned to fractions
              that are open floor on the CURRENT painting rather than on
              the grid.

              ⚠ THE OTHER J'YNXI IS NOT THIS ONE AND MUST NOT FOLLOW IT
              OUT. NpcStage's `lurker="jynxi"` in V'yen's window is a
              different presence entirely, it is canon in the SPINE
              (beat 11 — "eventually always in the background of V'yen's
              Sweet Treats"), and it renders on a plain backdrop where
              she is actually visible. It stays. */}
          {/* <PlazaLurker tile={TILE} cols={activeCOLS} rows={activeROWS} portrait={portrait} /> */}

          {/* ── ZONE DEBUG OVERLAY ─────────────────────────────────
              Flip SHOW_ZONES to true to draw the collision table
              straight over the art: red is solid, cyan is an interact
              area. The boxes were authored by eye against a painting,
              so the fix for "the tent's edge is a tile short" is to
              LOOK at it, not to walk into it forty times. Replaced
              three sets of empty <div>s that rendered nothing and
              handled nothing — the real hit-testing has always been
              in the movement loop, never in the DOM. */}
          {SHOW_ZONES && activeZONES.flatMap(z => [
            ...(z.solid || []).map(([x0,y0,x1,y1], i) => (
              <div key={`dbg_s_${z.id}_${i}`} style={{ position:"absolute", zIndex:6, pointerEvents:"none",
                left:x0*TILE, top:y0*TILE, width:(x1-x0+1)*TILE, height:(y1-y0+1)*TILE,
                background:"rgba(248,113,113,0.22)", border:"1px solid rgba(248,113,113,0.85)" }} />
            )),
            ...(z.zone || []).map(([x0,y0,x1,y1], i) => (
              <div key={`dbg_z_${z.id}_${i}`} style={{ position:"absolute", zIndex:6, pointerEvents:"none",
                left:x0*TILE, top:y0*TILE, width:(x1-x0+1)*TILE, height:(y1-y0+1)*TILE,
                background:"rgba(34,211,238,0.14)", border:"1px dashed rgba(34,211,238,0.8)" }} />
            )),
          ])}

          {/* Players */}
          {allPlayers.map(({ sid, data, isLocal, colorIndex }) => (
            <PlayerEntity
              key={sid}
              player={data}
              isLocal={isLocal}
              colorIndex={colorIndex}
              companionId={data.companionId}
              equipped={data.equipped}
              tile={TILE}
              localSpriteRef={isLocal ? localSpriteRef : undefined}
              localCompanionRef={isLocal ? localCompanionRef : undefined}
              compDir={isLocal ? compOffset : undefined}
              radiantTier={isLocal ? (myCompanionRadiant || null) : undefined}
            />
          ))}

          {/* Floating proximity bubbles EXCISED (July 8) — every prompt
              now flows through the RoomConsole (rendered outside the map):
              persistent session log + ◀ ▶ / Tab cycling across ALL prompts
              in range simultaneously (old precedence chain retired). */}

        </div>}
        {/* The Solrend pill used to live INSIDE the world div. DockPortal
            portals it into the dock in portrait and it is position:fixed
            on desktop, so its place in the tree never mattered — it sits
            here now so it exists in BOTH renderers. Byte-identical JSX. */}
          {/* ── SOLREND CHALLENGE PILL ── */}
          {challengePrompt && !portalPrompt && !shopPrompt && !npcPrompt && (
            <DockPortal align="top">
            <div style={{
              position: portrait ? "relative" : "fixed",
              left: portrait ? undefined : "50%",
              bottom: portrait ? undefined : (mobile ? 120 : 80),
              transform: portrait ? "none" : "translateX(-50%)",
              zIndex: 60, display: "flex", gap: 8, alignItems: "center",
            }}>
              <button onClick={handleSendChallenge} style={{
                padding: "10px 24px", borderRadius: 24,
                background: `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`,
                border: `1px solid ${GM.inkGold}`,
                color: GM.vellum, fontFamily: GM.font,
                fontSize: 13, fontWeight: 900, letterSpacing: 2,
                cursor: "pointer", animation: "fightPulse 1s ease-in-out infinite alternate",
                boxShadow: `0 0 20px ${GM.inkGold}66`,
              }}>
                ⚔ Solrend · {challengePrompt.targetName}
              </button>
              <button aria-label="Dismiss" onClick={() => setChallengePrompt(null)} style={{
                padding: "8px 14px", borderRadius: 20,
                background: "transparent", border: `1px solid ${GM.inkBrown}66`,
                color: GM.vellumFaint, fontFamily: GM.font, fontSize: 11,
                cursor: "pointer",
              }}>✕</button>
            </div>
            </DockPortal>
          )}
      </div>

      {/* ── PORTRAIT DOCK ── reserved bottom half on a phone.
          Holds the d-pad and is the landing zone for popups (pet
          close-up, etc.) via portrait-aware placement below. On
          desktop/landscape this renders nothing. */}
      {portrait && (
        <div id="ie-dock" style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          height: window.innerHeight - getViewportH(),
          zIndex: 40, overflow: "hidden",
          background: "linear-gradient(180deg, #050308 0%, #07050f 100%)",
          borderTop: `1px solid ${GM.inkGold}2e`,
          boxShadow: "inset 0 14px 28px rgba(0,0,0,0.55)",
        }}>
          {/* faint manuscript ruling so empty dock isn't dead space */}
          <div style={{ position:"absolute", inset:0, pointerEvents:"none", opacity:0.5,
            background:"repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(122,109,156,0.07) 28px)" }} />
        </div>
      )}
      {/* ── INCOMING CHALLENGE MODAL ── */}
      {incomingChallenge && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
        }}>
          <div style={{
            background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
            border: `1px solid ${GM.inkGold}`,
            borderRadius: 16, padding: "32px 40px",
            textAlign: "center", fontFamily: GM.font,
            boxShadow: `0 0 40px ${GM.inkGold}44`,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 20,
          }}>
            <div style={{ fontSize: 11, color: GM.inkGold, letterSpacing: 3 }}>INCOMING CHALLENGE</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellum, fontStyle: "italic" }}>
              ⚔ {incomingChallenge.fromName}
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint }}>challenges you to a Solrend</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={handleAcceptChallenge} style={{
                padding: "12px 28px", borderRadius: 12,
                background: `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`,
                border: `1px solid ${GM.inkGold}`,
                color: GM.vellum, fontFamily: GM.font,
                fontSize: 13, fontWeight: 900, cursor: "pointer",
                boxShadow: `0 0 16px ${GM.inkGold}44`,
              }}>Accept</button>
              <button onClick={handleDeclineChallenge} style={{
                padding: "12px 28px", borderRadius: 12,
                background: "transparent",
                border: `1px solid ${GM.inkBrown}66`,
                color: GM.vellumFaint, fontFamily: GM.font,
                fontSize: 13, cursor: "pointer",
              }}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Controls hint — desktop only */}
      {!mobile && (
        <div style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)",
          fontSize:9, color:GM.vellumFaint, letterSpacing:2, zIndex:50, fontFamily: GM.fontUI, fontWeight: 600,
          background:"rgba(7,4,16,0.72)", border:`1px solid ${GM.pageBorder}`,
          boxShadow:"inset 0 2px 6px rgba(0,0,0,0.6)", textShadow: TEXT_SET,
          padding:"4px 14px", borderRadius:12 }}>
          WASD / ARROW KEYS TO MOVE
        </div>
      )}

      {/* Mobile D-pad — suppressed while the pet close-up owns the dock
          in portrait so it doesn't float over the codex card. */}
      {mobile && !(portrait && showPet) && <MobileDpad keysRef={keysRef} />}

      {/* START — opens the unified menu (also bound to the M key).
          Desktop: lives at the TOP of the right-column CHROME RAIL (caP'n,
          July 8 — menu + chat live in the backdrop band now). Mobile:
          stays bottom-left for thumb reach. Suppressed while the pet
          close-up owns the dock in portrait. */}
      {uid && playerName && !needsStarterPick && !needsOnboarding && authReady && !showMenu && !(portrait && showPet) && !railFolded && (
        <button
          onClick={() => setShowMenu(true)}
          aria-label="Open menu"
          style={{
            position: "fixed",
            ...(mobile
              ? { left: 16, bottom: 26 }
              : { right: 12, top: 12, width: "min(280px, 24vw)", justifyContent: "center" }),
            zIndex: 70,
            display: "flex", alignItems: "center", gap: 6,
            padding: mobile ? "11px 18px" : "9px 18px",
            background: OBSIDIAN_SLAB,
            border: "none",
            borderTop: "1px solid #f5d878",
            clipPath: facetTB(9),
            color: "#ffe9a8", fontFamily: GM.font,
            fontSize: 12, fontWeight: 700, letterSpacing: 2,
            cursor: "pointer",
            boxShadow: `inset 0 1px 0 rgba(245,216,120,0.5), inset 0 -6px 14px rgba(0,0,0,0.45), inset 0 0 16px ${GM.amethyst}22`,
            filter: `drop-shadow(0 4px 10px rgba(0,0,0,0.65)) drop-shadow(0 0 9px ${GM.inkGoldBright}44)`,
            textShadow: textGlow(GM.inkGoldBright),
            userSelect: "none", WebkitUserSelect: "none",
          }}
        >
          {mobile && <span style={{ position: "absolute", left: 0, top: "16%", bottom: "16%", width: 3, background: GOLD_METAL, boxShadow: `0 0 7px ${GM.inkGoldBright}cc` }} />}
          ☰ MENU
        </button>
      )}

      {/* START MENU — Pokémon-style nested front door to every interactable */}
      {showMenu && (
        <StartMenu sections={menuSections} onClose={() => setShowMenu(false)} />
      )}

      {/* THE RAIL — fold button (row, far left) and the folded, draggable
          MENU pill. Both render nothing off the rail (mobile/portrait). */}
      {uid && playerName && authReady && !needsStarterPick && !needsOnboarding && !showMenu && <RailFoldButton />}
      {uid && playerName && authReady && !needsStarterPick && !needsOnboarding && !showMenu && <RailMenuPill />}

      {/* COMPANION PETTING — hearts + codex close-up.
          In portrait it docks into the reserved bottom zone (DockPortal);
          on desktop/landscape it's a normal centered overlay. */}
      {showPet && (
        <DockPortal>
          <CompanionCloseup
            companionId={myCompanionId}
            radiantTier={myCompanionRadiant || null}
            profile={profile}
            docked={portrait}
            /* ⭐ PETTING COUNTS NOW. This is the one and only mount of
               CompanionCloseup in the codebase and it passed no onPet,
               so the component counted taps into local state, offered
               the callback, and threw the number away on close.
               progression.recordCompanionPet was written for exactly
               this — its `sessionPets` argument is literally "the tap
               count this session (from CompanionInteraction state)" —
               and had zero callers, which made terror:pet_100 and
               terror:pet_500 unreachable, and with them the Menagerie
               title family that hangs off them.

               Flushed on CLOSE with the session total rather than on
               every tap, which is what the argument's name asks for and
               what keeps a child hammering a cat from writing to
               Firebase forty times a second. */
            onPet={(n) => { petCountRef.current = n; }}
            onClose={() => {
              const pets = petCountRef.current || 0;
              petCountRef.current = 0;
              if (pets > 0 && uid) {
                import("./progression")
                  .then(({ recordCompanionPet }) => recordCompanionPet(uid, pets))
                  .catch(() => { /* non-fatal — a badge must never cost a cuddle */ });
              }
              setShowPet(false);
              // If we're still facing the companion, re-offer the Pet prompt.
              const co = compOffsetRef.current, f = facingRef.current;
              if (myCompanionId && co.x === f.x && co.y === f.y) {
                setPetPrompt({ x: posRef.current.x + co.x, y: posRef.current.y + co.y });
              }
            }} />
        </DockPortal>
      )}

      {/* Chat — only when logged in and settled. Desktop: docked into the
          right-column CHROME RAIL, just under MENU. Mobile: bottom-left. */}
      {uid && playerName && !needsStarterPick && !needsOnboarding && authReady && (
        <ChatSystem {...chat} floaties={plaza3D ? [] : chat.floaties} tile={TILE} camOffset={camOffset || { x: 0, y: 0 }}
          togglePos={mobile ? { left: 16, bottom: 80 } : { right: 12, top: 60 }} railed={!portrait} />
      )}

      {/* LOGIN */}
      {authReady && !playerName && <LoginOverlay onLogin={handleLogin} />}

      {/* ONBOARDING — first-login welcome + gender + Ilk + starter deck */}
      {authReady && uid && playerName && needsOnboarding && (
        // ⭐ devSkipEnabled={false} — SKIP INTRO IS A DEV BUTTON AND IT
        // WAS SHIPPING. Onboarding.js declares `devSkipEnabled = true`
        // as its DEFAULT and its own comment claims the button is
        // "hidden in production via devSkipEnabled prop" — nothing hid
        // it. The prop appears in exactly three places in src/, all
        // three inside Onboarding.js, and no caller ever passed it. So
        // every brand-new player's first interactive screen carried a
        // button, top right, that skipped the entire authored intro —
        // and handleSkip then silently assigns STARTER_DECKS[0] and
        // gender "m", so they are handed somebody else's deck with no
        // sign a choice was taken from them.
        //
        // Passed HERE rather than flipping the default, deliberately:
        // this is the one production mount, and a dev who wants the
        // button back edits this line and can see exactly what it does.
        <Onboarding uid={uid} playerName={playerName} devSkipEnabled={false} onComplete={handleOnboardingComplete} />
      )}

      {/* STARTER PICK — fallback for profiles that completed onboarding
          without picking a deck (edge case). New users go through Onboarding. */}
      {authReady && uid && playerName && !needsOnboarding && needsStarterPick && (
        <StarterPickerOverlay playerName={playerName} uid={uid} onPick={handleStarterPicked} />
      )}

      {/* GENDER SETTINGS */}
      {showGenderSettings && (
        <GenderSettings uid={uid} currentGender={gender}
          onClose={() => setShowGenderSettings(false)}
          onChanged={(g) => setGender(g)} />
      )}

      {/* VYREN KIOSK */}
      {showVyren && (
        <VyrenKiosk playerName={playerName} profile={{ ...profile, uid }}
          autoJoinCode={pendingInvite}
          onAutoJoinConsumed={() => { setPendingInvite(null); onInviteConsumed && onInviteConsumed(); }}
          onClose={() => { setShowVyren(false); setPendingInvite(null); onInviteConsumed && onInviteConsumed(); }}
          onEnterGame={(code, idx, name) => { setShowVyren(false); onEnterGame && onEnterGame(code, idx, name); }}
          onEnterNPC={(name, deck, prophecy, hand, diff, meta) => { setShowVyren(false); onEnterNPC && onEnterNPC(name, deck, prophecy, hand, diff, meta); }}
          onGauntlet={() => { setShowVyren(false); setShowDepths(true); }} />
      )}

      {/* BULLETIN BOARD */}
      {showBoard && (
        <BulletinBoard profile={{ ...profile, uid }} playerName={playerName} onClose={() => setShowBoard(false)}
          onCollection={(tab) => { setShowBoard(false); onCollection && onCollection({ ...profile, uid, name: playerName, initialTab: tab }); }}
          onEnterGame={(code, idx, name) => { setShowBoard(false); onEnterGame && onEnterGame(code, idx, name); }} />
      )}

      {showHub && (
        <PlazaHub
          profile={{ ...profile, uid, name: playerName }}
          logLine={(entry) => addPlazaLog(entry)}
          section={hubSection}
          onClose={() => { setShowHub(false); setHubSection(null); }}
          onOpenCodex={() => {
            setShowHub(false);
            onCollection && onCollection({ ...profile, uid, name: playerName });
          }}
          onForge={() => {
            setShowHub(false);
            onForge && onForge({ ...profile, uid, name: playerName });
          }}
        />
      )}

      {/* ROOM CONSOLE — prompts + the persistent session record.
          Desktop: vertical panel in the right letterbox bar.
          Portrait: lands in the reserved dock via DockPortal. */}
      {!showMenu && !showPet && (consoleTargets.length > 0 || plazaLog.length > 0) && (
        portrait ? (
          <DockPortal align="top">
            <RoomConsole inline zoneId="plaza"
              log={plazaLog}
              nearbyList={consoleTargets}
              nearbyIndex={consoleIdx}
              onCycle={cycleConsole}
              onActivate={() => activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0])}
            />
          </DockPortal>
        ) : (
          <RoomConsole
            zoneId="plaza"
            topOffset={156}
            log={plazaLog}
            nearbyList={consoleTargets}
            nearbyIndex={consoleIdx}
            onCycle={cycleConsole}
            onActivate={() => activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0])}
          />
        )
      )}

      {/* WILLY */}
      {showWilly && (
        <WillyDialogue profile={profile} uid={uid} showToast={showToast}
          logLine={(text) => addPlazaLog({ label: "Willy", color: GM.inkGold, text })}
          onClose={() => setShowWilly(false)}
          onTutorial1={() => { setShowWilly(false); onTutorial1 && onTutorial1({ ...profile, uid, name: playerName }); }}
          onRulebook={() => { setShowWilly(false); onRulebook && onRulebook({ ...profile, uid, name: playerName }); }} />
      )}

      {/* ── THE FOUNTAIN ── */}
      {showFountain && (
        <FountainOverlay uid={uid} onClose={() => setShowFountain(false)} />
      )}

      {/* ── A SHUT DOOR SPEAKING ──────────────────────────────────
          The purple and red arches are lit and painted but lead
          nowhere yet. They say so in character rather than being
          dark or, worse, silently doing nothing when pressed. */}
      {lockedNote && (
        <div onClick={() => setLockedNote(null)}
          style={{ position:"fixed", inset:0, zIndex:390, display:"flex",
            alignItems:"center", justifyContent:"center", padding:20,
            background:"rgba(4,2,10,0.78)", backdropFilter:"blur(2px)" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ maxWidth:400, textAlign:"center", padding:"26px 26px 22px",
              borderRadius:14, background:OBSIDIAN_SLAB,
              border:`1px solid ${lockedNote.color}`,
              boxShadow:`0 0 32px ${lockedNote.color}44, 0 18px 50px rgba(0,0,0,0.8)` }}>
            <div style={{ fontFamily:GM.font, fontSize:10, letterSpacing:5,
              color:lockedNote.color, textShadow:textGlow(lockedNote.color) }}>◈ SEALED</div>
            <div style={{ fontFamily:GM.fontBody, fontSize:18, lineHeight:1.5,
              color:GM.vellum, textShadow:TEXT_SET, margin:"16px 0 22px" }}>
              {lockedNote.text}
            </div>
            <button onClick={() => setLockedNote(null)}
              style={{ padding:"10px 22px", borderRadius:999,
                background:"rgba(20,12,38,0.9)", border:`1px solid ${lockedNote.color}`,
                color:lockedNote.color, fontFamily:GM.font, fontSize:12, letterSpacing:2,
                cursor:"pointer", textShadow:textGlow(lockedNote.color) }}>
              Step Back
            </button>
          </div>
        </div>
      )}

      {/* PORTAL READY-UP */}
      {portalPrompt && portalActive && portalPrompt.id !== "inner" && (
        <PortalReadyUp portal={portalPrompt} players={players} localPlayerName={playerName} localUid={uid}
          readySet={readySet}
          onReady={() => set(ref(db, `plaza/ready/${portalPrompt.id}/${uid}`), true)}
          onCancel={() => { remove(ref(db, `plaza/ready/${portalPrompt.id}/${uid}`)); setPortalPrompt(null); setPortalActive(false); }} />
      )}

      {/* INNER EMPERIUM */}
      {portalPrompt && portalActive && portalPrompt.id === "inner" && !showDepths && (
        <InnerMineriumSelect playerName={playerName} profile={{ ...profile, uid }}
          onClose={() => { setPortalPrompt(null); setPortalActive(false); }}
          onDepths={() => setShowDepths(true)}
          onEnterNPC={(diff, villain) => {
            setPortalPrompt(null);
            const deck = resolveDeck(profile);
            if (!deck) { showToast("No deck found. Visit The Forge first.", "#dc2626"); return; }
            onEnterNPC && onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null], esclings:deck.esclings||null }, deck.prophecy||null, null, diff, { isInnerEmperium:true, villainId:villain?.id||null, villainName:villain?.name||null, npcDeckId:villain?.starterDeckId||null, storyUid:uid||playerName, collection: buildHoloCollectionFromDeck(profile?.collection, deck) });
          }} />
      )}

      {/* ENDLESS DEPTHS */}
      {showDepths && (
        <EndlessDepths playerName={playerName} uid={uid} profile={profile}
          onClose={() => setShowDepths(false)}
          onFight={(opponent, depth) => {
            setShowDepths(false); setPortalPrompt(null); setPortalActive(false);
            const deck = resolveDeck(profile);
            if (!deck) { showToast("No deck found. Visit The Forge first.", "#dc2626"); return; }
            onEnterNPC && onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null], esclings:deck.esclings||null }, deck.prophecy||null, null, opponent.difficulty, { isEndlessDepths:true, depthOpponentId:opponent.id, depthOpponentName:opponent.name, depthIsGatekeeper:!!opponent.isGatekeeper, depthGatekeeperId:opponent.isGatekeeper?opponent.id:null, npcDeckId:opponent?.starterDeckId||null, storyUid:uid||playerName, difficulty:opponent.difficulty, collection: buildHoloCollectionFromDeck(profile?.collection, deck) });
          }} />
      )}

      {/* PORTAL CINEMATIC */}
      {portalCinematicData && (
        <PortalCinematic portal={portalCinematicData.portal} dungeon={portalCinematicData.dungeon}
          onComplete={() => {
            const { portal, profile } = portalCinematicData;
            setPortalCinematicData(null);
            onEnterDungeon && onEnterDungeon(portal, profile);
          }} />
      )}

      {/* TOAST */}
      {toast && (
        <div style={{ position:"absolute", top:60, left:"50%", transform:"translateX(-50%)",
          background:`${GM.pageWarm}f0`, border:`1px solid ${toast.color}`,
          color:toast.color, padding:"8px 20px", borderRadius:2,
          fontSize:12, zIndex:300, fontFamily:GM.font,
          boxShadow:`0 0 16px ${toast.color}33` }}>
          {toast.msg}
        </div>
      )}

      {/* DEEDS & RENOWN — the achievement wall */}
      {showGallery && uid && (
        <AchievementGallery uid={uid} onClose={() => setShowGallery(false)} />
      )}

      {/* ACHIEVEMENT TOAST — global, self-queuing. Surfaces any newly
          earned achievement no matter where in the app it was granted. */}
      <AchievementToast ids={toastIds} onDrained={() => setToastIds([])} />

      {/* ── PORTRAIT LOCK CURTAIN ── topmost; blocks landscape on
          devices where the OS-level hard lock isn't granted. */}
      <RotateGate show={needsRotate} />
    </div>
  );
}